// App.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppProvider,
  Page,
  Layout,
  Card,
  Text,
  InlineStack,
  BlockStack,
  Icon,
  Banner,
  Checkbox,
  Tooltip as PolarisTooltip,
  Button,
  Modal,
  Select
} from "@shopify/polaris";
import { ChartVerticalIcon, ExternalIcon, DeleteIcon, ClipboardIcon, PlusIcon, RefreshIcon } from "@shopify/polaris-icons";
import axios from "axios";
import {
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceArea,
  ReferenceLine,
  Bar
} from "recharts";
import GaugeComponent from "react-gauge-component";
import './App.css';
import glossaryTerms from "./glossaryTerms.json";
import PerformanceDebugDashboard from "./PerformanceDebugDashboard";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "",
  validateStatus: (status) => status >= 200 && status < 400
});

const TODAY_SALES_REFRESH_INTERVAL_SECONDS = 60;
const SUMMARY_REGENERATION_INTERVAL_MS = 15 * 60 * 1000;
const SUMMARY_REQUEST_DEBOUNCE_MS = 600;
const currentDashboardMonthDate = new Date();
const currentDashboardMonthValue = currentDashboardMonthDate.toLocaleDateString("en-CA").slice(0, 7);
const firstDashboardMonthDate = new Date(2026, 0, 1);
const DASHBOARD_MONTH_OPTIONS = [];
for (
  const monthCursor = new Date(firstDashboardMonthDate);
  monthCursor <= currentDashboardMonthDate;
  monthCursor.setMonth(monthCursor.getMonth() + 1)
) {
  DASHBOARD_MONTH_OPTIONS.push({
    label: monthCursor.toLocaleString("default", { month: "long", year: "numeric" }),
    value: monthCursor.toLocaleDateString("en-CA").slice(0, 7)
  });
}
const SALES_STORE_SCOPE_OPTIONS = [
  { value: "md", label: "MD only" },
  { value: "hunting", label: "Hunting only" },
  { value: "both", label: "Both" }
];
const TRAFFIC_SERIES_CONFIG = {
  allTraffic: { label: "All Traffic Sessions", color: "#4a5c7a" },
  direct: { label: "Direct Sessions", color: "#5c6ac4" },
  organic: { label: "Organic Sessions", color: "#008060" },
  paid: { label: "Paid Sessions", color: "#b98900" }
};
const TRAFFIC_TAB_OPTIONS = [
  { key: "allTraffic", label: "All Traffic", shortLabel: "All" },
  { key: "direct", label: "Direct", shortLabel: "Direct" },
  { key: "organic", label: "Organic", shortLabel: "Organic" },
  { key: "paid", label: "Paid", shortLabel: "Paid" }
];
const CLICK_THROUGH_MODE_OPTIONS = [
  { key: "organic", label: "Organic" },
  { key: "paid", label: "Paid" }
];
const WEBOPS_ITEMS = [
  {
    key: "websiteAvailability",
    title: "Website Availability",
    definition: "Site is online, responsive, SSL healthy, and product pages load correctly."
  },
  {
    key: "websitePerformance",
    title: "Website Performance",
    definition: "Core page speed, responsiveness, and user experience are within acceptable thresholds."
  },
  {
    key: "ordersCheckout",
    title: "Orders & Checkout",
    definition: "Customers can add to cart, calculate tax/shipping, and complete payment."
  },
  {
    key: "integrationsApis",
    title: "Integrations / APIs",
    definition: "ERP, shipping, analytics, and third-party integrations are syncing and responding correctly."
  },
  {
    key: "customerCommunication",
    title: "Customer Communication",
    definition: "Order confirmations, shipping emails, forms, and support contact paths are working."
  },
  {
    key: "seoTrafficTrends",
    title: "SEO / Traffic Trends",
    definition: "Sessions by channel are within expected range and no unusual traffic drops are present."
  },
  {
    key: "securityBackups",
    title: "Security / Backups",
    definition: "Security posture, SSL coverage, and backup verification are current and reliable."
  },
  {
    key: "contentMerchandising",
    title: "Content / Merchandising",
    definition: "Product content, collection merchandising, and on-site merchandising logic are current and accurate."
  }
];
const WEBOPS_GLOSSARY_TERMS = WEBOPS_ITEMS.reduce((acc, item) => {
  acc[item.title.toLowerCase()] = item.definition;
  return acc;
}, {});
WEBOPS_GLOSSARY_TERMS.webops = "Operational controls monitored at predetermined best-practice intervals with measurable verification cadence, status aging, and exception visibility.";
const WEBOPS_STATUS_CYCLE = ["green", "yellow", "orange", "red"];
const WEBOPS_STATUS_LABELS = {
  green: "Healthy",
  yellow: "Watch",
  orange: "Degraded",
  red: "Action Needed"
};

const CONTENT_MERCH_CHECKLIST_TEMPLATE = [
  {
    key: "productContentReview",
    title: "Product Content Review",
    items: [
      { key: "keyProductsAccurate", label: "Key products accurate" },
      { key: "pricingPromosCorrect", label: "Pricing/promos correct" },
      { key: "imagesRenderingCorrectly", label: "Images rendering correctly" },
      { key: "pdfsSpecSheetsAvailable", label: "PDFs/spec sheets available" },
      { key: "newProductsComplete", label: "New products complete" },
      { key: "messagingContentCurrent", label: "Messaging/content current" },
      { key: "huntingIndustrialBrandingCorrect", label: "Hunting/industrial branding correct" }
    ]
  },
  {
    key: "collectionMerchandisingReview",
    title: "Collection Merchandising Review",
    items: [
      { key: "featuredCollectionsRelevant", label: "Featured collections relevant" },
      { key: "seasonalPrioritiesAligned", label: "Seasonal priorities aligned" },
      { key: "filtersBehavingCorrectly", label: "Filters behaving correctly" },
      { key: "bestSellersSurfacedProperly", label: "Best sellers surfaced properly" },
      { key: "bannersPromosAligned", label: "Banners/promos aligned with current goals" },
      { key: "noDeadEmptyCollectionsVisible", label: "No dead/empty collections visible" },
      { key: "sortingMakesSense", label: "Sorting makes sense" }
    ]
  },
  {
    key: "onsiteMerchandisingLogicReview",
    title: "On-Site Merchandising Logic Review",
    items: [
      { key: "crossSellsFunctioning", label: "Cross-sells functioning" },
      { key: "substituteLogicWorking", label: "Substitute logic working" },
      { key: "upsellsAppearingCorrectly", label: "Upsells appearing correctly" },
      { key: "stickyAtcBehavingProperly", label: "Sticky ATC behaving properly" },
      { key: "searchFilterUxHealthy", label: "Search/filter UX healthy" },
      { key: "klaviyoFormsPromosBehaving", label: "Klaviyo/forms/promos behaving" },
      { key: "cartRecommendationsSensible", label: "Cart recommendations sensible" },
      { key: "noStorefrontRegressionsObserved", label: "No storefront regressions observed" }
    ]
  }
];
const CONTENT_MERCH_ISSUE_SEVERITY_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Minor", value: "minor" },
  { label: "Moderate", value: "moderate" },
  { label: "Critical", value: "critical" }
];
const CONTENT_MERCH_STOREFRONT_LINKS = [
  { label: "Homepage", href: "https://maltadynamics.com/" },
  { label: "Collections", href: "https://maltadynamics.com/collections" },
  { label: "Hunting", href: "https://maltadynamics.com/collections/hunting-safety-gear" },
  { label: "Best Sellers", href: "https://maltadynamics.com/collections/best-sellers" },
  { label: "Cart", href: "https://maltadynamics.com/cart" }
];

const SYSTEMS_INVENTORY_FIELD_LABELS = {
  id: "ID",
  notes_description: "Notes / Description",
  category: "Category",
  component_name: "Component Name",
  component_type: "Component Type",
  platform: "Platform",
  netsuite_internal_id: "NetSuite Internal ID",
  netsuite_script_id: "NetSuite Script ID",
  netsuite_script_type: "Script Type",
  netsuite_owner: "NetSuite Owner",
  netsuite_inactive: "NetSuite Inactive",
  related_system: "Related System",
  related_partner: "Related Partner",
  related_record_type: "Related Record Type",
  business_purpose: "Business Purpose",
  technical_notes: "Technical Notes",
  dependencies: "Dependencies",
  known_risks: "Known Risks",
  status: "Status",
  risk_level: "Risk Level",
  migration_action: "Migration Action",
  vendor_bundle: "Vendor Bundle",
  source: "Source",
  view_url: "View URL",
  edit_url: "Edit URL",
  last_reviewed: "Last Reviewed",
  reviewed_by: "Reviewed By",
  created_at: "Created At",
  updated_at: "Updated At"
};

const SYSTEMS_INVENTORY_CSV_FIELDS = [
  "id",
  "notes_description",
  "category",
  "component_name",
  "component_type",
  "platform",
  "netsuite_internal_id",
  "netsuite_script_id",
  "netsuite_script_type",
  "netsuite_owner",
  "netsuite_inactive",
  "related_system",
  "related_partner",
  "related_record_type",
  "business_purpose",
  "technical_notes",
  "dependencies",
  "known_risks",
  "status",
  "risk_level",
  "migration_action",
  "vendor_bundle",
  "source",
  "view_url",
  "edit_url",
  "last_reviewed",
  "reviewed_by",
  "created_at",
  "updated_at"
];

const SYSTEMS_INVENTORY_FORM_FIELDS = [
  "category",
  "component_name",
  "component_type",
  "platform",
  "netsuite_internal_id",
  "netsuite_script_id",
  "netsuite_script_type",
  "netsuite_owner",
  "netsuite_inactive",
  "related_system",
  "related_partner",
  "related_record_type",
  "business_purpose",
  "technical_notes",
  "dependencies",
  "known_risks",
  "status",
  "risk_level",
  "migration_action",
  "vendor_bundle",
  "source",
  "view_url",
  "edit_url",
  "last_reviewed",
  "reviewed_by"
];

const SYSTEMS_INVENTORY_EMPTY_FORM = {
  category: "",
  component_name: "",
  component_type: "",
  platform: "NetSuite",
  netsuite_internal_id: "",
  netsuite_script_id: "",
  netsuite_script_type: "",
  netsuite_owner: "",
  netsuite_inactive: "",
  related_system: "",
  related_partner: "",
  related_record_type: "",
  business_purpose: "",
  technical_notes: "",
  dependencies: "",
  known_risks: "",
  status: "Needs Validation",
  risk_level: "Unknown",
  migration_action: "Validate",
  vendor_bundle: 0,
  source: "Manual",
  view_url: "",
  edit_url: "",
  last_reviewed: "",
  reviewed_by: ""
};

const SYSTEMS_INVENTORY_STATUS_OPTIONS = ["Active", "Inactive", "In Development", "Needs Validation", "Retire Candidate", "Business Critical", "Unknown"];
const SYSTEMS_INVENTORY_RISK_OPTIONS = ["Low", "Medium", "High", "Unknown"];
const SYSTEMS_INVENTORY_MIGRATION_OPTIONS = ["Keep", "Rebuild", "Retire", "Validate", "Unknown"];
const READINESS_PROJECT_QUERY_PARAM = "project";
const PROP65_PROJECT_CODE = "PROP65";
const MALTA_NETSUITE_PROJECT_CODE = "MALTA-NS-SDF";
const MALTA_NETSUITE_NORMALIZED_PROJECT_CODE = "MALTA-NS-NORMALIZED";
const MALTA_NETSUITE_SESSION_PROJECT_CODE = "MALTA-NS-SESSION";
const MD_EDI_PROJECT_CODE = "MD-EDI";
const READINESS_DEFAULT_PROJECT = MALTA_NETSUITE_SESSION_PROJECT_CODE;
const READINESS_PROJECT_GROUPS = [
  {
    label: "CURRENT PLAN",
    options: [
      { value: MALTA_NETSUITE_SESSION_PROJECT_CODE, label: "Malta NetSuite Migration — Executive Readiness" }
    ]
  },
  {
    label: "HISTORICAL COMPARISONS",
    options: [
      { value: MALTA_NETSUITE_PROJECT_CODE, label: "Original SDF Baseline — 1,794 hours" },
      { value: MALTA_NETSUITE_NORMALIZED_PROJECT_CODE, label: "Normalized v2 Estimate — 1,302.9 hours" }
    ]
  },
  {
    label: "OTHER PROJECTS",
    options: [
      { value: "MHS-LAUNCH", label: "MHS Launch" },
      { value: MD_EDI_PROJECT_CODE, label: "Malta EDI" },
      { value: PROP65_PROJECT_CODE, label: "Proposition 65" }
    ]
  }
];
const READINESS_PROJECT_LABELS = READINESS_PROJECT_GROUPS
  .flatMap((group) => group.options)
  .reduce((labels, option) => ({ ...labels, [option.value]: option.label }), {});
const READINESS_CURRENT_PLAN_DESCRIPTOR = "Current plan · v16 · 634.8 most likely · 9+2 recommended schedule";
const SHOPIFY_STORE_HANDLE = process.env.REACT_APP_SHOPIFY_STORE || "vr0g7p-um";
const SHOPIFY_APP_HANDLE = process.env.REACT_APP_SHOPIFY_APP_HANDLE || "sales-goal-dashboard";
const READINESS_LANE_OPTIONS = ["Plan", "To Do", "Doing", "Blocked", "Done"];
const READINESS_STATUS_OPTIONS = ["Open", "In Progress", "Blocked", "Done"];
const READINESS_PRIORITY_OPTIONS = ["Low", "Normal", "High", "Critical"];
const READINESS_LIFECYCLE_STATUS_OPTIONS = ["Not Started", "In Progress", "Blocked", "Complete"];
const READINESS_COMPLIANCE_STATUS_OPTIONS = [
  "Not Started",
  "In Review",
  "Supplier Info Needed",
  "Product Page Update Needed",
  "Packaging Review Needed",
  "Legal Review Needed",
  "Remediation Complete",
  "No Action Needed"
];
const READINESS_COMPLIANCE_RISK_OPTIONS = ["Low", "Medium", "High", "Critical"];
const READINESS_COMPLIANCE_EMPTY_AUDIT = {
  category: "Compliance",
  section: "Prop 65 Warning Coverage Review",
  audit_type: "Product / Storefront Audit",
  finding_type: "New finding",
  status: "Not Started",
  risk_level: "Medium",
  product_area: "New product / area",
  sku: "",
  product_category: "",
  california_sales_exposure: "",
  potential_prop65_risk: "",
  current_warning_status: "",
  supplier_manufacturer_warning_data: "",
  packaging_manual_warning_status: "",
  shopify_product_page_warning_status: "",
  cart_checkout_visibility: "",
  warning_wording_status: "",
  legal_review_status: "",
  remediation_needed: "",
  owner: "",
  due_date: "",
  next_action: "",
  notes_evidence: "",
  sort_order: 0
};

const READINESS_EMPTY_ITEM = {
  lane: "To Do",
  category: "",
  title: "New readiness item",
  status: "Open",
  priority: "Normal",
  owner: "",
  due_date: "",
  notes: "",
  sort_order: 0
};

function buildSystemsInventoryNetSuiteUrl(internalId, edit = false) {
  const cleanId = String(internalId || "").trim();
  if (!cleanId) return "";
  return `https://5805817.app.netsuite.com/app/common/scripting/script.nl?id=${encodeURIComponent(cleanId)}${edit ? "&e=T" : ""}`;
}

function escapeCsvValue(value) {
  const rawValue = value === null || value === undefined ? "" : String(value);
  return `"${rawValue.replace(/"/g, '""')}"`;
}

function escapeHtmlValue(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function buildBadgeClass(prefix, value) {
  return `${prefix} ${prefix}--${String(value || "empty").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function formatReadinessDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function buildReadinessOptions(values = [], allLabel = "All") {
  return [
    { label: allLabel, value: "" },
    ...values.map((value) => ({ label: value, value }))
  ];
}

function getReadinessProjectFromUrl() {
  if (typeof window === "undefined") return READINESS_DEFAULT_PROJECT;
  const params = new URLSearchParams(window.location.search || "");
  return params.get(READINESS_PROJECT_QUERY_PARAM) || READINESS_DEFAULT_PROJECT;
}

function buildReadinessInternalUrl(projectCode) {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  url.pathname = "/readiness-board";
  url.searchParams.set(READINESS_PROJECT_QUERY_PARAM, projectCode || READINESS_DEFAULT_PROJECT);
  return url.toString();
}

function buildShopifyReadinessProjectUrl(projectCode) {
  if (typeof window === "undefined") return "";
  const url = new URL(
    `/store/${encodeURIComponent(SHOPIFY_STORE_HANDLE)}/apps/${encodeURIComponent(SHOPIFY_APP_HANDLE)}/app`,
    "https://admin.shopify.com"
  );
  url.searchParams.set(READINESS_PROJECT_QUERY_PARAM, projectCode || READINESS_DEFAULT_PROJECT);
  return url.toString();
}

function downloadSystemsInventoryCsv(rows) {
  const header = SYSTEMS_INVENTORY_CSV_FIELDS.map((field) => escapeCsvValue(SYSTEMS_INVENTORY_FIELD_LABELS[field] || field)).join(",");
  const lines = rows.map((row) => (
    SYSTEMS_INVENTORY_CSV_FIELDS.map((field) => escapeCsvValue(
      field === "notes_description" ? getSystemsInventoryNotesDescription(row) : row?.[field]
    )).join(",")
  ));
  const csv = [header, ...lines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  const link = document.createElement("a");
  link.href = url;
  link.download = `systems_inventory_${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getSystemsInventoryNotesDescription(item) {
  return String(item?.business_purpose || item?.technical_notes || "").trim();
}

function truncateSystemsInventoryText(value, maxLength = 110) {
  const cleanValue = String(value || "").replace(/\s+/g, " ").trim();
  if (!cleanValue) return "Needs notes";
  return cleanValue.length > maxLength ? `${cleanValue.slice(0, maxLength - 1)}...` : cleanValue;
}

const ORDER_MIX_SUMMARY_MODES = [
  { value: "executive_summary", label: "Executive Summary" },
  { value: "more_detail", label: "More Detail" },
  { value: "straight_talk", label: "Straight Talk" }
];

function clamp(value, min, max) {
  return Math.min(Math.max(Number(value) || 0, min), max);
}

function formatKpiGaugeValue({ value = 0, kind = "number", currencyFractionDigits = 0 }) {
  const safe = Number(value) || 0;
  if (kind === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: currencyFractionDigits,
      maximumFractionDigits: currencyFractionDigits
    }).format(safe);
  }
  if (kind === "percent") {
    return `${safe.toFixed(1)}%`;
  }
  return `${Math.round(safe)}`;
}

function resolveGaugeZone(value, zoneRanges = []) {
  const safeValue = Number(value) || 0;
  for (const range of zoneRanges) {
    if (safeValue <= Number(range.max)) {
      return range.zone;
    }
  }

  return zoneRanges.length ? zoneRanges[zoneRanges.length - 1].zone : "yellow";
}

const KPI_CURRENT_READING_COPY = {
  typical_order_size: {
    green: "The needle is in the green range, meaning everyday order size is healthy and broad customer demand is doing most of the work.",
    yellow: "The needle is in the yellow range, meaning typical order size is acceptable but not yet strong enough to carry the month on its own.",
    red: "The needle is in the red range, meaning most current orders are relatively small and overall sales are leaning more on occasional large orders."
  },
  large_order_impact: {
    green: "The needle is in the green range, meaning large orders are not heavily distorting results and day-to-day demand is showing through clearly.",
    yellow: "The needle is in the yellow range, meaning large orders are influencing averages and should be watched when interpreting momentum.",
    red: "The needle is in the red range because a small number of large orders are currently having a major influence on sales metrics."
  },
  revenue_dependence: {
    green: "The needle is in the green range, meaning revenue is spread across a broader base of orders and demand is more resilient.",
    yellow: "The needle is in the yellow range, meaning some dependence on larger orders exists but broader purchasing activity is still contributing.",
    red: "The needle is in the red range, meaning month-to-date revenue is concentrated in a small number of large orders."
  },
  core_forecast: {
    green: "The needle is in the green range, meaning baseline demand is tracking strong enough to support most of the month-end target.",
    yellow: "The needle is in the yellow range, meaning core demand is decent but still needs support from stronger days or large deals.",
    red: "The needle is in the red range, meaning baseline demand alone is currently too light versus plan."
  },
  big_deal_lift: {
    green: "The needle is in the green range, meaning forecast dependence on large deals is low and month-end risk is more controlled.",
    yellow: "The needle is in the yellow range, meaning expected large deals are a meaningful part of getting to target.",
    red: "The needle is in the red range because the forecast currently relies heavily on expected large-order contribution."
  },
  forecast_stability: {
    green: "The needle is in the green range, meaning the forecast is relatively predictable based on current order behavior.",
    yellow: "The needle is in the yellow range, meaning forecast reliability is moderate and should be monitored as the month progresses.",
    red: "The needle is in the red range, meaning forecast confidence is lower due to concentration and uneven order behavior."
  }
};

function buildGaugeGlossaryTooltip({ glossaryKey, glossaryDefinition, zone }) {
  const fallback = "This metric helps interpret month-to-date quality and forecast risk.";
  const definition = String(glossaryDefinition || fallback).trim();
  const readingByZone = KPI_CURRENT_READING_COPY?.[glossaryKey] || {};
  const reading = zone === "insufficient_data"
    ? "No completed-day interpretation is available yet; this first-day reading is preliminary."
    : (readingByZone?.[zone] || readingByZone?.yellow || fallback);
  const merged = `${definition}<p><strong>Current reading:</strong> ${reading}</p>`;
  return renderGlossaryTooltipContent(merged);
}

const CompactKpiGauge = React.memo(function CompactKpiGauge({ label, value, min, max, bands, formattedValue, tooltipContent }) {
  const clampedValue = clamp(value, min, max);
  return (
    <div className="kpi-gauge-item">
      <TooltipGlossary content={tooltipContent}>
        <span className="kpi-gauge-label">
          <Text variant="bodySm" as="p" tone="subdued">{label}</Text>
        </span>
      </TooltipGlossary>
      <TooltipGlossary content={tooltipContent}>
        <div className="kpi-gauge-viz kpi-gauge-viz--hoverable">
          <GaugeComponent
            type="semicircle"
            minValue={min}
            maxValue={max}
            value={clampedValue}
            arc={{
              width: 0.22,
              padding: 0.008,
              cornerRadius: 3,
              subArcs: bands.map((band) => ({
                limit: band.limit,
                color: band.color
              }))
            }}
            pointer={{
              type: "needle",
              color: "#222",
              baseColor: "#222",
              width: 11,
              length: 0.72,
              animate: true,
              elastic: false,
              animationDuration: 700
            }}
            labels={{
              valueLabel: { hide: true },
              tickLabels: {
                hideMinMax: true,
                defaultTickLineConfig: { hide: true },
                defaultTickValueConfig: { hide: true }
              }
            }}
          />
        </div>
      </TooltipGlossary>
      <Text variant="headingSm" as="p">{formattedValue}</Text>
    </div>
  );
});

function getInitialDashboardMonth() {
  const params = new URLSearchParams(window.location.search || "");
  const requestedMonth = params.get("month");
  if (DASHBOARD_MONTH_OPTIONS.some((option) => option.value === requestedMonth)) {
    return requestedMonth;
  }

  return DASHBOARD_MONTH_OPTIONS.some((option) => option.value === currentDashboardMonthValue)
    ? currentDashboardMonthValue
    : DASHBOARD_MONTH_OPTIONS[DASHBOARD_MONTH_OPTIONS.length - 1].value;
}

function decodeDashboardIdentityToken(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch (err) {
    return null;
  }
}

function pickDashboardIdentityName(identity = {}) {
  const fullName = [
    identity.first_name || identity.firstName || identity.given_name,
    identity.last_name || identity.lastName || identity.family_name
  ].filter(Boolean).join(" ").trim();

  return (
    fullName
    || identity.name
    || identity.displayName
    || identity.email
    || identity.user_email
    || identity.userEmail
    || ""
  );
}

function getDashboardUserGreeting() {
  if (typeof window === "undefined") {
    return "Welcome";
  }

  const params = new URLSearchParams(window.location.search || "");
  const queryIdentity = {
    first_name: params.get("first_name"),
    firstName: params.get("firstName"),
    given_name: params.get("given_name"),
    last_name: params.get("last_name"),
    lastName: params.get("lastName"),
    family_name: params.get("family_name"),
    name: params.get("name") || params.get("user"),
    email: params.get("email") || params.get("user_email")
  };

  const queryName = pickDashboardIdentityName(queryIdentity);
  if (queryName) return `Welcome, ${queryName}`;

  const tokenParamNames = ["id_token", "session", "token"];
  for (const tokenParamName of tokenParamNames) {
    const tokenIdentity = decodeDashboardIdentityToken(params.get(tokenParamName));
    const tokenName = pickDashboardIdentityName(tokenIdentity || {});
    if (tokenName) return `Welcome, ${tokenName}`;
  }

  const configuredName = process.env.REACT_APP_DASHBOARD_USER_NAME || process.env.REACT_APP_DASHBOARD_USER_EMAIL || "Jim";
  return configuredName ? `Welcome, ${configuredName}` : "Welcome";
}

function getDashboardIdentityParams() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search || "");
  const allowedParams = [
    "dashboard_user_id",
    "dashboard_user",
    "user",
    "dashboard_user_email",
    "user_email",
    "shopify_user_id"
  ];

  return allowedParams.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

function parseDashboardMonth(monthValue) {
  const [year, month] = String(monthValue || "").split("-").map(Number);
  if (!year || !month) {
    return new Date();
  }

  return new Date(year, month - 1, 1);
}

function parseDashboardDate(dateValue) {
  const [year, month, day] = String(dateValue || "").split("-").map(Number);
  if (!year || !month || !day) {
    return new Date();
  }

  return new Date(year, month - 1, day);
}

function formatDashboardMonth(dateValue) {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

function mergeGlossaryTermsWithDefaults(terms) {
  const merged = {
    ...WEBOPS_GLOSSARY_TERMS,
    ...(terms && typeof terms === "object" ? terms : {})
  };

  const conversionKey = "conversion rate";
  const existingConversionDef = String(merged[conversionKey] || "").trim();
  if (!existingConversionDef || !/human traffic only/i.test(existingConversionDef)) {
    merged[conversionKey] = "Sessions converting to completed checkout (human traffic only).";
  }
  const abandonmentKey = "cart abandonment";
  const existingAbandonmentDef = String(merged[abandonmentKey] || "").trim();
  if (!existingAbandonmentDef) {
    merged[abandonmentKey] = "Share of checkouts still abandoned versus abandoned plus recovered in the completed-day comparison window.";
  }

  return merged;
}

function formatWebopsCadence(hoursValue) {
  const hours = Number(hoursValue || 0);
  if (hours <= 0.2) return "Every 5 min";
  if (Math.abs(hours - 24) < 0.01) return "Daily";
  if (Math.abs(hours - 168) < 0.01) return "Weekly";
  if (hours < 24) return `Every ${hours.toFixed(1)}h`;
  const days = hours / 24;
  return `Every ${Number.isInteger(days) ? days : days.toFixed(1)} day(s)`;
}

function formatWebopsDate(isoValue) {
  const date = new Date(isoValue || "");
  if (Number.isNaN(date.getTime())) return "Not checked yet";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function getWebopsTimingState(item) {
  const severity = { green: 0, yellow: 1, orange: 2, red: 3 };
  const baseStatus = String(item?.status || "green").toLowerCase();
  const lastCheckedMs = new Date(item?.lastChecked || 0).getTime();
  const frequencyMs = Math.max(1, Number(item?.checkFrequencyHours || 0) * 60 * 60 * 1000);
  if (!Number.isFinite(lastCheckedMs)) {
    return {
      effectiveStatus: baseStatus,
      dueAtIso: null,
      ringState: "critical-overdue"
    };
  }

  const nowMs = Date.now();
  const elapsedMs = Math.max(0, nowMs - lastCheckedMs);
  const overdueRatio = elapsedMs / frequencyMs;
  const dueAtIso = new Date(lastCheckedMs + frequencyMs).toISOString();

  if (String(item?.key || "") === "contentMerchandising") {
    if (overdueRatio >= 2) {
      return { effectiveStatus: "red", dueAtIso, ringState: "critical-overdue" };
    }
    if (overdueRatio >= 1) {
      return { effectiveStatus: "yellow", dueAtIso, ringState: "near-expiry" };
    }
    return { effectiveStatus: "green", dueAtIso, ringState: "fresh" };
  }

  if (baseStatus === "red") {
    return { effectiveStatus: "red", dueAtIso, ringState: "critical-overdue" };
  }

  let agedStatus = "green";
  if (overdueRatio >= 2) {
    agedStatus = "orange";
  } else if (overdueRatio >= 1) {
    agedStatus = "yellow";
  }

  const effectiveStatus = severity[baseStatus] > severity[agedStatus] ? baseStatus : agedStatus;
  if (overdueRatio >= 0.75) {
    return { effectiveStatus, dueAtIso, ringState: "near-expiry" };
  }

  return { effectiveStatus, dueAtIso, ringState: agedStatus === "green" ? "fresh" : "overdue" };
}








export default function App() {
  const [loading, setLoading] = useState(true);
  const [storeConnected, setStoreConnected] = useState(true);
  const [salesYesterday, setSalesYesterday] = useState(0);
  const [refundsYesterday, setRefundsYesterday] = useState(0);
  const [onlineStoreYesterday, setOnlineStoreYesterday] = useState(0);
  const [posYesterday, setPosYesterday] = useState(0);
  const [salesMTD, setSalesMTD] = useState(0);
  const [salesMTDWithPOS, setSalesMTDWithPOS] = useState(0);
  const [liveSalesMTDWithPOS, setLiveSalesMTDWithPOS] = useState(0);
  const [monthlyGoal, setMonthlyGoal] = useState(0);
  const [planMTD, setPlanMTD] = useState(0);
  const [percentToPlan, setPercentToPlan] = useState(0);
  const [percentToPlanWithPOS, setPercentToPlanWithPOS] = useState(0);
  const [orders, setOrders] = useState([]);
  const [environment, setEnvironment] = useState("unknown");
  const [chartData, setChartData] = useState([]);
  const [trafficCharts, setTrafficCharts] = useState({ allTraffic: [], direct: [], organic: [], paid: [], conversionRate: [], cartAbandonment: [], clickThrough: [] });
  const [trafficSummary, setTrafficSummary] = useState({});
  const [trafficSignals, setTrafficSignals] = useState({});
  const [cartAbandonmentMode, setCartAbandonmentMode] = useState("operational");
  const [clickThroughMode, setClickThroughMode] = useState("organic");
  const [activeTrafficTab, setActiveTrafficTab] = useState("allTraffic");
  const [topReferringChannels, setTopReferringChannels] = useState({ allTraffic: [], direct: [], organic: [], paid: [], conversionRate: [], cartAbandonment: [], clickThrough: [] });
  const [trafficComparisonWindow, setTrafficComparisonWindow] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [trafficError, setTrafficError] = useState("");
  const [salesSummary, setSalesSummary] = useState("");
  const [salesSummaryBullets, setSalesSummaryBullets] = useState([]);
  const [orderMixExecutiveSummary, setOrderMixExecutiveSummary] = useState("");
  const [orderMixExecutiveSummaryLoading, setOrderMixExecutiveSummaryLoading] = useState(false);
  const [orderMixSummaryMode, setOrderMixSummaryMode] = useState("executive_summary");
  const [orderMixSummaryRefreshTick, setOrderMixSummaryRefreshTick] = useState(0);
  const [orderMixSummaryManualRefreshTick, setOrderMixSummaryManualRefreshTick] = useState(0);
  const forceNextOrderMixSummaryRefreshRef = useRef(false);
  const [salesCollapsed, setSalesCollapsed] = useState(false);
  const [topProductsMTD, setTopProductsMTD] = useState([]);
  const [showWebTrendline, setShowWebTrendline] = useState(true);
  const [showRealitySeasoning, setShowRealitySeasoning] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [compareGoalModel, setCompareGoalModel] = useState(false);
  const [webScenarioKeys, setWebScenarioKeys] = useState([]);
  const [dashboardChartSource, setDashboardChartSource] = useState(null);
  const [pacingDiagnosticsV2, setPacingDiagnosticsV2] = useState(null);
  const [majorProjects, setMajorProjects] = useState([]);
  const [projectsDirty, setProjectsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const [dotCount, setDotCount] = useState(0); // For "Saving..."
  const [selectedMonth, setSelectedMonth] = useState(getInitialDashboardMonth);
  const selectedMonthRef = useRef(selectedMonth);
  const [salesStoreScope, setSalesStoreScope] = useState(() => {
    try {
      return window.localStorage.getItem("shopify-sales-store-scope") || "both";
    } catch (_err) {
      return "both";
    }
  });
  const salesStoreScopeRef = useRef(salesStoreScope);
  const [scopedSalesSnapshot, setScopedSalesSnapshot] = useState(null);
  const [scopedSalesLoading, setScopedSalesLoading] = useState(false);
  const [scopedSalesError, setScopedSalesError] = useState(null);
  const selectedMonthDate = parseDashboardMonth(selectedMonth);
  const selectedMonthLabel = selectedMonthDate.toLocaleString("default", { month: "long", year: "numeric" });
  const monthName = selectedMonthDate.toLocaleString("default", { month: "long" });
  const monthNameAbbr = selectedMonthDate.toLocaleString("default", { month: "short" });
  const currentMonthKey = new Date().toLocaleDateString("en-CA").slice(0, 7);
  const isSelectedMonthActive = selectedMonth === currentMonthKey;
  const [showCompleted, setShowCompleted] = useState(false);
  const [todaysSales, setTodaysSales] = useState(0);
  const [todaysGoal, setTodaysGoal] = useState(0);
  const [goalYesterday, setGoalYesterday] = useState(0);
  const [ordersCollapsed, setOrdersCollapsed] = useState(true);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [intradayPacing, setIntradayPacing] = useState(null);
  const [glossaryTermsMap, setGlossaryTermsMap] = useState(
    mergeGlossaryTermsWithDefaults(glossaryTerms)
  );
  const [glossaryModalOpen, setGlossaryModalOpen] = useState(false);
  const [glossaryDraft, setGlossaryDraft] = useState([]);
  const [glossarySaving, setGlossarySaving] = useState(false);
  const [simulationModalOpen, setSimulationModalOpen] = useState(false);
  const [simulationDraft, setSimulationDraft] = useState([{ id: uuidv4(), amount: "" }]);
  const [refreshCountdown, setRefreshCountdown] = useState(TODAY_SALES_REFRESH_INTERVAL_SECONDS);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [forecastMethodologyModalOpen, setForecastMethodologyModalOpen] = useState(false);
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [hoveredGoalSeries, setHoveredGoalSeries] = useState(null);
  const [showPerfDashboard, setShowPerfDashboard] = useState(false);
  const [movingAveragePreset, setMovingAveragePreset] = useState(() => {
    const configured = Math.max(1, Math.floor(Number(process.env.REACT_APP_DAILY_MA_DAYS) || 5));
    if (configured === 3) return "3";
    if (configured === 5) return "5";
    if (configured === 7) return "7";
    if (configured === 14) return "14";
    return "custom";
  });
  const [movingAverageCustomDaysInput, setMovingAverageCustomDaysInput] = useState(() => {
    const configured = Number(process.env.REACT_APP_DAILY_MA_DAYS);
    if (!Number.isFinite(configured)) return "5";
    return String(Math.max(1, Math.floor(configured)));
  });
  const [webopsItems, setWebopsItems] = useState([]);
  const [contentMerchChecklistModalOpen, setContentMerchChecklistModalOpen] = useState(false);
  const [contentMerchChecklistState, setContentMerchChecklistState] = useState({
    sections: CONTENT_MERCH_CHECKLIST_TEMPLATE.map((section) => ({
      ...section,
      items: section.items.map((item) => ({ ...item, checked: false }))
    })),
    notes: "",
    issueSeverity: "none",
    lastCompletedAt: null,
    lastSavedAt: null,
    lastCompletedBy: null,
    lastCompletionSnapshot: null,
    effectiveStatus: "red",
    isComplete: false
  });
  const [contentMerchChecklistSaving, setContentMerchChecklistSaving] = useState(false);
  const [contentMerchChecklistCompleting, setContentMerchChecklistCompleting] = useState(false);
  const [webopsLampOverrides, setWebopsLampOverrides] = useState({});
  const [webopsLampBlinkOff, setWebopsLampBlinkOff] = useState(false);
  const [systemsInventory, setSystemsInventory] = useState([]);
  const [systemsInventoryFilters, setSystemsInventoryFilters] = useState({});
  const [systemsInventorySearch, setSystemsInventorySearch] = useState("");
  const [systemsInventoryFilterOptions, setSystemsInventoryFilterOptions] = useState({});
  const [systemsInventoryLoading, setSystemsInventoryLoading] = useState(false);
  const [systemsInventoryError, setSystemsInventoryError] = useState("");
  const [systemsInventoryDetailsItem, setSystemsInventoryDetailsItem] = useState(null);
  const [systemsInventoryFormOpen, setSystemsInventoryFormOpen] = useState(false);
  const [systemsInventoryFormMode, setSystemsInventoryFormMode] = useState("add");
  const [systemsInventoryFormDraft, setSystemsInventoryFormDraft] = useState(SYSTEMS_INVENTORY_EMPTY_FORM);
  const [systemsInventorySaving, setSystemsInventorySaving] = useState(false);
  const [readinessProjects, setReadinessProjects] = useState([]);
  const [readinessProjectCode, setReadinessProjectCode] = useState(getReadinessProjectFromUrl);
  const [readinessProject, setReadinessProject] = useState(null);
  const [readinessLifecycle, setReadinessLifecycle] = useState([]);
  const [readinessItems, setReadinessItems] = useState([]);
  const [readinessArtifacts, setReadinessArtifacts] = useState([]);
  const [readinessComplianceAudits, setReadinessComplianceAudits] = useState([]);
  const [readinessPlanAnalysis, setReadinessPlanAnalysis] = useState(null);
  const [readinessNormalizationAudit, setReadinessNormalizationAudit] = useState([]);
  const [readinessPlanCopyFeedback, setReadinessPlanCopyFeedback] = useState("");
  const [readinessFilters, setReadinessFilters] = useState({ lane: "", status: "", owner: "", category: "" });
  const [readinessComplianceFilters, setReadinessComplianceFilters] = useState({
    category: "",
    prop65Only: true,
    needsLegalReview: false,
    supplierInfoNeeded: false,
    productPageUpdateNeeded: false,
    highRisk: false
  });
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessSaving, setReadinessSaving] = useState(false);
  const [readinessComplianceSaving, setReadinessComplianceSaving] = useState(false);
  const [readinessError, setReadinessError] = useState("");
  const [readinessComplianceError, setReadinessComplianceError] = useState("");
  const [readinessAiContext, setReadinessAiContext] = useState(null);
  const [readinessAiContextOpen, setReadinessAiContextOpen] = useState(false);
  const [readinessAiContextLoading, setReadinessAiContextLoading] = useState(false);
  const [readinessAiContextFeedback, setReadinessAiContextFeedback] = useState("");
  const [readinessShareFeedback, setReadinessShareFeedback] = useState("");
  const [dashboardMe, setDashboardMe] = useState(null);
  const dashboardUserGreeting = dashboardMe?.display_name
    ? `Welcome, ${dashboardMe.display_name}`
    : getDashboardUserGreeting();
  const systemsInventoryTableRef = useRef(null);
  const systemsInventoryTabulatorRef = useRef(null);
  const systemsInventoryFiltersRef = useRef({});
  const isSystemsInventoryPage = typeof window !== "undefined"
    && window.location.pathname.replace(/\/+$/, "") === "/systems-inventory";
  const isReadinessBoardPage = typeof window !== "undefined"
    && (
      window.location.pathname.replace(/\/+$/, "") === "/readiness-board"
      || new URLSearchParams(window.location.search || "").has(READINESS_PROJECT_QUERY_PARAM)
    );
  const isMaltaNetSuiteMigration = readinessProjectCode === MALTA_NETSUITE_PROJECT_CODE;
  const isMaltaNetSuiteNormalized = readinessProjectCode === MALTA_NETSUITE_NORMALIZED_PROJECT_CODE;
  const isMaltaNetSuiteSession = readinessProjectCode === MALTA_NETSUITE_SESSION_PROJECT_CODE;
  const isAnyMaltaNetSuiteProject = isMaltaNetSuiteMigration || isMaltaNetSuiteNormalized || isMaltaNetSuiteSession;
  const readinessItemsTableRef = useRef(null);
  const readinessArtifactsTableRef = useRef(null);
  const readinessComplianceAuditsTableRef = useRef(null);
  const readinessItemsTabulatorRef = useRef(null);
  const readinessArtifactsTabulatorRef = useRef(null);
  const readinessComplianceAuditsTabulatorRef = useRef(null);
  const [monthDetailsGaugeDisplayValues, setMonthDetailsGaugeDisplayValues] = useState({});
  const [isCopyingAiContext, setIsCopyingAiContext] = useState(false);
  const [aiCopyFeedback, setAiCopyFeedback] = useState(null);
  const webopsLampRanRef = useRef(false);
  const webopsClickTimersRef = useRef({});
  const monthDetailsGaugeIntroRanRef = useRef(false);
  const monthDetailsGaugeAnimationFrameRef = useRef(null);
  const aiCopyFeedbackTimeoutRef = useRef(null);

  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);

  useEffect(() => {
    salesStoreScopeRef.current = salesStoreScope;
  }, [salesStoreScope]);

  useEffect(() => {
    return () => {
      if (aiCopyFeedbackTimeoutRef.current) {
        clearTimeout(aiCopyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const path = window.location.pathname || "";
    const search = window.location.search || "";
    const isPerfPath = path.toLowerCase().includes("/performance");
    const isPerfQuery = /(?:[?&])(perf|debug)=perf/.test(search.toLowerCase());
    if (isPerfPath || isPerfQuery) {
      setShowPerfDashboard(true);
    }
  }, []);

  async function fetchDashboardUserContext() {
    try {
      const identityParams = getDashboardIdentityParams();
      const meResponse = await api.get("/api/me", { params: identityParams });
      const user = meResponse.data?.user || null;
      setDashboardMe(user);
    } catch (err) {
      console.error("Failed to load dashboard user context:", err);
    }
  }

  useEffect(() => {
    if (!isSystemsInventoryPage) {
      fetchDashboardUserContext();
    }
  }, [isSystemsInventoryPage]);

  const handleDeleteProject = async (id) => {


    try {
      await api.delete(`/api/projects/${id}`);
      setMajorProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  // 👇 PUT IT HERE (with the others)
  useEffect(() => {
    console.log("STATE UPDATED:", majorProjects);
  }, [majorProjects]);

  const markProjectsDirty = () => {
    setProjectsDirty(true);
    setSaveStatus((current) => (current === "saving" ? current : "idle"));
  };

  const buildProjectsPayload = () => (
    majorProjects.map((p, idx) => ({
      id: p.id || null,
      tempId: p.tempId || null,
      project_name: p.project_name || "",
      completed_date: p.completed_date || null,
      sort_order: p.sort_order ?? idx
    }))
  );

  const hydrateProjects = (projects) => (
    projects
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => ({
        id: p.id,
        tempId: p.temp_id || p.tempId || uuidv4(),
        project_name: p.project_name,
        completed_date: p.completed_date,
        sort_order: p.sort_order
      }))
  );

  const saveProjects = async () => {
    setSaveStatus("saving");

    try {
      const payload = buildProjectsPayload();
      await api.post("/api/projects", { projects: payload });
      const refreshed = await api.get("/api/projects");

      setMajorProjects(hydrateProjects(refreshed.data.projects));
      setProjectsDirty(false);
      setSaveStatus("saved");

      setTimeout(() => {
        setSaveStatus((current) => (current === "saved" ? "idle" : current));
      }, 2000);
    } catch (err) {
      console.error("Failed saving projects:", err);
      setSaveStatus("idle");
    }
  };

  async function refreshTodaySales() {
    const requestMonth = selectedMonth;
    if (!isSelectedMonthActive) {
      return;
    }

    try {
      const response = await api.get("/api/today-sales");
      if (selectedMonthRef.current !== requestMonth) {
        return;
      }

      if (response.status === 401) {
        setStoreConnected(false);
        return;
      }

      setTodaysSales(response.data.todaySales || 0);
      setTodaysGoal(response.data.todaysGoal || 0);
      setIntradayPacing(response.data.intradayPacing || null);
      const liveSalesTotals = response.data.liveSalesTotals || null;
      if (liveSalesTotals) {
        setLiveSalesMTDWithPOS(Number(liveSalesTotals.monthToDateWithPOS || 0));
        setYearlyStats((current) => ({
          ...(current || {}),
          ...liveSalesTotals
        }));
      }
      setRefreshCountdown(TODAY_SALES_REFRESH_INTERVAL_SECONDS);
      setStoreConnected(true);
    } catch (err) {
      console.error("Error refreshing today's sales:", err);
    }
  }

  async function fetchScopedSalesTotals(options = {}) {
    const requestMonth = options.month || selectedMonthRef.current;
    const requestScope = options.scope || salesStoreScopeRef.current;
    setScopedSalesLoading(true);

    try {
      const response = await api.get("/api/store-sales", {
        params: {
          month: requestMonth,
          scope: requestScope
        }
      });

      if (selectedMonthRef.current !== requestMonth || salesStoreScopeRef.current !== requestScope) {
        return;
      }

      setScopedSalesSnapshot(response.data || null);
      setScopedSalesError(null);
    } catch (err) {
      if (selectedMonthRef.current !== requestMonth || salesStoreScopeRef.current !== requestScope) {
        return;
      }

      const errorPayload = err?.response?.data || {};
      setScopedSalesSnapshot(null);
      setScopedSalesError({
        message: errorPayload.error || "Selected store sales are unavailable.",
        connectUrl: errorPayload.connectUrl || null
      });
      console.error("Error fetching scoped store sales:", err);
    } finally {
      if (selectedMonthRef.current === requestMonth && salesStoreScopeRef.current === requestScope) {
        setScopedSalesLoading(false);
      }
    }
  }

  function withIntelligentForecastContext(summaryText, chartRows = null) {
    const rows = Array.isArray(chartRows) && chartRows.length ? chartRows : chartData;
    if (!Array.isArray(rows) || !rows.length) {
      return summaryText || "";
    }

    return summaryText || "";
  }

  async function fetchDashboardSummary(chartRows = null, options = {}) {
    const { force = false } = options;
    const requestMonth = selectedMonth;

    try {
      const response = await api.get("/api/dashboard-summary", {
        params: {
          month: requestMonth,
          ...(force ? { force: 1 } : {})
        }
      });
      if (selectedMonthRef.current !== requestMonth || response.data?.selectedMonth !== requestMonth) {
        return;
      }

      if (response.status >= 200 && response.status < 400) {
        setSalesSummary(withIntelligentForecastContext(response.data?.summary?.text || "", chartRows));
        setSalesSummaryBullets(Array.isArray(response.data?.summary?.bullets) ? response.data.summary.bullets : []);
        return;
      }
    } catch (err) {
      console.error("Error fetching dashboard summary:", err);
    }

    if (chartRows && selectedMonthRef.current === requestMonth) {
      setSalesSummary(withIntelligentForecastContext(webSalesTrendSentence(chartRows), chartRows));
      setSalesSummaryBullets([]);
    }
  }

  async function fetchGlossaryTerms() {
    try {
      const response = await api.get("/api/glossary");
      const terms = response.data?.terms;
      setGlossaryTermsMap(mergeGlossaryTermsWithDefaults(terms));
    } catch (err) {
      console.error("Error fetching glossary terms:", err);
    }
  }

  async function fetchWebopsStatuses() {
    try {
      const response = await api.get("/api/webops/statuses");
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setWebopsItems(items);
    } catch (err) {
      console.error("Error fetching WebOps statuses:", err);
    }
  }

  async function cycleWebopsStatus(item) {
    const currentStatus = String(item?.status || "green").toLowerCase();
    const currentIndex = WEBOPS_STATUS_CYCLE.indexOf(currentStatus);
    const nextStatus = WEBOPS_STATUS_CYCLE[(currentIndex + 1) % WEBOPS_STATUS_CYCLE.length];
    try {
      if (nextStatus === "green") {
        // Green implies freshly verified, so update lastChecked too.
        await api.post(`/api/webops/statuses/${item.key}/mark-checked`, { suppressAlert: true });
      } else {
        await api.patch(`/api/webops/statuses/${item.key}`, { status: nextStatus, suppressAlert: true });
      }
      await fetchWebopsStatuses();
    } catch (err) {
      console.error("Error updating WebOps status:", err);
    }
  }

  async function markWebopsChecked(item) {
    try {
      await api.post(`/api/webops/statuses/${item.key}/mark-checked`, { suppressAlert: true });
      await fetchWebopsStatuses();
    } catch (err) {
      console.error("Error marking WebOps checked:", err);
    }
  }

  function handleWebopsStatusClick(item) {
    const itemKey = item?.key;
    if (!itemKey) {
      return;
    }

    clearTimeout(webopsClickTimersRef.current[itemKey]);
    webopsClickTimersRef.current[itemKey] = setTimeout(() => {
      delete webopsClickTimersRef.current[itemKey];
      cycleWebopsStatus(item);
    }, 250);
  }

  function handleWebopsStatusDoubleClick(item) {
    const itemKey = item?.key;
    if (!itemKey) {
      return;
    }

    clearTimeout(webopsClickTimersRef.current[itemKey]);
    delete webopsClickTimersRef.current[itemKey];
    markWebopsChecked(item);
  }

  async function fetchSystemsInventory(nextFilters = systemsInventoryFiltersRef.current) {
    setSystemsInventoryLoading(true);
    setSystemsInventoryError("");
    try {
      const response = await api.get("/api/systems-inventory", {
        params: Object.fromEntries(
          Object.entries(nextFilters || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
        )
      });
      setSystemsInventory(Array.isArray(response.data?.inventory)
        ? response.data.inventory.map((item) => ({
          ...item,
          notes_description: getSystemsInventoryNotesDescription(item)
        }))
        : []);
      setSystemsInventoryFilterOptions(response.data?.filters || {});
    } catch (err) {
      console.error("Error fetching systems inventory:", err);
      setSystemsInventoryError("Failed to load Systems Inventory.");
    } finally {
      setSystemsInventoryLoading(false);
    }
  }

  function updateSystemsInventoryFilter(key, value) {
    const nextFilters = {
      ...systemsInventoryFilters,
      [key]: value
    };
    setSystemsInventoryFilters(nextFilters);
    systemsInventoryFiltersRef.current = nextFilters;
    fetchSystemsInventory(nextFilters);
  }

  function openSystemsInventoryAddModal() {
    setSystemsInventoryFormMode("add");
    setSystemsInventoryFormDraft({ ...SYSTEMS_INVENTORY_EMPTY_FORM });
    setSystemsInventoryFormOpen(true);
  }

  function openSystemsInventoryEditModal(item) {
    setSystemsInventoryFormMode("edit");
    setSystemsInventoryFormDraft({
      ...SYSTEMS_INVENTORY_EMPTY_FORM,
      ...item,
      vendor_bundle: item?.vendor_bundle ? 1 : 0,
      last_reviewed: item?.last_reviewed ? String(item.last_reviewed).slice(0, 10) : ""
    });
    setSystemsInventoryFormOpen(true);
  }

  function updateSystemsInventoryDraft(field, value) {
    setSystemsInventoryFormDraft((current) => {
      const next = {
        ...current,
        [field]: field === "vendor_bundle" ? Number(value) : value
      };
      if (field === "netsuite_internal_id") {
        next.view_url = buildSystemsInventoryNetSuiteUrl(value, false);
        next.edit_url = buildSystemsInventoryNetSuiteUrl(value, true);
      }
      return next;
    });
  }

  async function saveSystemsInventoryItem() {
    setSystemsInventorySaving(true);
    try {
      const draft = {
        ...systemsInventoryFormDraft,
        view_url: systemsInventoryFormDraft.view_url || buildSystemsInventoryNetSuiteUrl(systemsInventoryFormDraft.netsuite_internal_id, false),
        edit_url: systemsInventoryFormDraft.edit_url || buildSystemsInventoryNetSuiteUrl(systemsInventoryFormDraft.netsuite_internal_id, true)
      };
      if (systemsInventoryFormMode === "edit" && draft.id) {
        await api.put(`/api/systems-inventory/${draft.id}`, draft);
      } else {
        await api.post("/api/systems-inventory", draft);
      }
      setSystemsInventoryFormOpen(false);
      await fetchSystemsInventory();
    } catch (err) {
      console.error("Error saving systems inventory item:", err);
      setSystemsInventoryError(err?.response?.data?.error || "Failed to save Systems Inventory item.");
    } finally {
      setSystemsInventorySaving(false);
    }
  }

  async function deleteSystemsInventoryItem(item) {
    const confirmed = window.confirm(`Soft delete "${item.component_name}" from Systems Inventory?`);
    if (!confirmed) return;
    try {
      await api.delete(`/api/systems-inventory/${item.id}`);
      await fetchSystemsInventory();
    } catch (err) {
      console.error("Error deleting systems inventory item:", err);
      setSystemsInventoryError("Failed to delete Systems Inventory item.");
    }
  }

  async function fetchReadinessProjects() {
    try {
      const response = await api.get("/api/readiness/projects");
      const projects = Array.isArray(response.data?.projects) ? response.data.projects : [];
      setReadinessProjects(projects);
      if (!projects.some((project) => project.project_code === readinessProjectCode) && projects[0]?.project_code) {
        setReadinessProjectCode(projects[0].project_code);
      }
    } catch (err) {
      console.error("Error loading readiness projects:", err);
      setReadinessError("Failed to load readiness projects.");
    }
  }

  async function fetchReadinessBoard(projectCode = readinessProjectCode) {
    setReadinessLoading(true);
    setReadinessError("");
    try {
      const response = await api.get(`/api/readiness/${encodeURIComponent(projectCode || READINESS_DEFAULT_PROJECT)}`);
      setReadinessProject(response.data?.project || null);
      setReadinessLifecycle(Array.isArray(response.data?.lifecycle) ? response.data.lifecycle : []);
      setReadinessItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setReadinessArtifacts(Array.isArray(response.data?.artifacts) ? response.data.artifacts : []);
      setReadinessComplianceAudits(Array.isArray(response.data?.complianceAudits) ? response.data.complianceAudits : []);
      setReadinessPlanAnalysis(response.data?.planAnalysis || null);
      setReadinessNormalizationAudit(Array.isArray(response.data?.normalizationAudit) ? response.data.normalizationAudit : []);
    } catch (err) {
      console.error("Error loading readiness board:", err);
      setReadinessError(err?.response?.data?.error || "Failed to load Readiness Board.");
    } finally {
      setReadinessLoading(false);
    }
  }

  async function fetchReadinessAiContext(projectId = readinessProject?.id) {
    if (!projectId) return;
    setReadinessAiContextLoading(true);
    setReadinessAiContextFeedback("");
    try {
      const response = await api.get(`/api/readiness/projects/${projectId}/ai-context`);
      setReadinessAiContext(response.data || null);
    } catch (err) {
      console.error("Error loading readiness AI context:", err);
      setReadinessAiContextFeedback("AI context failed to load.");
    } finally {
      setReadinessAiContextLoading(false);
    }
  }

  async function copyReadinessAiContextJson() {
    if (!readinessAiContext) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(readinessAiContext, null, 2));
      setReadinessAiContextFeedback("Copied.");
    } catch (err) {
      console.error("Error copying readiness AI context:", err);
      setReadinessAiContextFeedback("Copy failed.");
    }
  }

  async function copyReadinessAiBrief() {
    if (!readinessAiContext) return;
    const brief = readinessAiContext.aiBrief || {};
    const lines = [
      `AI Readiness Brief - ${readinessAiContext.project?.projectCode || "Project"}`,
      `Generated: ${readinessAiContext.generatedAt || "-"}`,
      "",
      `Current readiness state: ${brief.currentReadinessState || readinessAiContext.readinessAssessment?.summary || "-"}`,
      "",
      "Top blockers:",
      ...(brief.topBlockers || []).map((item) => `- ${item}`),
      "",
      "Top risks:",
      ...(brief.topRisks || []).map((item) => `- ${item}`),
      "",
      "Next recommended actions:",
      ...(brief.nextRecommendedActions || []).map((item) => `- ${item}`),
      "",
      "Questions leadership should ask:",
      ...(brief.questionsLeadershipShouldAsk || []).map((item) => `- ${item}`)
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setReadinessAiContextFeedback("Brief copied.");
    } catch (err) {
      console.error("Error copying readiness AI brief:", err);
      setReadinessAiContextFeedback("Brief copy failed.");
    }
  }

  async function copyReadinessBoardLink() {
    const url = buildShopifyReadinessProjectUrl(readinessProjectCode);
    try {
      await navigator.clipboard.writeText(url);
      setReadinessShareFeedback("Link copied.");
    } catch (err) {
      console.error("Error copying readiness board link:", err);
      setReadinessShareFeedback(url);
    }
  }

  async function copyNormalizedPlanForAi() {
    if (!readinessProject || !readinessPlanAnalysis) return;
    const isSessionPlan = readinessProject.project_code === MALTA_NETSUITE_SESSION_PROJECT_CODE;
    let exportPlanAnalysis = readinessPlanAnalysis;
    if (isSessionPlan && readinessProject.id) {
      try {
        const runtimeContext = await api.get(`/api/readiness/projects/${readinessProject.id}/ai-context`);
        if (!runtimeContext.data?.planAnalysis) throw new Error("Runtime plan analysis was missing from AI context.");
        exportPlanAnalysis = runtimeContext.data.planAnalysis;
        setReadinessPlanAnalysis(exportPlanAnalysis);
      } catch (err) {
        console.error("Error refreshing runtime AI plan:", err);
        setReadinessPlanCopyFeedback("AI plan refresh failed; stale data was not copied.");
        return;
      }
    }
    const payload = {
      purpose: isSessionPlan
        ? "Review, challenge, refine, or continue the Malta NetSuite Migration CLI-AI-v3 execution-session model."
        : "Review, challenge, refine, or continue the Malta NetSuite Migration normalized CLI/AI execution plan.",
      instructionsForAi: [
        "Treat MALTA-NS-SDF as the preserved 308-task / 1,794-hour baseline; do not overwrite it.",
        isSessionPlan
          ? "Preserve MALTA-NS-NORMALIZED (CLI-AI-v2) unchanged and analyze MALTA-NS-SESSION (CLI-AI-v3) at the 79-work-package execution-session level; baseline rows are checklist links, not estimate units."
          : "Analyze MALTA-NS-NORMALIZED as the second execution scenario.",
        "Keep AI as a copilot only. Every system action, review, correction, decision, and approval has an accountable human owner.",
        "Do not count automated elapsed time as continuous human labor.",
        "Do not reduce business testing, accounting reconciliation, data cleanup, vendor waits, decisions, cutover controls, or rollback planning without evidence.",
        "Keep Malta execution and reusable VSG migration-factory development hours separate.",
        "When proposing a change, identify the affected work package and linked baseline task IDs."
      ],
      generatedAt: new Date().toISOString(),
      project: readinessProject,
      planAnalysis: exportPlanAnalysis,
      normalizedWorkPackages: readinessItems.map((item) => ({
        id: item.id, title: item.title, workstream: item.workstream, phase: item.phase,
        owner: item.owner, supportingResources: item.supporting_resources, status: item.status,
        priority: item.priority, riskLevel: item.risk_level, criticalPath: Boolean(item.critical_path),
        plannedStartDate: item.planned_start_date, plannedCompletionDate: item.planned_completion_date,
        dependencies: item.dependencies, deliverable: item.deliverable, acceptanceCriteria: item.acceptance_criteria,
        traditionalExecutionMethod: item.traditional_execution_method,
        traditionalHumanHours: Number(item.traditional_estimated_hours || 0),
        cliAiExecutionMethod: item.cli_ai_execution_method,
        preparationHours: Number(item.cli_ai_preparation_hours || 0),
        automatedElapsedHours: Number(item.automated_elapsed_hours || 0),
        humanReviewHours: Number(item.human_review_hours || 0),
        correctionTroubleshootingHours: Number(item.human_correction_hours || 0),
        humanApprovalHours: Number(item.human_approval_hours || 0),
        normalizedHumanHours: Number(item.estimated_hours || 0),
        cliSdfHoursSaved: Number(item.cli_sdf_hours_saved || 0),
        jiraAutomationHoursSaved: Number(item.jira_automation_hours_saved || 0),
        aiAssistanceHoursSaved: Number(item.ai_assistance_hours_saved || 0),
        maltaExecutionHours: Number(item.malta_execution_hours || 0),
        factoryDevelopmentHours: Number(item.factory_development_hours || 0),
        executionSessionDesign: item.execution_session_design,
        distinctWorkSessions: Number(item.distinct_work_sessions || 0),
        sharedSetupActivities: item.shared_setup_activities,
        sharedReviewGate: item.shared_review_gate,
        reviewerRole: item.reviewer_role, reviewMaterial: item.review_material,
        reviewDecision: item.review_decision, separateReviewReason: item.separate_review_reason,
        reviewDurationHours: Number(item.review_duration_hours || 0),
        uncertaintyClassification: item.uncertainty_classification,
        primaryUncertainty: item.primary_uncertainty, reserveReleaseTrigger: item.reserve_release_trigger,
        baseExecutionHours: Number(item.base_execution_hours || 0),
        knownExceptionHours: Number(item.known_exception_hours || 0),
        riskProbability: Number(item.risk_probability || 0),
        fullRiskImpactHours: Number(item.risk_impact_hours || 0),
        riskReserveHours: Number(item.risk_reserve_hours || 0),
        conservativeHours: Number(item.conservative_hours || 0),
        vendorWaitingElapsedHours: Number(item.vendor_waiting_elapsed_hours || 0),
        v2EstimatedHours: Number(item.v2_estimated_hours || 0),
        v2PreparationHours: Number(item.v2_preparation_hours || 0),
        v2ReviewHours: Number(item.v2_review_hours || 0),
        v2TroubleshootingHours: Number(item.v2_troubleshooting_hours || 0),
        v2ApprovalHours: Number(item.v2_approval_hours || 0),
        factoryCategory: item.factory_category,
        maltaCriticalFactoryHours: Number(item.malta_critical_factory_hours || 0),
        deferredFactoryHours: Number(item.deferred_factory_hours || 0),
        confidence: item.confidence_level, estimateBasis: item.estimate_basis,
        scopeClassification: item.scope_classification, laborClassification: item.labor_classification,
        baselineTaskIds: item.baseline_task_ids
      })),
      baselineToNormalizedAudit: readinessNormalizationAudit.map((row) => ({
        baselineTaskId: row.baseline_task_id, baselineTask: row.baseline_task_title,
        normalizedTaskId: row.normalized_task_id, normalizedWorkPackage: row.normalized_task_title,
        assignmentBefore: row.assignment_before, assignmentAfter: row.assignment_after,
        traditionalExecutionMethod: row.traditional_execution_method,
        traditionalHumanHours: Number(row.traditional_hours || 0),
        cliAiExecutionMethod: row.cli_ai_execution_method,
        preparationHours: Number(row.cli_ai_preparation_hours || 0),
        automatedElapsedHours: Number(row.automated_elapsed_hours || 0),
        humanReviewHours: Number(row.human_review_hours || 0),
        correctionTroubleshootingHours: Number(row.human_correction_hours || 0),
        humanApprovalHours: Number(row.human_approval_hours || 0),
        normalizedHumanHours: Number(row.normalized_hands_on_hours || 0),
        cliSdfHoursSaved: Number(row.cli_sdf_hours_saved || 0),
        jiraAutomationHoursSaved: Number(row.jira_automation_hours_saved || 0),
        aiAssistanceHoursSaved: Number(row.ai_assistance_hours_saved || 0),
        maltaExecutionHours: Number(row.malta_execution_hours || 0),
        factoryDevelopmentHours: Number(row.factory_development_hours || 0),
        scopeClassification: row.scope_classification, laborClassification: row.labor_classification,
        confidence: row.confidence_level, estimateBasis: row.estimate_basis
      }))
    };
    const text = [
      `Malta NetSuite Migration — ${isSessionPlan ? "CLI-AI-v3 execution-session" : "normalized CLI/AI"} plan context for ChatGPT`,
      "Use the instructions and structured project data below as the source of truth for this review.",
      "",
      JSON.stringify(payload, null, 2)
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setReadinessPlanCopyFeedback(`AI plan copied (${Math.round(text.length / 1024)} KB).`);
    } catch (err) {
      console.error("Error copying normalized AI plan:", err);
      setReadinessPlanCopyFeedback("AI plan copy failed.");
    }
  }

  async function saveReadinessItemCell(cell) {
    const field = cell.getField();
    const rowData = cell.getRow().getData();
    if (!field || !rowData?.id) return;

    setReadinessSaving(true);
    setReadinessError("");
    try {
      const payload = { [field]: field === "due_date" ? formatReadinessDate(cell.getValue()) : cell.getValue() };
      const response = await api.put(`/api/readiness/items/${rowData.id}`, payload);
      const savedItem = response.data?.item;
      if (savedItem) {
        setReadinessItems((current) => current.map((item) => (item.id === savedItem.id ? savedItem : item)));
      }
    } catch (err) {
      console.error("Error saving readiness item:", err);
      setReadinessError(err?.response?.data?.error || "Failed to save readiness item.");
      await fetchReadinessBoard();
    } finally {
      setReadinessSaving(false);
    }
  }

  async function saveReadinessLifecycleRow(row, patch) {
    if (!row?.id) return;
    const optimisticRow = { ...row, ...patch };
    setReadinessLifecycle((current) => current.map((item) => (item.id === row.id ? optimisticRow : item)));
    setReadinessSaving(true);
    setReadinessError("");
    try {
      const response = await api.put(`/api/readiness/lifecycle/${row.id}`, patch);
      const savedLifecycle = response.data?.lifecycle;
      if (savedLifecycle) {
        setReadinessLifecycle((current) => current.map((item) => (
          item.id === savedLifecycle.id ? savedLifecycle : item
        )));
      }
    } catch (err) {
      console.error("Error saving readiness lifecycle:", err);
      setReadinessError(err?.response?.data?.error || "Failed to save lifecycle row.");
      await fetchReadinessBoard();
    } finally {
      setReadinessSaving(false);
    }
  }

  async function saveReadinessArtifactCell(cell) {
    const field = cell.getField();
    const rowData = cell.getRow().getData();
    if (!field || !rowData?.id) return;

    setReadinessSaving(true);
    setReadinessError("");
    try {
      const response = await api.put(`/api/readiness/artifacts/${rowData.id}`, { [field]: cell.getValue() });
      const savedArtifact = response.data?.artifact;
      if (savedArtifact) {
        setReadinessArtifacts((current) => current.map((artifact) => (
          artifact.id === savedArtifact.id ? savedArtifact : artifact
        )));
      }
    } catch (err) {
      console.error("Error saving readiness artifact:", err);
      setReadinessError(err?.response?.data?.error || "Failed to save readiness artifact.");
      await fetchReadinessBoard();
    } finally {
      setReadinessSaving(false);
    }
  }

  async function saveReadinessComplianceAuditCell(cell) {
    const field = cell.getField();
    const rowData = cell.getRow().getData();
    if (!field || !rowData?.id) return;

    setReadinessComplianceSaving(true);
    setReadinessComplianceError("");
    try {
      const payload = { [field]: field === "due_date" ? formatReadinessDate(cell.getValue()) : cell.getValue() };
      const response = await api.put(`/api/readiness/compliance-audits/${rowData.id}`, payload);
      const savedAudit = response.data?.complianceAudit;
      if (savedAudit) {
        setReadinessComplianceAudits((current) => current.map((audit) => (
          audit.id === savedAudit.id ? savedAudit : audit
        )));
      }
    } catch (err) {
      console.error("Error saving readiness compliance audit:", err);
      setReadinessComplianceError(err?.response?.data?.error || "Failed to save Prop 65 review row.");
      await fetchReadinessBoard();
    } finally {
      setReadinessComplianceSaving(false);
    }
  }

  async function addReadinessItem() {
    setReadinessSaving(true);
    setReadinessError("");
    try {
      const nextSortOrder = readinessItems.reduce((max, item) => Math.max(max, Number(item.sort_order || 0)), 0) + 1;
      const response = await api.post(
        `/api/readiness/${encodeURIComponent(readinessProjectCode || READINESS_DEFAULT_PROJECT)}/items`,
        { ...READINESS_EMPTY_ITEM, sort_order: nextSortOrder }
      );
      const item = response.data?.item;
      if (item) {
        setReadinessItems((current) => [...current, item]);
      } else {
        await fetchReadinessBoard();
      }
    } catch (err) {
      console.error("Error adding readiness item:", err);
      setReadinessError(err?.response?.data?.error || "Failed to add readiness item.");
    } finally {
      setReadinessSaving(false);
    }
  }

  async function addReadinessComplianceAudit() {
    setReadinessComplianceSaving(true);
    setReadinessComplianceError("");
    try {
      const nextSortOrder = readinessComplianceAudits.reduce((max, audit) => Math.max(max, Number(audit.sort_order || 0)), 0) + 1;
      const response = await api.post(
        `/api/readiness/${encodeURIComponent(readinessProjectCode || READINESS_DEFAULT_PROJECT)}/compliance-audits`,
        { ...READINESS_COMPLIANCE_EMPTY_AUDIT, sort_order: nextSortOrder }
      );
      const audit = response.data?.complianceAudit;
      if (audit) {
        setReadinessComplianceAudits((current) => [...current, audit]);
      } else {
        await fetchReadinessBoard();
      }
    } catch (err) {
      console.error("Error adding readiness compliance audit:", err);
      setReadinessComplianceError(err?.response?.data?.error || "Failed to add Prop 65 review row.");
    } finally {
      setReadinessComplianceSaving(false);
    }
  }

  function getMountedSystemsInventoryTable() {
    const table = systemsInventoryTabulatorRef.current;
    const element = systemsInventoryTableRef.current;
    if (!isSystemsInventoryPage || !table || !element || !element.isConnected) {
      return null;
    }
    return table;
  }

  function downloadVisibleSystemsInventoryCsv() {
    const table = getMountedSystemsInventoryTable();
    const activeRows = table
      ? table.getData("active")
      : systemsInventoryVisibleRows;
    downloadSystemsInventoryCsv(activeRows);
  }

  function mergeChecklistSections(sections) {
    const sectionMap = new Map(
      Array.isArray(sections) ? sections.map((section) => [String(section?.key || ""), section]) : []
    );
    return CONTENT_MERCH_CHECKLIST_TEMPLATE.map((templateSection) => {
      const section = sectionMap.get(templateSection.key) || {};
      const itemMap = new Map(
        Array.isArray(section?.items) ? section.items.map((item) => [String(item?.key || ""), item]) : []
      );
      return {
        key: templateSection.key,
        title: templateSection.title,
        items: templateSection.items.map((templateItem) => ({
          key: templateItem.key,
          label: templateItem.label,
          checked: Boolean(itemMap.get(templateItem.key)?.checked)
        }))
      };
    });
  }

  async function openContentMerchChecklistModal() {
    setContentMerchChecklistSaving(true);
    try {
      const response = await api.get("/api/webops/checklists/content-merchandising");
      const checklist = response.data?.checklist || {};
      setContentMerchChecklistState({
        sections: mergeChecklistSections(checklist.sections),
        notes: String(checklist.notes || ""),
        issueSeverity: String(checklist.issueSeverity || "none").toLowerCase(),
        lastCompletedAt: checklist.lastCompletedAt || null,
        lastSavedAt: checklist.lastSavedAt || null,
        lastCompletedBy: checklist.lastCompletedBy || null,
        lastCompletionSnapshot: checklist.lastCompletionSnapshot || null,
        effectiveStatus: String(response.data?.effectiveStatus || checklist.status || "red").toLowerCase(),
        isComplete: Boolean(response.data?.isComplete)
      });
      setContentMerchChecklistModalOpen(true);
    } catch (err) {
      console.error("Error loading content merchandising checklist:", err);
    } finally {
      setContentMerchChecklistSaving(false);
    }
  }

  function toggleChecklistItem(sectionKey, itemKey, checkedValue) {
    setContentMerchChecklistState((current) => ({
      ...current,
      sections: current.sections.map((section) => (
        section.key === sectionKey
          ? {
            ...section,
            items: section.items.map((item) => (
              item.key === itemKey ? { ...item, checked: checkedValue } : item
            ))
          }
          : section
      ))
    }));
  }

  async function saveContentMerchChecklistProgress() {
    setContentMerchChecklistSaving(true);
    try {
      const response = await api.post("/api/webops/checklists/content-merchandising/save-progress", {
        sections: contentMerchChecklistState.sections,
        notes: contentMerchChecklistState.notes,
        issueSeverity: contentMerchChecklistState.issueSeverity
      });
      const checklist = response.data?.checklist || {};
      setContentMerchChecklistState((current) => ({
        ...current,
        sections: mergeChecklistSections(checklist.sections),
        notes: String(checklist.notes || ""),
        issueSeverity: String(checklist.issueSeverity || current.issueSeverity || "none").toLowerCase(),
        lastSavedAt: checklist.lastSavedAt || current.lastSavedAt,
        isComplete: Boolean(response.data?.isComplete)
      }));
      await fetchWebopsStatuses();
    } catch (err) {
      console.error("Error saving checklist progress:", err);
    } finally {
      setContentMerchChecklistSaving(false);
    }
  }

  async function markContentMerchChecklistComplete() {
    setContentMerchChecklistCompleting(true);
    try {
      const response = await api.post("/api/webops/checklists/content-merchandising/mark-complete", {
        sections: contentMerchChecklistState.sections,
        notes: contentMerchChecklistState.notes,
        issueSeverity: contentMerchChecklistState.issueSeverity
      });
      const checklist = response.data?.checklist || {};
      setContentMerchChecklistState((current) => ({
        ...current,
        sections: mergeChecklistSections(checklist.sections),
        notes: String(checklist.notes || ""),
        issueSeverity: String(checklist.issueSeverity || current.issueSeverity || "none").toLowerCase(),
        lastCompletedAt: checklist.lastCompletedAt || current.lastCompletedAt,
        lastCompletedBy: checklist.lastCompletedBy || current.lastCompletedBy || null,
        lastCompletionSnapshot: checklist.lastCompletionSnapshot || current.lastCompletionSnapshot || null,
        lastSavedAt: checklist.lastSavedAt || current.lastSavedAt,
        effectiveStatus: String(response.data?.effectiveStatus || "green").toLowerCase(),
        isComplete: true
      }));
      await fetchWebopsStatuses();
      setContentMerchChecklistModalOpen(false);
    } catch (err) {
      console.error("Error marking checklist complete:", err);
    } finally {
      setContentMerchChecklistCompleting(false);
    }
  }

  async function fetchTrafficData() {
    const requestMonth = selectedMonth;
    setTrafficLoading(true);
    setTrafficError("");

    try {
      const trafficMonthEnd = parseDashboardMonth(requestMonth);
      const trafficMonthStart = new Date(trafficMonthEnd.getFullYear(), 0, 1);
      const response = await api.get("/api/traffic/charts", {
        params: {
          month_from: formatDashboardMonth(trafficMonthStart),
          month_to: requestMonth,
          cart_abandonment_mode: cartAbandonmentMode,
          click_through_mode: clickThroughMode
        }
      });
      if (selectedMonthRef.current !== requestMonth) {
        return;
      }
      setTrafficCharts({
        allTraffic: Array.isArray(response.data?.allTraffic) ? response.data.allTraffic : [],
        direct: Array.isArray(response.data?.direct) ? response.data.direct : [],
        organic: Array.isArray(response.data?.organic) ? response.data.organic : [],
        paid: Array.isArray(response.data?.paid) ? response.data.paid : [],
        conversionRate: Array.isArray(response.data?.conversionRate) ? response.data.conversionRate : [],
        cartAbandonment: Array.isArray(response.data?.cartAbandonment) ? response.data.cartAbandonment : [],
        clickThrough: Array.isArray(response.data?.clickThrough) ? response.data.clickThrough : []
      });
      setTrafficSummary(response.data?.summary || {});
      setTrafficSignals(response.data?.trafficSignals || {});
      setTopReferringChannels(response.data?.topReferringChannels || { allTraffic: [], direct: [], organic: [], paid: [], conversionRate: [], cartAbandonment: [], clickThrough: [] });
      setTrafficComparisonWindow(response.data?.comparisonWindow || null);
      if (response.data?.clickThroughMode === "paid" || response.data?.clickThroughMode === "organic") {
        setClickThroughMode(response.data.clickThroughMode);
      }
    } catch (err) {
      console.error("Error fetching traffic analytics:", err);
      if (selectedMonthRef.current === requestMonth) {
        setTrafficError("Unable to load traffic analytics.");
      }
    } finally {
      if (selectedMonthRef.current === requestMonth) {
        setTrafficLoading(false);
      }
    }
  }

  const buildChartWithForecasts = ({
    dailyGoals,
    todaySales,
    backendGoal,
    forecastModel
  }) => {
    let mtdActual = 0;
    let mtdGoal = 0;
    let mtdWebOnly = 0;

    const sortedGoals = (dailyGoals || []).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    const daysInMonth = sortedGoals.length || 0;

    const todayISO = new Date().toLocaleDateString("en-CA");
    const simulationAmounts = showSimulation
      ? simulationDraft
        .map((entry) => Number(String(entry.amount || "").replace(/[^0-9.-]/g, "")))
        .filter((amount) => Number.isFinite(amount) && amount > 0)
      : [];

    const chartDataSafe = (sortedGoals || []).map((dg) => {
      const [year, month, day] = dg.date.split("-").map(Number);
      const dt = new Date(year, month - 1, day);
      const isToday = dg.date === todayISO;
      const web = isToday
        ? (todaySales || 0)
        : (dg.channels?.["Online Store"]?.net || 0);
      const pos = isToday
        ? 0
        : (dg.channels?.["Point of Sale"]?.net || 0);

      const total = web + pos;
      const goal = Number(dg.goal) || 0;
      const dailyPercent = goal > 0 ? (total / goal) * 100 : 0;
      const dailyWebPercent = goal > 0 ? (web / goal) * 100 : 0;
      const isFuture = dg.date > todayISO;

      mtdActual += total;
      mtdGoal += goal;
      mtdWebOnly += web;

      return {
        date: dt,
        dateISO: dg.date,
        formattedDate: dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        goal,
        actual: total,
        webOnly: web,
        posOnly: pos,
        dailyPercentOfGoal: isFuture ? null : dailyPercent,
        dailyWebPercentOfGoal: isFuture ? null : dailyWebPercent,
        mtdPercentOfGoal: isFuture ? null : (mtdGoal > 0 ? (mtdActual / mtdGoal) * 100 : 0),
        mtdWebPercentOfGoal: isFuture ? null : (mtdGoal > 0 ? (mtdWebOnly / mtdGoal) * 100 : 0)
      };
    });

    let runningWebPctTotal = 0;
    let runningWebPctCount = 0;
    let simulationIndex = 0;

    const finalChartData = chartDataSafe.map((d) => {
      let simulationAmount = null;
      let effectiveDailyWebPercent = d.dailyWebPercentOfGoal;

      if ((effectiveDailyWebPercent === null || effectiveDailyWebPercent === undefined) && simulationIndex < simulationAmounts.length) {
        simulationAmount = simulationAmounts[simulationIndex];
        simulationIndex += 1;
        effectiveDailyWebPercent = d.goal > 0 ? (simulationAmount / d.goal) * 100 : 0;
      }

      if (effectiveDailyWebPercent !== null && effectiveDailyWebPercent !== undefined) {
        runningWebPctTotal += effectiveDailyWebPercent;
        runningWebPctCount += 1;
      }

      return {
        ...d,
        simulatedWebOnly: simulationAmount,
        simulationDailyWebPercentOfGoal: simulationAmount !== null ? effectiveDailyWebPercent : null,
        webTrend: runningWebPctCount > 0 ? (runningWebPctTotal / runningWebPctCount) : null
      };
    });

    const actualWebDayIndexes = finalChartData
      .map((day, index) => (
        day.dailyWebPercentOfGoal !== null && day.dailyWebPercentOfGoal !== undefined
          ? index
          : null
      ))
      .filter((index) => index !== null);

    const scenarioKeys = actualWebDayIndexes
      .slice(0, -1)
      .map((_, scenarioIndex) => `webScenario${scenarioIndex + 1}`);

    const lastChartIndex = finalChartData.length - 1;
    const chartWithScenarios = finalChartData.map((day) => ({ ...day }));

    actualWebDayIndexes.slice(0, -1).forEach((startIndex, scenarioIndex) => {
      const scenarioKey = `webScenario${scenarioIndex + 1}`;
      const startValue = chartWithScenarios[startIndex].dailyWebPercentOfGoal;
      const projectedFinish = chartWithScenarios[startIndex].webTrend;
      const remainingPoints = Math.max(lastChartIndex - startIndex, 1);

      for (let pointIndex = startIndex; pointIndex <= lastChartIndex; pointIndex += 1) {
        const progress = (pointIndex - startIndex) / remainingPoints;
        const easedProgress = 1 - ((1 - progress) ** 2);

        chartWithScenarios[pointIndex][scenarioKey] = startValue + (
          (projectedFinish - startValue) * easedProgress
        );
      }
    });

    const simulationTotal = simulationAmounts.reduce((sum, amount) => sum + amount, 0);
    const futureChartDays = finalChartData.filter((day) => day.dailyWebPercentOfGoal === null || day.dailyWebPercentOfGoal === undefined);
    const coreDailyRunRate = Number(
      forecastModel?.debug?.weightedRecentRunRate
      || forecastModel?.debug?.coreDailyRunRate
      || 0
    );
    const simulatedEventContribution = {
      outperform: 0,
      breakout: 0,
      extreme: 0
    };

    let simulationCoreDelta = 0;
    simulationAmounts.forEach((amount, index) => {
      const futureDay = futureChartDays[index];
      const simulatedGoal = Number(futureDay?.goal || 0);
      const dailyGoalPct = simulatedGoal > 0 ? (amount / simulatedGoal) * 100 : 0;
      const monthlyGoalPct = backendGoal > 0 ? (amount / backendGoal) * 100 : 0;

      let simulationClass = "core";
      if (dailyGoalPct >= Number(forecastModel?.thresholds?.extreme?.dailyGoalPctMin || 0) &&
        monthlyGoalPct >= Number(forecastModel?.thresholds?.extreme?.monthlyGoalPctMin || 0)) {
        simulationClass = "extreme";
      } else if (dailyGoalPct >= Number(forecastModel?.thresholds?.breakout?.dailyGoalPctMin || 0) &&
        monthlyGoalPct >= Number(forecastModel?.thresholds?.breakout?.monthlyGoalPctMin || 0)) {
        simulationClass = "breakout";
      } else if (dailyGoalPct >= Number(forecastModel?.thresholds?.outperform?.dailyGoalPctMin || 0)) {
        simulationClass = "outperform";
      }

      simulationCoreDelta += amount - coreDailyRunRate;

      if (simulationClass !== "core") {
        simulatedEventContribution[simulationClass] += Math.max(amount - simulatedGoal, 0);
      }
    });

    const coreForecast = Number(forecastModel?.coreForecast || 0) + simulationCoreDelta;
    const remainingOutperformForecast = Math.max(
      0,
      Number(forecastModel?.outperformForecast || 0) - simulatedEventContribution.outperform
    );
    const remainingBreakoutForecast = Math.max(
      0,
      Number(forecastModel?.breakoutForecast || 0) - simulatedEventContribution.breakout
    );
    const remainingExtremeForecast = Math.max(
      0,
      Number(forecastModel?.extremeForecast || 0) - simulatedEventContribution.extreme
    );
    const intelligentForecastTarget = coreForecast + remainingOutperformForecast + remainingBreakoutForecast + remainingExtremeForecast;
    const realitySeasoningPct = backendGoal > 0 && intelligentForecastTarget > 0
      ? (intelligentForecastTarget / backendGoal) * 100
      : null;
    const lastActualWebIndex = actualWebDayIndexes.length
      ? actualWebDayIndexes[actualWebDayIndexes.length - 1]
      : -1;
    const baseForecastPct = chartWithScenarios.length
      ? Number(chartWithScenarios[chartWithScenarios.length - 1]?.webTrend || 0)
      : 0;
    const realitySeasoningStartPct = lastActualWebIndex >= 0
      ? Number(chartWithScenarios[lastActualWebIndex]?.webTrend ?? chartWithScenarios[lastActualWebIndex]?.dailyWebPercentOfGoal ?? baseForecastPct)
      : baseForecastPct;
    const remainingRealityPoints = Math.max(lastChartIndex - lastActualWebIndex, 1);

    if (realitySeasoningPct !== null && Number.isFinite(realitySeasoningPct) && lastActualWebIndex >= 0) {
      for (let pointIndex = lastActualWebIndex; pointIndex <= lastChartIndex; pointIndex += 1) {
        const progress = (pointIndex - lastActualWebIndex) / remainingRealityPoints;
        chartWithScenarios[pointIndex].realitySeasoning = realitySeasoningStartPct + (
          (realitySeasoningPct - realitySeasoningStartPct) * progress
        );
      }
    }

    if (showRealitySeasoning) {
      console.log("Reality seasoning debug", {
        backendGoal,
        simulationTotal,
        simulationCoreDelta,
        simulatedEventContribution,
        coreForecast,
        remainingOutperformForecast,
        remainingBreakoutForecast,
        remainingExtremeForecast,
        intelligentForecastTarget,
        backendBaseForecast: Number(forecastModel?.baseForecast || 0)
      });
    }

    return {
      chartWithScenarios,
      scenarioKeys
    };
  };

  async function fetchDashboardData(options = {}) {
    const { showLoadingSpinner = false, todayOnly = false } = options;
    const requestMonth = selectedMonth;

    if (showLoadingSpinner) {
      setLoading(true);
    }

    try {
      const response = await api.get("/api/dashboard", {
        params: { month: requestMonth }
      });
      let juneDetailsFromAiContract = null;
      try {
        const aiLiteResponse = await api.get("/api/dashboard-ai-context-lite", {
          params: {
            month: requestMonth,
            cart_abandonment_mode: cartAbandonmentMode,
            click_through_mode: clickThroughMode
          }
        });
        if (selectedMonthRef.current === requestMonth && aiLiteResponse?.data?.selectedMonth === requestMonth) {
          juneDetailsFromAiContract = aiLiteResponse?.data?.juneDetailsIntelligence || null;
        }
      } catch (aiLiteErr) {
        juneDetailsFromAiContract = null;
      }

      if (selectedMonthRef.current !== requestMonth || response.data?.selectedMonth !== requestMonth) {
        return;
      }

      if (response.status === 401) {
        setStoreConnected(false);
        return;
      }

      const {
        environment: env,
        isActiveMonth: responseIsActiveMonth,
        periodLastCompletedDate,
        yesterday,
        refunds,
        monthToDate,
        monthToDateWithPOS,
        yesterdayOrders,
        monthlyGoal: backendGoal,
        onlineStoreYesterday: onlineYesterday,
        posYesterday: posYest,
        topProductsMTD: topProductsFromBackend,
        dailyGoals,
        yearly,
        forecastModel,
        orderMixInsights,
        pacingDiagnosticsV2: pacingDiagnosticsFromBackend,
        todaySales,
        liveSalesTotals,
        intradayPacing: intradayPacingFromBackend
      } = response.data;

      setTodaysSales(responseIsActiveMonth ? (todaySales || 0) : (yesterday || 0));
      setRefreshCountdown(TODAY_SALES_REFRESH_INTERVAL_SECONDS);

      if (todayOnly) {
        setStoreConnected(true);
        return;
      }

      const sortedGoals = (dailyGoals || []).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      const selectedPeriodISO = periodLastCompletedDate || sortedGoals[sortedGoals.length - 1]?.date || new Date().toLocaleDateString("en-CA");
      const selectedPeriodDate = parseDashboardDate(selectedPeriodISO);
      const selectedDayISO = responseIsActiveMonth
        ? new Date().toLocaleDateString("en-CA")
        : selectedPeriodISO;
      const plannedThroughYesterday = sortedGoals
        .filter((entry) => String(entry?.date || "") <= selectedPeriodISO)
        .reduce((sum, entry) => sum + (Number(entry?.goal) || 0), 0);

      setSalesYesterday(yesterday || 0);
      setEnvironment(env);
      setRefundsYesterday(refunds || 0);
      setSalesMTD(monthToDate || 0);
      setSalesMTDWithPOS(monthToDateWithPOS || 0);
      setLiveSalesMTDWithPOS(
        responseIsActiveMonth
          ? Number(liveSalesTotals?.monthToDateWithPOS ?? monthToDateWithPOS ?? 0)
          : Number(monthToDateWithPOS || 0)
      );
      setMonthlyGoal(backendGoal || 0);
      setOrders(yesterdayOrders || []);
      setOnlineStoreYesterday(onlineYesterday || 0);
      setPosYesterday(posYest || 0);
      setPlanMTD(plannedThroughYesterday);
      setYearlyStats(
        responseIsActiveMonth && liveSalesTotals
          ? { ...(yearly || {}), ...liveSalesTotals }
          : (yearly || null)
      );
      setIntradayPacing(intradayPacingFromBackend || null);
      setPacingDiagnosticsV2(pacingDiagnosticsFromBackend || null);

      setPercentToPlan(
        plannedThroughYesterday > 0
          ? ((monthToDate / plannedThroughYesterday) * 100).toFixed(1)
          : 0
      );

      setPercentToPlanWithPOS(
        plannedThroughYesterday > 0
          ? ((monthToDateWithPOS / plannedThroughYesterday) * 100).toFixed(1)
          : 0
      );

      setStoreConnected(true);
      setTopProductsMTD(topProductsFromBackend || []);

      const todayGoalRow = sortedGoals.find((g) => g.date === selectedDayISO);
      const yesterdayISO = selectedPeriodDate.toLocaleDateString("en-CA");
      const yesterdayGoalRow = sortedGoals.find((g) => g.date === yesterdayISO);
      const todayGoalValue = Number(todayGoalRow?.goal) || 0;
      const yesterdayGoalValue = Number(yesterdayGoalRow?.goal) || 0;
      setTodaysGoal(todayGoalValue);
      setGoalYesterday(yesterdayGoalValue);
      const chartSource = {
        dailyGoals,
        todaySales,
        backendGoal,
        plannedThroughYesterday,
        monthToDate,
        forecastModel,
        orderMixInsights,
        periodLastCompletedDate,
        sales: {
          liveOrderCount: Number(response?.data?.sales?.liveOrderCount || 0)
        },
        juneDetailsIntelligence: juneDetailsFromAiContract
      };
      setDashboardChartSource(chartSource);
      const { chartWithScenarios, scenarioKeys } = buildChartWithForecasts(chartSource);
      setWebScenarioKeys(scenarioKeys);
      setChartData(chartWithScenarios);
      // Do not block primary dashboard paint on AI summary latency.
      fetchDashboardSummary(chartWithScenarios);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      if (selectedMonthRef.current === requestMonth) {
        setStoreConnected(false);
      }
    } finally {
      if (showLoadingSpinner && selectedMonthRef.current === requestMonth) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    fetchDashboardData({ showLoadingSpinner: true });
    fetchGlossaryTerms();
    fetchTrafficData();
    fetchWebopsStatuses();
  }, [selectedMonth, cartAbandonmentMode, clickThroughMode]);

  useEffect(() => {
    fetchScopedSalesTotals({ month: selectedMonth, scope: salesStoreScope });
  }, [selectedMonth, salesStoreScope]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchWebopsStatuses();
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (webopsLampRanRef.current) {
      return undefined;
    }
    if (!Array.isArray(webopsItems) || !webopsItems.length) {
      return undefined;
    }
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      webopsLampRanRef.current = true;
      return undefined;
    }

    webopsLampRanRef.current = true;
    const timers = [];
    const colors = ["red", "orange", "yellow", "green"];
    const dotStepMs = 165;
    const dotStaggerMs = 210;
    const initialDelayMs = 1500;

    webopsItems.forEach((item, index) => {
      colors.forEach((color, colorIndex) => {
        const delayMs = initialDelayMs + (index * dotStaggerMs) + (colorIndex * dotStepMs);
        timers.push(setTimeout(() => {
          setWebopsLampOverrides((current) => ({
            ...current,
            [item.key]: color
          }));
        }, delayMs));
      });
    });

    const lastDotEndMs = initialDelayMs + ((webopsItems.length - 1) * dotStaggerMs) + (colors.length * dotStepMs);
    const blinkStartMs = lastDotEndMs + 40;
    const blinkStepMs = 250;
    // Blink all dots twice: off/on, off/on.
    timers.push(setTimeout(() => setWebopsLampBlinkOff(true), blinkStartMs));
    timers.push(setTimeout(() => setWebopsLampBlinkOff(false), blinkStartMs + blinkStepMs));
    timers.push(setTimeout(() => setWebopsLampBlinkOff(true), blinkStartMs + (blinkStepMs * 2)));
    timers.push(setTimeout(() => setWebopsLampBlinkOff(false), blinkStartMs + (blinkStepMs * 3)));
    timers.push(setTimeout(() => setWebopsLampOverrides({}), blinkStartMs + (blinkStepMs * 3) + 80));

    return () => {
      timers.forEach((timerId) => clearTimeout(timerId));
    };
  }, [webopsItems]);

  useEffect(() => {
    if (isSystemsInventoryPage) {
      fetchSystemsInventory();
    }
  }, [isSystemsInventoryPage]);

  useEffect(() => {
    if (isReadinessBoardPage) {
      fetchReadinessProjects();
    }
  }, [isReadinessBoardPage]);

  useEffect(() => {
    if (!isReadinessBoardPage || typeof window === "undefined") return;
    const nextUrl = buildReadinessInternalUrl(readinessProjectCode);
    if (nextUrl && nextUrl !== window.location.href) {
      window.history.replaceState({}, "", nextUrl);
    }
  }, [isReadinessBoardPage, readinessProjectCode]);

  useEffect(() => {
    if (isReadinessBoardPage) {
      fetchReadinessBoard(readinessProjectCode);
    }
  }, [isReadinessBoardPage, readinessProjectCode]);

  useEffect(() => {
    if (isReadinessBoardPage && readinessProject?.id) {
      fetchReadinessAiContext(readinessProject.id);
    }
  }, [isReadinessBoardPage, readinessProject?.id]);

  useEffect(() => {
    if (!isSystemsInventoryPage || !systemsInventoryTableRef.current) {
      return undefined;
    }

    const tableElement = systemsInventoryTableRef.current;
    const table = new Tabulator(tableElement, {
      layout: "fitDataStretch",
      reactiveData: false,
      pagination: true,
      paginationSize: 10,
      paginationSizeSelector: [10, 25, 50, 100],
      movableColumns: true,
      placeholder: "No Systems Inventory records match the current filters.",
      data: systemsInventory,
      columns: [
        { title: "Category", field: "category", headerFilter: "input", minWidth: 150 },
        {
          title: "Component Name",
          field: "component_name",
          headerFilter: "input",
          minWidth: 240,
          formatter: (cell) => {
            const data = cell.getRow().getData();
            const name = escapeHtmlValue(cell.getValue() || "");
            const scriptId = data.netsuite_script_id ? `<div class="systems-inventory-subtext">${escapeHtmlValue(data.netsuite_script_id)}</div>` : "";
            return `<strong>${name}</strong>${scriptId}`;
          }
        },
        { title: "Component Type", field: "component_type", headerFilter: "input", minWidth: 145 },
        { title: "Platform", field: "platform", headerFilter: "input", minWidth: 110 },
        { title: "Related System", field: "related_system", headerFilter: "input", minWidth: 180 },
        { title: "Related Partner", field: "related_partner", headerFilter: "input", minWidth: 150 },
        { title: "Status", field: "status", headerFilter: "input", minWidth: 145 },
        { title: "Risk Level", field: "risk_level", headerFilter: "input", minWidth: 120 },
        { title: "Migration Action", field: "migration_action", headerFilter: "input", minWidth: 150 },
        {
          title: "Vendor Bundle",
          field: "vendor_bundle",
          minWidth: 135,
          formatter: (cell) => (cell.getValue() ? "Vendor Bundle" : "Custom")
        },
        {
          title: "Notes / Description",
          field: "notes_description",
          minWidth: 260,
          formatter: (cell) => truncateSystemsInventoryText(getSystemsInventoryNotesDescription(cell.getRow().getData()))
        },
        {
          title: "Actions",
          field: "actions",
          hozAlign: "left",
          headerSort: false,
          download: false,
          frozen: true,
          minWidth: 360,
          formatter: () => (
            `<div class="systems-inventory-actions">
              <button type="button" class="systems-inventory-action-btn" data-action="view">View</button>
              <button type="button" class="systems-inventory-action-btn" data-action="edit">Edit</button>
              <button type="button" class="systems-inventory-action-btn systems-inventory-action-btn--delete" data-action="delete">Delete</button>
              <button type="button" class="systems-inventory-action-link" data-action="view-netsuite">View NS</button>
              <button type="button" class="systems-inventory-action-link" data-action="edit-netsuite">Edit NS</button>
            </div>`
          ),
          cellClick: (event, cell) => {
            const action = event.target?.dataset?.action;
            if (!action) return;

            const item = cell.getRow().getData();
            if (action === "view") {
              setSystemsInventoryDetailsItem(item);
            } else if (action === "edit") {
              openSystemsInventoryEditModal(item);
            } else if (action === "delete") {
              deleteSystemsInventoryItem(item);
            } else if (action === "view-netsuite") {
              const url = item.view_url || buildSystemsInventoryNetSuiteUrl(item.netsuite_internal_id, false);
              if (url) window.open(url, "_blank", "noopener,noreferrer");
            } else if (action === "edit-netsuite") {
              const url = item.edit_url || buildSystemsInventoryNetSuiteUrl(item.netsuite_internal_id, true);
              if (url) window.open(url, "_blank", "noopener,noreferrer");
            }
          }
        }
      ]
    });

    systemsInventoryTabulatorRef.current = table;

    return () => {
      try {
        table.destroy();
      } catch (err) {
        console.warn("Systems Inventory table cleanup warning:", err);
      }
      systemsInventoryTabulatorRef.current = null;
    };
  }, [isSystemsInventoryPage, systemsInventory]);

  useEffect(() => {
    const table = getMountedSystemsInventoryTable();
    if (!table) return;

    const search = String(systemsInventorySearch || "").trim().toLowerCase();
    if (!search) {
      table.clearFilter();
      return;
    }

    table.setFilter((data) => (
      [
        data.component_name,
        data.component_type,
        data.platform,
        data.category,
        data.netsuite_internal_id,
        data.netsuite_script_id,
        data.netsuite_script_type,
        data.related_system,
        data.related_partner,
        data.status,
        data.risk_level,
        data.migration_action,
        getSystemsInventoryNotesDescription(data)
      ].some((value) => String(value || "").toLowerCase().includes(search))
    ));
  }, [systemsInventorySearch, systemsInventory]);

  useEffect(() => {
    if (!isReadinessBoardPage || !readinessItemsTableRef.current) {
      return undefined;
    }

    const table = new Tabulator(readinessItemsTableRef.current, {
      layout: isAnyMaltaNetSuiteProject ? "fitData" : "fitColumns",
      reactiveData: false,
      data: readinessItems,
      groupBy: isAnyMaltaNetSuiteProject ? "phase" : "lane",
      groupStartOpen: true,
      movableColumns: true,
      placeholder: "No readiness items match the current filters.",
      columns: [
        ...(isAnyMaltaNetSuiteProject ? [
          { title: "Project", formatter: () => readinessProjectCode, width: 165, frozen: true },
          { title: "Week", field: "week_number", editor: "number", width: 74, hozAlign: "right" },
          { title: "Phase", field: "phase", editor: "input", minWidth: 190 },
          { title: "Workstream", field: "workstream", editor: "input", minWidth: 230 },
          { title: "Task", field: "title", editor: "input", minWidth: 300, frozen: true },
          { title: "Detailed description", field: "description", editor: "textarea", minWidth: 420 },
          { title: "Assigned resource", field: "owner", editor: "input", minWidth: 190 },
          { title: "Supporting resources", field: "supporting_resources", editor: "textarea", minWidth: 320 },
          { title: "Labor hours", field: "estimated_hours", editor: "number", width: 110, hozAlign: "right" },
          ...(isMaltaNetSuiteNormalized ? [
            { title: "Traditional hours", field: "traditional_estimated_hours", editor: "number", width: 130, hozAlign: "right" },
            { title: "Traditional method", field: "traditional_execution_method", editor: "textarea", minWidth: 390 },
            { title: "CLI/AI method", field: "cli_ai_execution_method", editor: "textarea", minWidth: 430 },
            { title: "Prep hours", field: "cli_ai_preparation_hours", editor: "number", width: 105, hozAlign: "right" },
            { title: "Automated elapsed", field: "automated_elapsed_hours", editor: "number", width: 145, hozAlign: "right" },
            { title: "Human review", field: "human_review_hours", editor: "number", width: 120, hozAlign: "right" },
            { title: "Correction / troubleshooting", field: "human_correction_hours", editor: "number", width: 185, hozAlign: "right" },
            { title: "Human approval", field: "human_approval_hours", editor: "number", width: 130, hozAlign: "right" },
            { title: "CLI/SDF saved", field: "cli_sdf_hours_saved", editor: "number", width: 125, hozAlign: "right" },
            { title: "Jira saved", field: "jira_automation_hours_saved", editor: "number", width: 110, hozAlign: "right" },
            { title: "AI saved", field: "ai_assistance_hours_saved", editor: "number", width: 105, hozAlign: "right" },
            { title: "Malta execution", field: "malta_execution_hours", editor: "number", width: 130, hozAlign: "right" },
            { title: "Factory development", field: "factory_development_hours", editor: "number", width: 155, hozAlign: "right" },
            { title: "Confidence", field: "confidence_level", editor: "input", width: 110 },
            { title: "Estimate basis", field: "estimate_basis", editor: "textarea", minWidth: 420 },
            { title: "Scope", field: "scope_classification", editor: "input", minWidth: 210 },
            { title: "Labor classification", field: "labor_classification", editor: "textarea", minWidth: 330 }
          ] : []),
          ...(isMaltaNetSuiteSession ? [
            { title: "v2 hours", field: "v2_estimated_hours", editor: "number", width: 100, hozAlign: "right" },
            { title: "Execution-session design", field: "execution_session_design", editor: "textarea", minWidth: 440 },
            { title: "Sessions", field: "distinct_work_sessions", editor: "number", width: 90, hozAlign: "right" },
            { title: "Shared setup", field: "shared_setup_activities", editor: "textarea", minWidth: 400 },
            { title: "Base execution", field: "base_execution_hours", editor: "number", width: 120, hozAlign: "right" },
            { title: "Known exceptions", field: "known_exception_hours", editor: "number", width: 135, hozAlign: "right" },
            { title: "Risk probability", field: "risk_probability", editor: "number", width: 125, hozAlign: "right", formatter: (cell) => `${Math.round(Number(cell.getValue() || 0) * 100)}%` },
            { title: "Full risk impact", field: "risk_impact_hours", editor: "number", width: 125, hozAlign: "right" },
            { title: "Risk reserve", field: "risk_reserve_hours", editor: "number", width: 115, hozAlign: "right" },
            { title: "Most likely", field: "estimated_hours", editor: "number", width: 110, hozAlign: "right" },
            { title: "Conservative", field: "conservative_hours", editor: "number", width: 115, hozAlign: "right" },
            { title: "Automated elapsed", field: "automated_elapsed_hours", editor: "number", width: 145, hozAlign: "right" },
            { title: "Vendor/wait elapsed", field: "vendor_waiting_elapsed_hours", editor: "number", width: 155, hozAlign: "right" },
            { title: "Uncertainty class", field: "uncertainty_classification", editor: "input", minWidth: 190 },
            { title: "Primary uncertainty", field: "primary_uncertainty", editor: "textarea", minWidth: 300 },
            { title: "Reserve release trigger", field: "reserve_release_trigger", editor: "textarea", minWidth: 320 },
            { title: "Reviewer / role", field: "reviewer_role", editor: "textarea", minWidth: 250 },
            { title: "Review material", field: "review_material", editor: "textarea", minWidth: 300 },
            { title: "Decision / gate", field: "review_decision", editor: "textarea", minWidth: 280 },
            { title: "Why separate review", field: "separate_review_reason", editor: "textarea", minWidth: 350 },
            { title: "Review duration", field: "review_duration_hours", editor: "number", width: 130, hozAlign: "right" },
            { title: "v2 prep", field: "v2_preparation_hours", editor: "number", width: 90, hozAlign: "right" },
            { title: "v2 review", field: "v2_review_hours", editor: "number", width: 95, hozAlign: "right" },
            { title: "v2 troubleshooting", field: "v2_troubleshooting_hours", editor: "number", width: 145, hozAlign: "right" },
            { title: "v2 approval", field: "v2_approval_hours", editor: "number", width: 100, hozAlign: "right" },
            { title: "Factory category", field: "factory_category", editor: "input", minWidth: 260 },
            { title: "Malta-critical factory", field: "malta_critical_factory_hours", editor: "number", width: 155, hozAlign: "right" },
            { title: "Deferred factory", field: "deferred_factory_hours", editor: "number", width: 130, hozAlign: "right" },
            { title: "Confidence", field: "confidence_level", editor: "input", width: 110 },
            { title: "Baseline task IDs", field: "baseline_task_ids", minWidth: 210, formatter: (cell) => Array.isArray(cell.getValue()) ? cell.getValue().join(", ") : String(cell.getValue() || "") }
          ] : []),
          { title: "Start", field: "planned_start_date", editor: "date", width: 118, formatter: (cell) => formatReadinessDate(cell.getValue()) },
          { title: "Complete", field: "planned_completion_date", editor: "date", width: 118, formatter: (cell) => formatReadinessDate(cell.getValue()) },
          { title: "Duration (days)", field: "duration_days", editor: "number", width: 125, hozAlign: "right" },
          { title: "Dependencies", field: "dependencies", editor: "textarea", minWidth: 360 },
          { title: "Can run concurrently", field: "concurrent_tasks", editor: "textarea", minWidth: 320 },
          { title: "Critical path", field: "critical_path", editor: "tickCross", formatter: "tickCross", width: 110, hozAlign: "center" },
          { title: "Deliverable", field: "deliverable", editor: "textarea", minWidth: 320 },
          { title: "Acceptance criteria", field: "acceptance_criteria", editor: "textarea", minWidth: 420 },
          {
            title: "Status", field: "status", editor: "list", editorParams: { values: READINESS_STATUS_OPTIONS }, minWidth: 135,
            formatter: (cell) => `<span class="${buildBadgeClass("readiness-badge", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
          },
          {
            title: "Priority", field: "priority", editor: "list", editorParams: { values: READINESS_PRIORITY_OPTIONS }, minWidth: 120,
            formatter: (cell) => `<span class="${buildBadgeClass("readiness-priority", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
          },
          { title: "Risk", field: "risk_level", editor: "input", width: 100 },
          { title: "Assumptions", field: "assumptions", editor: "textarea", minWidth: 340 },
          { title: "Open decisions", field: "open_decisions", editor: "textarea", minWidth: 340 },
          { title: "Notes", field: "notes", editor: "textarea", minWidth: 320 },
          { title: "Reuse", field: "reuse_scope", editor: "input", minWidth: 260 },
          { title: "AI/SDF hours saved", field: "hours_saved_by_ai_sdf", editor: "number", width: 145, hozAlign: "right" },
          { title: "Vendor hours", field: "outside_vendor_hours", editor: "number", width: 115, hozAlign: "right" },
          { title: "Business-user hours", field: "business_user_hours", editor: "number", width: 155, hozAlign: "right" },
          { title: "Sort", field: "sort_order", editor: "number", width: 80, hozAlign: "right" }
        ] : [
        {
          title: "Lane",
          field: "lane",
          editor: "list",
          editorParams: { values: READINESS_LANE_OPTIONS },
          width: 120
        },
        { title: "Category", field: "category", editor: "input", minWidth: 135 },
        { title: "Title", field: "title", editor: "input", minWidth: 260 },
        {
          title: "Status",
          field: "status",
          editor: "list",
          editorParams: { values: READINESS_STATUS_OPTIONS },
          minWidth: 135,
          formatter: (cell) => `<span class="${buildBadgeClass("readiness-badge", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
        },
        {
          title: "Priority",
          field: "priority",
          editor: "list",
          editorParams: { values: READINESS_PRIORITY_OPTIONS },
          minWidth: 120,
          formatter: (cell) => `<span class="${buildBadgeClass("readiness-priority", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
        },
        { title: "Owner", field: "owner", editor: "input", minWidth: 130 },
        { title: "Due", field: "due_date", editor: "date", width: 118, formatter: (cell) => formatReadinessDate(cell.getValue()) },
        { title: "Notes", field: "notes", editor: "textarea", minWidth: 320 },
        { title: "Sort", field: "sort_order", editor: "number", width: 80, hozAlign: "right" }
        ])
      ],
      cellEdited: saveReadinessItemCell
    });

    readinessItemsTabulatorRef.current = table;
    return () => {
      try {
        table.destroy();
      } catch (err) {
        console.warn("Readiness items table cleanup warning:", err);
      }
      readinessItemsTabulatorRef.current = null;
    };
  }, [isReadinessBoardPage, readinessItems, isMaltaNetSuiteMigration, isMaltaNetSuiteNormalized, isMaltaNetSuiteSession, isAnyMaltaNetSuiteProject, readinessProjectCode]);

  useEffect(() => {
    if (!isReadinessBoardPage || !readinessArtifactsTableRef.current) {
      return undefined;
    }

    const table = new Tabulator(readinessArtifactsTableRef.current, {
      layout: "fitColumns",
      reactiveData: false,
      data: readinessArtifacts,
      movableColumns: true,
      placeholder: "No artifacts captured yet.",
      columns: [
        { title: "Type", field: "artifact_type", editor: "input", minWidth: 120 },
        { title: "Title", field: "title", editor: "input", minWidth: 220 },
        { title: "File Name", field: "file_name", editor: "input", minWidth: 330 },
        { title: "Reference", field: "reference_number", editor: "input", minWidth: 130 },
        {
          title: "Status",
          field: "status",
          editor: "input",
          minWidth: 120,
          formatter: (cell) => `<span class="${buildBadgeClass("readiness-badge", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
        },
        { title: "Notes", field: "notes", editor: "textarea", minWidth: 300 },
        { title: "Created", field: "created_at", minWidth: 155, formatter: (cell) => String(cell.getValue() || "").slice(0, 19).replace("T", " ") }
      ],
      cellEdited: saveReadinessArtifactCell
    });

    readinessArtifactsTabulatorRef.current = table;
    return () => {
      try {
        table.destroy();
      } catch (err) {
        console.warn("Readiness artifacts table cleanup warning:", err);
      }
      readinessArtifactsTabulatorRef.current = null;
    };
  }, [isReadinessBoardPage, readinessArtifacts]);

  useEffect(() => {
    if (!isReadinessBoardPage || !readinessComplianceAuditsTableRef.current) {
      return undefined;
    }

    const table = new Tabulator(readinessComplianceAuditsTableRef.current, {
      layout: "fitDataStretch",
      responsiveLayout: "hide",
      reactiveData: false,
      data: readinessComplianceAudits,
      movableColumns: true,
      placeholder: "No Prop 65 review findings match the current filters.",
      columns: [
        { title: "Category", field: "category", editor: "input", minWidth: 120 },
        { title: "Section", field: "section", editor: "input", minWidth: 180 },
        { title: "Product / Area", field: "product_area", editor: "input", minWidth: 220 },
        { title: "SKU", field: "sku", editor: "input", minWidth: 120 },
        { title: "Product Category", field: "product_category", editor: "input", minWidth: 180 },
        { title: "Current Warning Status", field: "current_warning_status", editor: "input", minWidth: 180 },
        { title: "Supplier Data", field: "supplier_manufacturer_warning_data", editor: "input", minWidth: 180 },
        { title: "Packaging / Manual", field: "packaging_manual_warning_status", editor: "input", minWidth: 180 },
        { title: "Shopify Page", field: "shopify_product_page_warning_status", editor: "input", minWidth: 180 },
        { title: "Cart / Checkout", field: "cart_checkout_visibility", editor: "input", minWidth: 160 },
        { title: "Wording Status", field: "warning_wording_status", editor: "input", minWidth: 160 },
        { title: "Legal Review", field: "legal_review_status", editor: "input", minWidth: 150 },
        {
          title: "Status",
          field: "status",
          editor: "list",
          editorParams: { values: READINESS_COMPLIANCE_STATUS_OPTIONS },
          minWidth: 170,
          formatter: (cell) => `<span class="${buildBadgeClass("readiness-badge", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
        },
        {
          title: "Risk",
          field: "risk_level",
          editor: "list",
          editorParams: { values: READINESS_COMPLIANCE_RISK_OPTIONS },
          width: 120,
          formatter: (cell) => `<span class="${buildBadgeClass("readiness-priority", cell.getValue())}">${escapeHtmlValue(cell.getValue() || "")}</span>`
        },
        { title: "Owner", field: "owner", editor: "input", minWidth: 130 },
        { title: "Due", field: "due_date", editor: "date", width: 118, formatter: (cell) => formatReadinessDate(cell.getValue()) },
        { title: "Next Action", field: "next_action", editor: "textarea", minWidth: 260 },
        { title: "Remediation", field: "remediation_needed", editor: "textarea", minWidth: 260 },
        { title: "Notes / Evidence", field: "notes_evidence", editor: "textarea", minWidth: 300 },
        { title: "Sort", field: "sort_order", editor: "number", width: 80, hozAlign: "right" }
      ],
      cellEdited: saveReadinessComplianceAuditCell
    });

    readinessComplianceAuditsTabulatorRef.current = table;
    return () => {
      try {
        table.destroy();
      } catch (err) {
        console.warn("Readiness compliance audits table cleanup warning:", err);
      }
      readinessComplianceAuditsTabulatorRef.current = null;
    };
  }, [isReadinessBoardPage, readinessComplianceAudits]);

  useEffect(() => {
    const table = readinessItemsTabulatorRef.current;
    if (!isReadinessBoardPage || !table) return;

    const activeFilters = Object.entries(readinessFilters)
      .filter(([, value]) => String(value || "").trim())
      .map(([field, value]) => ({ field, type: "=", value }));
    table.setFilter(activeFilters);
  }, [isReadinessBoardPage, readinessFilters, readinessItems]);

  useEffect(() => {
    const table = readinessComplianceAuditsTabulatorRef.current;
    if (!isReadinessBoardPage || !table) return;

    const activeFilters = [];
    if (String(readinessComplianceFilters.category || "").trim()) {
      activeFilters.push({ field: "category", type: "=", value: readinessComplianceFilters.category });
    }
    if (readinessComplianceFilters.prop65Only) {
      activeFilters.push({ field: "section", type: "like", value: "Prop 65" });
    }
    if (readinessComplianceFilters.needsLegalReview) {
      activeFilters.push((data) => String(data.legal_review_status || "").toLowerCase().includes("need"));
    }
    if (readinessComplianceFilters.supplierInfoNeeded) {
      activeFilters.push((data) => (
        String(data.status || "").toLowerCase().includes("supplier info needed")
        || String(data.supplier_manufacturer_warning_data || "").toLowerCase().includes("need")
      ));
    }
    if (readinessComplianceFilters.productPageUpdateNeeded) {
      activeFilters.push((data) => String(data.status || "").toLowerCase().includes("product page update needed"));
    }
    if (readinessComplianceFilters.highRisk) {
      activeFilters.push((data) => ["high", "critical"].includes(String(data.risk_level || "").toLowerCase()));
    }

    table.setFilter(activeFilters);
  }, [isReadinessBoardPage, readinessComplianceFilters, readinessComplianceAudits]);

  useEffect(() => {
    if (!dashboardChartSource) {
      return;
    }

    const { chartWithScenarios, scenarioKeys } = buildChartWithForecasts(dashboardChartSource);
    setWebScenarioKeys(scenarioKeys);
    setChartData(chartWithScenarios);
  }, [dashboardChartSource, showSimulation, simulationDraft]);

  useEffect(() => {
    if (!isSelectedMonthActive) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      refreshTodaySales();
      fetchScopedSalesTotals();
    }, TODAY_SALES_REFRESH_INTERVAL_SECONDS * 1000);

    return () => clearInterval(intervalId);
  }, [isSelectedMonthActive, selectedMonth, salesStoreScope]);

  useEffect(() => {
    const countdownId = setInterval(() => {
      setRefreshCountdown(prev =>
        prev > 0 ? prev - 1 : 0
      );
    }, 1000);

    return () => clearInterval(countdownId);
  }, []);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await api.get("/api/projects");
        setMajorProjects(hydrateProjects(res.data.projects));
        setProjectsDirty(false);
      } catch (err) {
        console.error(err);
      }
    }
    fetchProjects();
  }, []);

  useEffect(() => {
    if (saveStatus !== "saving") {
      setDotCount(0);
      return; // no interval needed
    }

    const intervalId = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4); // cycles 0 → 3 dots
    }, 500);

    return () => clearInterval(intervalId); // cleanup
  }, [saveStatus]);

  const formatCurrency = (num) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num || 0);

  const formatSimulationAmount = (value) => {
    const rawValue = String(value ?? "");
    if (!rawValue) {
      return "";
    }

    const sanitized = rawValue.replace(/[^0-9.]/g, "");
    if (!sanitized) {
      return "";
    }

    const [wholePartRaw, decimalPartRaw] = sanitized.split(".");
    const wholePart = wholePartRaw ? Number(wholePartRaw).toLocaleString("en-US") : "0";

    if (sanitized.endsWith(".") && decimalPartRaw === undefined) {
      return `${wholePart}.`;
    }

    if (decimalPartRaw !== undefined) {
      return `${wholePart}.${decimalPartRaw.slice(0, 2)}`;
    }

    return wholePart;
  };

  const refreshLabel = "Refresh Dashboard";

  const updateAiCopyFeedback = (tone, message) => {
    setAiCopyFeedback({ tone, message });
    if (aiCopyFeedbackTimeoutRef.current) {
      clearTimeout(aiCopyFeedbackTimeoutRef.current);
    }
    aiCopyFeedbackTimeoutRef.current = setTimeout(() => {
      setAiCopyFeedback(null);
      aiCopyFeedbackTimeoutRef.current = null;
    }, 3500);
  };

  const writeClipboardText = async (value) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const handleCopyAiContext = async () => {
    setIsCopyingAiContext(true);
    try {
      const response = await api.get("/api/dashboard-ai-context", {
        params: {
          month: selectedMonth,
          cart_abandonment_mode: cartAbandonmentMode,
          click_through_mode: clickThroughMode
        }
      });

      const payload = response?.data && typeof response.data === "object" ? response.data : {};
      const text = JSON.stringify(payload, null, 2);
      await writeClipboardText(text);
      const kb = (text.length / 1024).toFixed(1);
      updateAiCopyFeedback("success", `Copied AI JSON (${kb} KB)`);
    } catch (err) {
      console.error("Failed to copy AI context:", err);
      updateAiCopyFeedback("error", "Copy failed. Try again.");
    } finally {
      setIsCopyingAiContext(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchDashboardData(),
        fetchScopedSalesTotals(),
        fetchTrafficData(),
        fetchWebopsStatuses()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSelectedMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
    setSalesSummary("");
    setSalesSummaryBullets([]);
    setDashboardChartSource(null);
    setChartData([]);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("month", newMonth);
    window.history.replaceState({}, "", `${nextUrl.pathname}${nextUrl.search}`);
  };

  const handleSalesStoreScopeChange = (nextScope) => {
    setSalesStoreScope(nextScope);
    try {
      window.localStorage.setItem("shopify-sales-store-scope", nextScope);
    } catch (_err) {
      // The selector still works when browser storage is unavailable.
    }
  };

  if (showPerfDashboard) {
    return (
      <AppProvider>
        <PerformanceDebugDashboard api={api} environment={environment} />
      </AppProvider>
    );
  }

  const handleReevaluate = async () => {
    setIsReevaluating(true);
    try {
      forceNextOrderMixSummaryRefreshRef.current = true;
      await fetchDashboardSummary(chartData, { force: true });
      setOrderMixSummaryManualRefreshTick((current) => current + 1);
    } finally {
      setIsReevaluating(false);
    }
  };

  const openGlossaryModal = () => {
    const entries = Object.entries(mergeGlossaryTermsWithDefaults(glossaryTermsMap))
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([term, definition]) => ({ id: uuidv4(), term, definition }));
    setGlossaryDraft(entries);
    setGlossaryModalOpen(true);
  };

  const handleGlossaryDraftChange = (index, field, value) => {
    setGlossaryDraft((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, [field]: value } : entry
    )));
  };

  const addGlossaryDraftRow = () => {
    setGlossaryDraft((current) => [...current, { id: uuidv4(), term: "", definition: "" }]);
  };

  const removeGlossaryDraftRow = (index) => {
    setGlossaryDraft((current) => current.filter((_, entryIndex) => entryIndex !== index));
  };

  const saveGlossary = async () => {
    setGlossarySaving(true);
    try {
      const terms = glossaryDraft.reduce((acc, entry) => {
        const term = String(entry.term || "").trim().toLowerCase();
        const definition = String(entry.definition || "").trim();
        if (term && definition) {
          acc[term] = definition;
        }
        return acc;
      }, {});

      const response = await api.post("/api/glossary", { terms });
      const savedTerms = response.data?.terms || terms;
      setGlossaryTermsMap(mergeGlossaryTermsWithDefaults(savedTerms));
      setGlossaryModalOpen(false);
    } catch (err) {
      console.error("Error saving glossary terms:", err);
    } finally {
      setGlossarySaving(false);
    }
  };

  const openSimulationModal = () => {
    setSimulationModalOpen(true);
  };

  const closeSimulationModal = () => {
    setSimulationModalOpen(false);
  };

  const handleSimulationToggle = (newValue) => {
    setShowSimulation(newValue);

    if (newValue) {
      openSimulationModal();
      return;
    }

    setSimulationModalOpen(false);
  };

  const addSimulationDraftRow = () => {
    setSimulationDraft((current) => [...current, { id: uuidv4(), amount: "" }]);
  };

  const handleSimulationDraftChange = (index, value) => {
    const sanitizedValue = String(value || "").replace(/[^0-9.]/g, "");

    setSimulationDraft((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, amount: sanitizedValue } : entry
    )));
  };

  const removeSimulationDraftRow = (index) => {
    setSimulationDraft((current) => {
      const nextRows = current.filter((_, entryIndex) => entryIndex !== index);
      return nextRows.length ? nextRows : [{ id: uuidv4(), amount: "" }];
    });
  };

  const tone = percentToPlan >= 100 ? "success" : percentToPlan >= 80 ? "caution" : "critical";
  const mtdTone = percentToPlanWithPOS >= 100 ? "success" : percentToPlanWithPOS >= 80 ? "caution" : "critical";
  const scopedSalesTotals = scopedSalesSnapshot?.totals || null;
  const displayedSalesYesterday = Number(scopedSalesTotals?.priorDay ?? salesYesterday ?? 0);
  const displayedSalesMTD = Number(scopedSalesTotals?.monthToDate ?? liveSalesMTDWithPOS ?? 0);
  const displayedTodaySales = Number(scopedSalesTotals?.selectedDay ?? todaysSales ?? 0);
  const displayedYearToDate = Number(scopedSalesTotals?.yearToDate ?? yearlyStats?.yearToDate ?? 0);
  const displayedProjectedYearEnd = Number(scopedSalesTotals?.projectedYearEnd ?? yearlyStats?.projectedYearEnd ?? 0);
  const displayedMtdPct = planMTD > 0 ? (displayedSalesMTD / planMTD) * 100 : 0;
  const displayedMtdTone = displayedMtdPct >= 100 ? "success" : displayedMtdPct >= 80 ? "caution" : "critical";
  const selectedDaySalesLabel = isSelectedMonthActive ? "Today's Net Sales" : "Final Day Net Sales";
  const selectedDayGoalLabel = isSelectedMonthActive ? "Today's Goal" : "Final Day Goal";
  const selectedDayPaceLabel = isSelectedMonthActive ? "Intraday Forcast" : "Final Day Pace";
  const getKey = (p) => p.id ?? p.tempId;
  const now = new Date();
  const currentMonthDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const todayISO = now.toLocaleDateString("en-CA");
  const isTodayComplete = now.getHours() >= 23 && now.getMinutes() >= 55;
  const goalRows = Array.isArray(dashboardChartSource?.dailyGoals) ? dashboardChartSource.dailyGoals : [];
  const sortedGoalRows = [...goalRows].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const daysInMonthForForecast = sortedGoalRows.length || currentMonthDays;
  const completedDayRows = sortedGoalRows.filter((row) => (
    String(row?.date) < todayISO || (String(row?.date) === todayISO && isTodayComplete)
  ));
  const completedDaySales = completedDayRows.map((row) => Number(row?.channels?.["Online Store"]?.net || 0));
  const recentCompletedDaySales = completedDaySales.length >= 7
    ? completedDaySales.slice(-7)
    : completedDaySales;
  const completedDaysUsed = recentCompletedDaySales.length;
  const recentCompletedDaySalesTotal = recentCompletedDaySales.reduce((sum, value) => sum + value, 0);
  const recentCompletedDayRunRate = recentCompletedDaySales.length
    ? (recentCompletedDaySalesTotal / recentCompletedDaySales.length)
    : 0;
  const completedDaysCount = completedDayRows.length;
  const remainingDays = Math.max(daysInMonthForForecast - completedDaysCount, 0);
  const forecastRemainingCoreSales = recentCompletedDayRunRate * remainingDays;
  const simpleForecast = planMTD > 0
    ? monthlyGoal * (salesMTD / planMTD)
    : salesMTD;
  const expectedOutperformContribution = Math.max(0, Number(dashboardChartSource?.forecastModel?.outperformForecast || 0));
  const expectedBreakoutContribution = Math.max(0, Number(dashboardChartSource?.forecastModel?.breakoutForecast || 0));
  const expectedExtremeContribution = Math.max(0, Number(dashboardChartSource?.forecastModel?.extremeForecast || 0));
  const remainingExpectedOutlierCount = (
    Number(dashboardChartSource?.forecastModel?.remainingExpectedDays?.outperform || 0)
    + Number(dashboardChartSource?.forecastModel?.remainingExpectedDays?.breakout || 0)
    + Number(dashboardChartSource?.forecastModel?.remainingExpectedDays?.extreme || 0)
  );
  const expectedRemainingOutlierUpside = expectedOutperformContribution + expectedBreakoutContribution + expectedExtremeContribution;
  const intelligentForecast = salesMTD + forecastRemainingCoreSales + expectedRemainingOutlierUpside;
  const intelligentForecastValue = Math.max(salesMTD, intelligentForecast);
  const orderMixInsights = dashboardChartSource?.orderMixInsights || null;
  const juneDetailsIntelligence = dashboardChartSource?.juneDetailsIntelligence || null;
  const orderMixAov = orderMixInsights?.aov || {};
  const orderMixConcentration = orderMixInsights?.concentration || {};
  const orderMixDecomposition = orderMixInsights?.forecastDecomposition || {};
  const orderMixConfidence = orderMixInsights?.confidence || {};
  const orderMixThresholding = orderMixInsights?.thresholding || {};
  const adjustedAovValue = Number(orderMixAov.adjustedAov || 0);
  const largeOrderImpactPct = Math.max(0, Number(orderMixAov.distortionPct || 0));
  const top5DependencePct = Math.max(0, Number(orderMixConcentration.top5Pct || 0));
  const coreForecastStrengthPct = monthlyGoal > 0
    ? (Math.max(0, Number(orderMixDecomposition.coreForecast || 0)) / monthlyGoal) * 100
    : 0;
  const bigDealLiftPct = monthlyGoal > 0
    ? (Math.max(0, Number(orderMixDecomposition.expectedLargeOrderContribution || 0)) / monthlyGoal) * 100
    : 0;
  const forecastStabilityScore = Math.max(0, Number(orderMixConfidence.score || 0));
  const monthDetailsKpiGauges = useMemo(() => ([
    {
      key: "typical-order-size",
      glossaryKey: "typical_order_size",
      label: "Typical Order Size",
      min: 0,
      max: 300,
      value: adjustedAovValue,
      formattedValue: formatKpiGaugeValue({ value: adjustedAovValue, kind: "currency", currencyFractionDigits: 0 }),
      zoneRanges: [
        { max: 100, zone: "red" },
        { max: 180, zone: "yellow" },
        { max: 300, zone: "green" }
      ],
      bands: [
        { limit: 100, color: "#c62828" },
        { limit: 180, color: "#d4a017" },
        { limit: 300, color: "#2e7d32" }
      ]
    },
    {
      key: "large-order-impact",
      glossaryKey: "large_order_impact",
      label: "Large Order Impact",
      min: 0,
      max: 300,
      value: largeOrderImpactPct,
      formattedValue: formatKpiGaugeValue({ value: largeOrderImpactPct, kind: "percent" }),
      zoneRanges: [
        { max: 40, zone: "green" },
        { max: 100, zone: "yellow" },
        { max: 300, zone: "red" }
      ],
      bands: [
        { limit: 40, color: "#2e7d32" },
        { limit: 100, color: "#d4a017" },
        { limit: 300, color: "#c62828" }
      ]
    },
    {
      key: "revenue-dependence",
      glossaryKey: "revenue_dependence",
      label: "Revenue Dependence",
      min: 0,
      max: 100,
      value: top5DependencePct,
      formattedValue: formatKpiGaugeValue({ value: top5DependencePct, kind: "percent" }),
      zoneRanges: [
        { max: 25, zone: "green" },
        { max: 45, zone: "yellow" },
        { max: 100, zone: "red" }
      ],
      bands: [
        { limit: 25, color: "#2e7d32" },
        { limit: 45, color: "#d4a017" },
        { limit: 100, color: "#c62828" }
      ]
    },
    {
      key: "core-forecast",
      glossaryKey: "core_forecast",
      label: "Core Forecast",
      min: 0,
      max: 100,
      value: coreForecastStrengthPct,
      formattedValue: formatKpiGaugeValue({ value: coreForecastStrengthPct, kind: "percent" }),
      zoneRanges: [
        { max: 60, zone: "red" },
        { max: 85, zone: "yellow" },
        { max: 100, zone: "green" }
      ],
      bands: [
        { limit: 60, color: "#c62828" },
        { limit: 85, color: "#d4a017" },
        { limit: 100, color: "#2e7d32" }
      ]
    },
    {
      key: "big-deal-lift",
      glossaryKey: "big_deal_lift",
      label: "Big Deal Lift",
      min: 0,
      max: 25,
      value: bigDealLiftPct,
      formattedValue: formatKpiGaugeValue({ value: bigDealLiftPct, kind: "percent" }),
      zoneRanges: [
        { max: 5, zone: "green" },
        { max: 12, zone: "yellow" },
        { max: 25, zone: "red" }
      ],
      bands: [
        { limit: 5, color: "#2e7d32" },
        { limit: 12, color: "#d4a017" },
        { limit: 25, color: "#c62828" }
      ]
    },
    {
      key: "forecast-stability",
      glossaryKey: "forecast_stability",
      label: "Forecast Stability",
      min: 0,
      max: 100,
      value: forecastStabilityScore,
      formattedValue: formatKpiGaugeValue({ value: forecastStabilityScore, kind: "number" }),
      zoneRanges: [
        { max: 45, zone: "red" },
        { max: 70, zone: "yellow" },
        { max: 100, zone: "green" }
      ],
      bands: [
        { limit: 45, color: "#c62828" },
        { limit: 70, color: "#d4a017" },
        { limit: 100, color: "#2e7d32" }
      ]
    }
  ]), [
    adjustedAovValue,
    largeOrderImpactPct,
    top5DependencePct,
    coreForecastStrengthPct,
    bigDealLiftPct,
    forecastStabilityScore
  ]);
  const juneDetailsGaugeMetaById = useMemo(() => ({
    "typical-order-size": { glossaryKey: "typical_order_size" },
    "large-order-impact": { glossaryKey: "large_order_impact" },
    "revenue-dependence": { glossaryKey: "revenue_dependence" },
    "core-forecast": { glossaryKey: "core_forecast" },
    "big-deal-lift": { glossaryKey: "big_deal_lift" },
    "forecast-stability": { glossaryKey: "forecast_stability" }
  }), []);
  const monthDetailsKpiGaugesWithTooltip = useMemo(() => {
    return monthDetailsKpiGauges.map((kpi) => {
      const zone = resolveGaugeZone(kpi.value, kpi.zoneRanges);
      const glossaryDefinition = glossaryTermsMap?.[kpi.glossaryKey] || "";

      return {
        ...kpi,
        zone,
        tooltipContent: buildGaugeGlossaryTooltip({
          glossaryKey: kpi.glossaryKey,
          glossaryDefinition,
          zone
        })
      };
    });
  }, [monthDetailsKpiGauges, glossaryTermsMap]);
  const aiContractJuneDetailsGauges = useMemo(() => {
    const source = Array.isArray(juneDetailsIntelligence?.gauges) ? juneDetailsIntelligence.gauges : [];
    if (!source.length) return [];
    return source.map((gauge) => {
      const id = String(gauge?.id || "");
      const fallbackGauge = monthDetailsKpiGauges.find((kpi) => kpi.key === id);
      if (!fallbackGauge) return null;
      const value = Number(gauge?.value);
      const zone = String(gauge?.zone || resolveGaugeZone(value, fallbackGauge.zoneRanges));
      const meta = juneDetailsGaugeMetaById[id] || {};
      const glossaryDefinitionBase = glossaryTermsMap?.[meta.glossaryKey] || "";
      const glossaryDefinition = gauge?.caveat
        ? `${glossaryDefinitionBase}<p><strong>Caveat:</strong> ${String(gauge.caveat)}</p>`
        : glossaryDefinitionBase;
      const tooltipContent = buildGaugeGlossaryTooltip({
        glossaryKey: meta.glossaryKey,
        glossaryDefinition,
        zone
      });
      return {
        ...fallbackGauge,
        zone,
        value: Number.isFinite(value) ? value : 0,
        formattedValue: String(gauge?.displayValue || fallbackGauge.formattedValue || "N/A"),
        tooltipContent
      };
    }).filter(Boolean);
  }, [juneDetailsIntelligence, monthDetailsKpiGauges, juneDetailsGaugeMetaById, glossaryTermsMap]);
  const monthDetailsRenderSource = aiContractJuneDetailsGauges.length ? "ai_contract" : "client_fallback";
  const renderedMonthDetailsGauges = aiContractJuneDetailsGauges.length ? aiContractJuneDetailsGauges : monthDetailsKpiGaugesWithTooltip;
  const systemsInventoryVisibleRows = useMemo(() => {
    const search = String(systemsInventorySearch || "").trim().toLowerCase();
    if (!search) return systemsInventory;
    return systemsInventory.filter((item) => (
      [
        item.component_name,
        item.component_type,
        item.platform,
        item.category,
        item.netsuite_internal_id,
        item.netsuite_script_id,
        item.netsuite_script_type,
        item.related_system,
        item.related_partner,
        item.status,
        item.risk_level,
        item.migration_action,
        getSystemsInventoryNotesDescription(item)
      ].some((value) => String(value || "").toLowerCase().includes(search))
    ));
  }, [systemsInventory, systemsInventorySearch]);
  const systemsInventorySelectOptions = (values = [], allLabel = "All") => [
    { label: allLabel, value: "" },
    ...values.map((value) => ({ label: String(value), value: String(value) }))
  ];
  const readinessKnownProjectCodes = new Set(READINESS_PROJECT_GROUPS.flatMap((group) => group.options.map((option) => option.value)));
  const readinessAdditionalProjectOptions = readinessProjects
    .filter((project) => project.project_code && !readinessKnownProjectCodes.has(project.project_code))
    .map((project) => ({
      label: project.project_name || project.project_code,
      value: project.project_code
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const readinessProjectGroups = readinessAdditionalProjectOptions.length
    ? [
      ...READINESS_PROJECT_GROUPS,
      { label: "ADDITIONAL PROJECTS", options: readinessAdditionalProjectOptions }
    ]
    : READINESS_PROJECT_GROUPS;
  const readinessOwners = Array.from(new Set(readinessItems.map((item) => item.owner).filter(Boolean))).sort();
  const readinessCategories = Array.from(new Set(readinessItems.map((item) => item.category).filter(Boolean))).sort();
  const readinessComplianceCategories = Array.from(new Set(readinessComplianceAudits.map((audit) => audit.category).filter(Boolean))).sort();
  const readinessOpenItems = readinessItems.filter((item) => item.status !== "Done").length;
  const readinessDoneItems = readinessItems.filter((item) => item.status === "Done").length;
  const latestReadinessArtifact = readinessArtifacts[0] || {};
  const readinessEvidenceSummary = [
    ["Latest Artifact", latestReadinessArtifact.artifact_type || "EDI 850"],
    ["Partner", readinessProjectCode === MD_EDI_PROJECT_CODE ? "Northern Tool" : "-"],
    ["PO", String(latestReadinessArtifact.reference_number || "PO 222222").replace(/^PO\s+/i, "")],
    ["File", latestReadinessArtifact.file_name || "850.20260707120015.O25C3C366A8EFF14AA4718ED444BA10E1.dat"],
    ["Raw EDI", "Available"],
    ["Parsed JSON", "Available"],
    ["NetSuite SO", readinessProjectCode === MD_EDI_PROJECT_CODE ? "478945" : "-"],
    ["Status", latestReadinessArtifact.status || "Captured"]
  ];
  const readinessAiLifecycleSummary = readinessAiContext?.lifecycle?.summary || {};
  const readinessAiTopRisks = readinessAiContext?.readinessAssessment?.topRisks || [];
  const readinessAiNextActions = readinessAiContext?.readinessAssessment?.nextActions || [];
  const readinessAiKnownExclusions = readinessAiContext?.knownExclusions || [];
  const readinessAiRecommendedQuestions = readinessAiContext?.recommendedQuestions || [];
  const readinessAiContractHealth = readinessAiContext?.contractHealth || {};
  const readinessAiBrief = readinessAiContext?.aiBrief || {};
  const readinessAiBlockedItems = readinessAiContext?.checklist?.blockedItemDetails || [];
  const readinessAiCriticalOpenItems = readinessAiContext?.checklist?.criticalOpenItems || [];
  const readinessAiHighOpenItems = readinessAiContext?.checklist?.highOpenItems || [];
  const readinessAiAssumptions = readinessAiContext?.assumptions || [];
  const readinessAiHealthLabel = readinessAiContractHealth.state || (readinessAiContractHealth.ok ? "healthy" : "missing");
  const readinessSprintFocus = readinessAiContext?.sprintFocus || {};
  const readinessSprintPriorities = readinessSprintFocus.priorities || [];
  const readinessComplianceHighRiskCount = readinessComplianceAudits.filter((audit) => ["High", "Critical"].includes(String(audit.risk_level || ""))).length;
  const readinessComplianceLegalReviewCount = readinessComplianceAudits.filter((audit) => String(audit.status || "").includes("Legal Review")).length;
  const orderMixSummaryPayload = useMemo(() => {
    const selectedMonthParts = String(selectedMonth || "").split("-");
    const selectedYear = Number(selectedMonthParts[0] || 0);
    const selectedMonthNum = Number(selectedMonthParts[1] || 0);
    const periodLastCompletedDateRaw = String(dashboardChartSource?.periodLastCompletedDate || "");
    const periodLastCompletedDate = periodLastCompletedDateRaw ? new Date(`${periodLastCompletedDateRaw}T00:00:00`) : null;
    const completedDayInSelectedMonth = (
      periodLastCompletedDate
      && !Number.isNaN(periodLastCompletedDate.getTime())
      && periodLastCompletedDate.getFullYear() === selectedYear
      && (periodLastCompletedDate.getMonth() + 1) === selectedMonthNum
    ) ? periodLastCompletedDate.getDate() : 0;
    const completedDaysUsed = Math.max(Number(completedDayInSelectedMonth || 0), 0);
    const liveMtdNetSales = Number(salesMTD || 0) + Math.max(0, Number(todaysSales || 0));
    const hasLiveCurrentMonthActivity = liveMtdNetSales > 0;
    const firstDayLiveActivity = completedDaysUsed === 0 && hasLiveCurrentMonthActivity;

    const zoneColorMap = {
      green: "#2e7d32",
      yellow: "#d4a017",
      red: "#c62828"
    };

    return {
      adjustedAov: adjustedAovValue,
      rawAov: Number(orderMixAov.rawAov || 0),
      aovDistortionPct: largeOrderImpactPct,
      top5RevenueConcentrationPct: top5DependencePct,
      coreForecast: Number(orderMixDecomposition.coreForecast || 0),
      expectedLargeOrderContribution: Number(orderMixDecomposition.expectedLargeOrderContribution || 0),
      decomposedForecast: Number(orderMixDecomposition.decomposedForecast || 0),
      confidenceScore: forecastStabilityScore,
      firstDayLiveActivity,
      hasLiveCurrentMonthActivity,
      completedDaysUsed,
      liveMtdNetSales,
      liveOrderCount: Number(dashboardChartSource?.sales?.liveOrderCount || 0),
      gaugeStates: monthDetailsKpiGaugesWithTooltip.map((kpi) => ({
        id: kpi.glossaryKey,
        label: kpi.label,
        state: kpi.zone,
        color: zoneColorMap[kpi.zone] || "#d4a017",
        value: Number(kpi.value || 0)
      }))
    };
  }, [
    adjustedAovValue,
    orderMixAov.rawAov,
    largeOrderImpactPct,
    top5DependencePct,
    orderMixDecomposition.coreForecast,
    orderMixDecomposition.expectedLargeOrderContribution,
    orderMixDecomposition.decomposedForecast,
    forecastStabilityScore,
    monthDetailsKpiGaugesWithTooltip,
    selectedMonth,
    dashboardChartSource?.periodLastCompletedDate,
    dashboardChartSource?.sales?.liveOrderCount,
    salesMTD,
    todaysSales
  ]);
  const hasOrderMixGaugeSource = Boolean(dashboardChartSource?.orderMixInsights);
  const orderMixSummaryRequestKey = useMemo(() => {
    const cachePayload = { ...orderMixSummaryPayload };
    if (!cachePayload.firstDayLiveActivity) {
      delete cachePayload.liveMtdNetSales;
      delete cachePayload.liveOrderCount;
    }

    return JSON.stringify({
      selectedMonth,
      mode: orderMixSummaryMode,
      kpis: cachePayload
    });
  }, [orderMixSummaryMode, orderMixSummaryPayload, selectedMonth]);

  useEffect(() => {
    if (!hasOrderMixGaugeSource) {
      setOrderMixExecutiveSummary("");
      setOrderMixExecutiveSummaryLoading(false);
      return undefined;
    }

    let isCancelled = false;
    const forceRefresh = forceNextOrderMixSummaryRefreshRef.current;
    forceNextOrderMixSummaryRefreshRef.current = false;
    const fetchOrderMixExecutiveSummary = async () => {
      setOrderMixExecutiveSummaryLoading(true);
      try {
        const response = await api.post("/api/order-mix-summary", {
          kpis: orderMixSummaryPayload,
          mode: orderMixSummaryMode,
          force: forceRefresh
        });
        if (isCancelled) {
          return;
        }

        const summary = typeof response?.data?.summary === "string"
          ? response.data.summary.trim()
          : "";
        setOrderMixExecutiveSummary(summary);
      } catch (err) {
        if (!isCancelled) {
          setOrderMixExecutiveSummary("");
        }
      } finally {
        if (!isCancelled) {
          setOrderMixExecutiveSummaryLoading(false);
        }
      }
    };

    const debounceId = setTimeout(fetchOrderMixExecutiveSummary, SUMMARY_REQUEST_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(debounceId);
    };
  }, [
    hasOrderMixGaugeSource,
    orderMixSummaryMode,
    orderMixSummaryRequestKey,
    orderMixSummaryRefreshTick,
    orderMixSummaryManualRefreshTick,
    selectedMonth
  ]);

  useEffect(() => {
    if (!hasOrderMixGaugeSource) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setOrderMixSummaryRefreshTick((current) => current + 1);
    }, SUMMARY_REGENERATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [hasOrderMixGaugeSource, selectedMonth]);

  useEffect(() => {
    if (!renderedMonthDetailsGauges.length) {
      return undefined;
    }

    const actualValuesByKey = renderedMonthDetailsGauges.reduce((acc, kpi) => {
      acc[kpi.key] = Number(kpi.value) || 0;
      return acc;
    }, {});

    if (!hasOrderMixGaugeSource) {
      setMonthDetailsGaugeDisplayValues(actualValuesByKey);
      return undefined;
    }

    // Wait until the page finishes its initial load before running the gauge intro.
    if (loading) {
      return undefined;
    }

    if (monthDetailsGaugeIntroRanRef.current) {
      setMonthDetailsGaugeDisplayValues(actualValuesByKey);
      return undefined;
    }

    const prefersReducedMotion = typeof window !== "undefined"
      && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);
    if (prefersReducedMotion) {
      monthDetailsGaugeIntroRanRef.current = true;
      setMonthDetailsGaugeDisplayValues(actualValuesByKey);
      return undefined;
    }

    const zeroValuesByKey = renderedMonthDetailsGauges.reduce((acc, kpi) => {
      acc[kpi.key] = 0;
      return acc;
    }, {});
    setMonthDetailsGaugeDisplayValues(zeroValuesByKey);

    const sweepStaggerMs = 100;
    const settleStaggerMs = 100;
    const sweepDurationMs = 650;
    const settleDurationMs = 700;
    const settlePauseMs = 250;
    const sweepGlobalEnd = ((renderedMonthDetailsGauges.length - 1) * sweepStaggerMs) + sweepDurationMs;
    const settleBaseStart = sweepGlobalEnd + settlePauseMs;
    const settleGlobalEnd = settleBaseStart + ((renderedMonthDetailsGauges.length - 1) * settleStaggerMs) + settleDurationMs;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const easeInOutCubic = (t) => (
      t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2
    );

    let isCancelled = false;
    const startMs = typeof performance !== "undefined" ? performance.now() : Date.now();

    const runFrame = () => {
      if (isCancelled) {
        return;
      }

      const nowMs = typeof performance !== "undefined" ? performance.now() : Date.now();
      const elapsedMs = nowMs - startMs;
      const nextValues = {};

      renderedMonthDetailsGauges.forEach((kpi, index) => {
        const actual = Number(kpi.value) || 0;
        const sweepStart = index * sweepStaggerMs;
        const sweepEnd = sweepStart + sweepDurationMs;
        const settleStart = settleBaseStart + (index * settleStaggerMs);
        const settleEnd = settleStart + settleDurationMs;

        if (elapsedMs <= sweepStart) {
          nextValues[kpi.key] = 0;
          return;
        }

        if (elapsedMs < sweepEnd) {
          const t = Math.min(1, Math.max(0, (elapsedMs - sweepStart) / sweepDurationMs));
          nextValues[kpi.key] = kpi.max * easeOutCubic(t);
          return;
        }

        if (elapsedMs < settleStart) {
          nextValues[kpi.key] = kpi.max;
          return;
        }

        if (elapsedMs < settleEnd) {
          const t = Math.min(1, Math.max(0, (elapsedMs - settleStart) / settleDurationMs));
          nextValues[kpi.key] = kpi.max + ((actual - kpi.max) * easeInOutCubic(t));
          return;
        }

        nextValues[kpi.key] = actual;
      });

      setMonthDetailsGaugeDisplayValues(nextValues);

      if (elapsedMs < settleGlobalEnd) {
        monthDetailsGaugeAnimationFrameRef.current = requestAnimationFrame(runFrame);
        return;
      }

      monthDetailsGaugeIntroRanRef.current = true;
      setMonthDetailsGaugeDisplayValues(actualValuesByKey);
      monthDetailsGaugeAnimationFrameRef.current = null;
    };

    monthDetailsGaugeAnimationFrameRef.current = requestAnimationFrame(runFrame);

    return () => {
      isCancelled = true;
      if (monthDetailsGaugeAnimationFrameRef.current !== null) {
        cancelAnimationFrame(monthDetailsGaugeAnimationFrameRef.current);
        monthDetailsGaugeAnimationFrameRef.current = null;
      }
    };
  }, [renderedMonthDetailsGauges, hasOrderMixGaugeSource, loading]);
  const chartIntelligentForecastPct = chartData.length
    ? Number(chartData[chartData.length - 1]?.realitySeasoning || 0)
    : 0;
  const monthSalesProjectedValue = Math.max(salesMTD, simpleForecast);
  const intelligentForecastPct = monthlyGoal > 0
    ? (intelligentForecastValue / monthlyGoal) * 100
    : 0;
  const trafficSummaryText = buildTrafficSummarySentence(trafficSignals, trafficSummary);
  const hasTrafficData =
    Object.keys(trafficSummary || {}).length > 0
    || Object.keys(trafficSignals || {}).length > 0
    || Object.values(trafficCharts || {}).some((series) => Array.isArray(series) && series.length > 0);
  const activeTrafficTabConfig = TRAFFIC_TAB_OPTIONS.find((option) => option.key === activeTrafficTab) || TRAFFIC_TAB_OPTIONS[0];
  const activeTrafficKey = activeTrafficTabConfig.key;
  const trafficSignalPanels = [
    {
      panelKey: "trafficSessions",
      key: activeTrafficKey,
      label: activeTrafficTabConfig.label,
      kind: "sessions",
      subtext: "Non-bot sessions (MTD completed-day comparable)"
    },
    {
      panelKey: "conversionRate",
      key: "conversionRate",
      label: "Conversion Rate",
      kind: "conversion",
      subtext: "Sessions converting to completed checkout"
    },
    {
      panelKey: "cartAbandonment",
      key: "cartAbandonment",
      label: "Cart Abandonment",
      kind: "abandonment",
      subtext: "Add to cart users minus begin checkout users (MTD)",
      invertSignalPolarity: true
    },
    {
      panelKey: "clickThrough",
      key: "clickThrough",
      label: "Click Through",
      kind: "clickthrough",
      subtext: `Click events divided by sessions (${clickThroughMode === "paid" ? "Paid" : "Organic"})`
    }
  ];
  const trafficChartPanels = [
    { key: activeTrafficKey, label: `${activeTrafficTabConfig.label} Sessions`, color: TRAFFIC_SERIES_CONFIG?.[activeTrafficKey]?.color || "#4a5c7a", kind: "sessions" },
    { key: "conversionRate", label: "Conversion Rate", color: "#6b5ce7", kind: "conversion" },
    { key: "cartAbandonment", label: "Cart Abandonment", color: "#b42368", kind: "count" },
    { key: "clickThrough", label: "Click Through", color: "#007f8c", kind: "clickthrough" }
  ];
  const projectedPanelTone = intelligentForecastPct >= 100
    ? {
      value: "#1b5e20"
    }
    : intelligentForecastPct >= 80
      ? {
        value: "#8d6e00"
      }
      : {
        value: "#b71c1c"
      };
  const intradayPacePct = Number(intradayPacing?.pacePct || 0);
  const intradayExpectedSales = Number(intradayPacing?.expectedSalesByNow || 0);
  const intradayExpectedPct = Number(intradayPacing?.expectedPctByNow || 0);
  const displayedIntradayPacePct = intradayPacing && intradayExpectedSales > 0
    ? (displayedTodaySales / intradayExpectedSales) * 100
    : (todaysGoal > 0 ? (displayedTodaySales / todaysGoal) * 100 : 0);
  const intradayTone = intradayPacing
    ? (displayedIntradayPacePct >= 100 ? "success" : displayedIntradayPacePct >= 85 ? "caution" : "critical")
    : tone;

  const goalModelDiagnostics = useMemo(() => {
    if (!Array.isArray(chartData) || !chartData.length) {
      return { rows: chartData || [], stats: null };
    }

    const monthGoal = chartData.reduce((sum, row) => sum + (Number(row?.goal) || 0), 0);
    const todayIso = new Date().toLocaleDateString("en-CA");
    const completedRows = chartData.filter((row) => String(row?.dateISO) < todayIso);
    const baseDailyGoal = chartData.length > 0 ? (monthGoal / chartData.length) : 0;

    const isOutlierDay = (row) => {
      const goal = Number(row?.goal || 0);
      const actual = Number(row?.webOnly || 0);
      const dailyPct = goal > 0 ? (actual / goal) * 100 : 0;
      const monthlyPct = monthGoal > 0 ? (actual / monthGoal) * 100 : 0;
      return dailyPct >= 200 && monthlyPct >= 5;
    };

    const coreRows = completedRows.filter((row) => !isOutlierDay(row));
    const sourceRows = coreRows.length >= 5 ? coreRows : completedRows;

    const weekdayRatios = new Map();
    sourceRows.forEach((row) => {
      const weekday = new Date(row.date).getDay();
      const actual = Number(row?.webOnly || 0);
      const ratioRaw = baseDailyGoal > 0 ? (actual / baseDailyGoal) : 0;
      const ratioCapped = Math.max(0.15, Math.min(ratioRaw, 2.5));
      if (!weekdayRatios.has(weekday)) weekdayRatios.set(weekday, []);
      weekdayRatios.get(weekday).push(ratioCapped);
    });

    const fallbackShares = [0.03, 0.16, 0.15, 0.15, 0.23, 0.16, 0.12];
    const weekdayScores = Array.from({ length: 7 }, (_, weekday) => {
      const values = weekdayRatios.get(weekday) || [];
      if (!values.length) return fallbackShares[weekday] * 7;
      const sorted = [...values].sort((a, b) => a - b);
      const middle = Math.floor(sorted.length / 2);
      return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
    });

    const totalScore = weekdayScores.reduce((sum, score) => sum + score, 0);
    const normalizedWeekShare = weekdayScores.map((score, index) => (
      totalScore > 0 ? (score / totalScore) : fallbackShares[index]
    ));

    const weekdayCountInMonth = chartData.reduce((acc, row) => {
      const weekday = new Date(row.date).getDay();
      acc[weekday] = (acc[weekday] || 0) + 1;
      return acc;
    }, {});

    const recalibratedGoalsRaw = chartData.map((row) => {
      const weekday = new Date(row.date).getDay();
      const count = Number(weekdayCountInMonth[weekday] || 1);
      const weekdayBudget = monthGoal * normalizedWeekShare[weekday];
      return Math.round(weekdayBudget / count);
    });
    const rawRollup = recalibratedGoalsRaw.reduce((sum, value) => sum + value, 0);
    const rollupDelta = Math.round(monthGoal - rawRollup);
    if (recalibratedGoalsRaw.length > 0 && rollupDelta !== 0) {
      recalibratedGoalsRaw[recalibratedGoalsRaw.length - 1] += rollupDelta;
    }

    const rows = chartData.map((row, index) => {
      const actual = Number(row?.webOnly || 0);
      const oldGoal = Number(row?.goal || 0);
      const recalGoal = Number(recalibratedGoalsRaw[index] || 0);
      const currentPct = oldGoal > 0 ? (actual / oldGoal) * 100 : null;
      const recalPct = recalGoal > 0 ? (actual / recalGoal) * 100 : null;
      return {
        ...row,
        oldDailyGoal: oldGoal,
        recalibratedDailyGoal: recalGoal,
        currentDailyGoalPct: currentPct,
        recalibratedDailyGoalPct: recalPct,
        varianceVsCurrentGoalPct: currentPct !== null ? currentPct - 100 : null,
        varianceVsRecalibratedGoalPct: recalPct !== null ? recalPct - 100 : null
      };
    });

    const completedRowsWithDiag = rows.filter((row) => String(row?.dateISO) < todayIso);
    const varianceCurrent = completedRowsWithDiag
      .map((row) => Math.abs(Number(row.varianceVsCurrentGoalPct)))
      .filter((value) => Number.isFinite(value));
    const varianceRecal = completedRowsWithDiag
      .map((row) => Math.abs(Number(row.varianceVsRecalibratedGoalPct)))
      .filter((value) => Number.isFinite(value));
    const daysOverOld = completedRowsWithDiag.filter((row) => Number(row.currentDailyGoalPct) >= 100).length;
    const daysOverRecal = completedRowsWithDiag.filter((row) => Number(row.recalibratedDailyGoalPct) >= 100).length;
    const biggestOverCurrent = completedRowsWithDiag
      .slice()
      .sort((a, b) => Number(b.varianceVsCurrentGoalPct) - Number(a.varianceVsCurrentGoalPct))[0] || null;
    const biggestUnderCurrent = completedRowsWithDiag
      .slice()
      .sort((a, b) => Number(a.varianceVsCurrentGoalPct) - Number(b.varianceVsCurrentGoalPct))[0] || null;

    return {
      rows,
      stats: {
        avgAbsVarianceOld: varianceCurrent.length ? (varianceCurrent.reduce((a, b) => a + b, 0) / varianceCurrent.length) : 0,
        avgAbsVarianceRecal: varianceRecal.length ? (varianceRecal.reduce((a, b) => a + b, 0) / varianceRecal.length) : 0,
        daysOverOld,
        daysOverRecal,
        biggestOverCurrent,
        biggestUnderCurrent
      }
    };
  }, [chartData]);

  const revenueGoalDiagnostics = useMemo(() => {
    const rows = Array.isArray(goalModelDiagnostics.rows) ? goalModelDiagnostics.rows : [];
    let runningActualMtd = 0;
    let runningCurrentExpectedMtd = 0;
    let runningRecalExpectedMtd = 0;

    const enrichedRows = rows.map((row) => {
      const actualRevenue = Number(row?.webOnly || 0);
      const currentExpected = Number(row?.oldDailyGoal || row?.goal || 0);
      const recalibratedExpected = Number(row?.recalibratedDailyGoal || row?.goal || 0);

      runningActualMtd += actualRevenue;
      runningCurrentExpectedMtd += currentExpected;
      runningRecalExpectedMtd += recalibratedExpected;

      return {
        ...row,
        actualRevenue,
        currentExpectedRevenue: currentExpected,
        recalibratedExpectedRevenue: recalibratedExpected,
        actualMTD: runningActualMtd,
        currentExpectedMTD: runningCurrentExpectedMtd,
        recalibratedExpectedMTD: runningRecalExpectedMtd,
        varianceVsCurrentDollar: runningActualMtd - runningCurrentExpectedMtd,
        varianceVsRecalibratedDollar: runningActualMtd - runningRecalExpectedMtd
      };
    });

    const todayIso = new Date().toLocaleDateString("en-CA");
    const completedRows = enrichedRows.filter((row) => String(row?.dateISO) < todayIso);
    const latestCompleted = completedRows.length ? completedRows[completedRows.length - 1] : null;

    return {
      rows: enrichedRows,
      summary: latestCompleted
        ? {
          mtdActual: Number(latestCompleted.actualMTD || 0),
          mtdExpectedCurrent: Number(latestCompleted.currentExpectedMTD || 0),
          mtdExpectedRecalibrated: Number(latestCompleted.recalibratedExpectedMTD || 0),
          varianceVsCurrent: Number(latestCompleted.varianceVsCurrentDollar || 0),
          varianceVsRecalibrated: Number(latestCompleted.varianceVsRecalibratedDollar || 0)
        }
        : null
    };
  }, [goalModelDiagnostics.rows]);

  const movingAverageDays = movingAveragePreset === "custom"
    ? Math.max(1, Math.floor(Number(movingAverageCustomDaysInput) || 5))
    : Math.max(1, Math.floor(Number(movingAveragePreset) || 5));

  const dailySalesGapTrend = useMemo(() => {
    const todayISO = new Date().toLocaleDateString("en-CA");
    const rows = Array.isArray(chartData) ? chartData : [];
    const movingWindowSize = Math.max(1, Math.floor(movingAverageDays));
    const transformed = rows.map((row, index) => {
      const isInScope = String(row?.dateISO || "") <= todayISO;
      const expectedGoal = Number(row?.goal || 0);
      const actualRevenue = Number(row?.actual || 0);
      const webOnly = Number(row?.webOnly || 0);
      const gapPct = isInScope && expectedGoal > 0
        ? ((actualRevenue / expectedGoal) - 1)
        : null;

      if (!Number.isFinite(gapPct)) {
        return { ...row, gapPct: null, gapPctMA: null };
      }

      const windowStart = Math.max(0, index - (movingWindowSize - 1));
      const windowValues = rows
        .slice(windowStart, index + 1)
        .map((point) => {
          const pointInScope = String(point?.dateISO || "") <= todayISO;
          const pointGoal = Number(point?.goal || 0);
          const pointActual = Number(point?.actual || 0);
          if (!pointInScope || pointGoal <= 0) return null;
          const pointGap = (pointActual / pointGoal) - 1;
          return Number.isFinite(pointGap) ? pointGap : null;
        })
        .filter((value) => value !== null);

      const gapPctMA = windowValues.length
        ? (windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length)
        : null;
      const webWindowStart = Math.max(0, index - (movingWindowSize - 1));
      const webWindowValues = rows
        .slice(webWindowStart, index + 1)
        .map((point) => Number(point?.webOnly || 0))
        .filter((value) => Number.isFinite(value));
      const webOnlyMA = webWindowValues.length
        ? (webWindowValues.reduce((sum, value) => sum + value, 0) / webWindowValues.length)
        : null;

      return {
        ...row,
        gapPct,
        gapPctMA: Number.isFinite(gapPctMA) ? gapPctMA : null,
        webOnlyMA: Number.isFinite(webOnlyMA) ? webOnlyMA : webOnly
      };
    });

    const maPoints = transformed
      .map((row) => row.gapPctMA)
      .filter((value) => Number.isFinite(value));
    const latestGapPctMA = maPoints.length ? maPoints[maPoints.length - 1] : null;
    const previousGapPctMA = maPoints.length > 1 ? maPoints[maPoints.length - 2] : null;

    let direction = "Not enough data";
    if (Number.isFinite(latestGapPctMA) && Number.isFinite(previousGapPctMA)) {
      if (latestGapPctMA > previousGapPctMA) {
        direction = "Improving";
      } else if (latestGapPctMA < previousGapPctMA) {
        direction = "Worsening";
      } else {
        direction = "Flat";
      }
    } else if (Number.isFinite(latestGapPctMA)) {
      direction = "Flat";
    }

    return {
      rows: transformed,
      latestGapPctMA,
      previousGapPctMA,
      direction
    };
  }, [chartData, movingAverageDays]);

  useEffect(() => {
    if (environment === "production") {
      return;
    }

    console.log("Smart forecast debug", {
      monthlyGoal,
      salesMTD,
      planMTD,
      completedDaysUsed,
      recentCompletedDaySalesTotal,
      recentCompletedDayRunRate,
      remainingDays,
      forecastRemainingCoreSales,
      remainingExpectedOutlierCount,
      expectedRemainingOutlierUpside,
      intelligentForecast: intelligentForecastValue,
      renderedCardField: "intelligentForecastValue"
    });

    if (!Number.isFinite(intelligentForecastValue)) {
      console.warn("Intelligent Forecast is non-finite.", { intelligentForecastValue });
    }

    if (intelligentForecastValue < salesMTD) {
      console.warn("Intelligent Forecast is below salesMTD.", {
        intelligentForecastValue,
        salesMTD
      });
    }

    if (!monthlyGoal || monthlyGoal <= 0) {
      console.warn("Monthly goal is missing or non-positive.", { monthlyGoal });
    }

    if (!planMTD || planMTD <= 0) {
      console.warn("planMTD is missing or non-positive.", { planMTD });
    }
  }, [
    environment,
    monthlyGoal,
    salesMTD,
    planMTD,
    completedDaysUsed,
    recentCompletedDaySalesTotal,
    recentCompletedDayRunRate,
    remainingDays,
    forecastRemainingCoreSales,
    remainingExpectedOutlierCount,
    expectedRemainingOutlierUpside,
    intelligentForecastValue
  ]);

  const getGoalChartLineProps = (dataKey, baseStroke, options = {}) => {
    const isHovered = hoveredGoalSeries === dataKey;
    const isDimmed = hoveredGoalSeries && !isHovered;
    const defaultDot = options.dot ?? true;
    const defaultStrokeWidth = options.strokeWidth ?? 2;
    const activeDotRadius = options.activeDotRadius ?? 7;
    const dotRadius = options.dotRadius ?? 4;

    return {
      stroke: isHovered ? "#000000" : baseStroke,
      strokeWidth: defaultStrokeWidth,
      strokeOpacity: isHovered ? 1 : (isDimmed ? 0.28 : 1),
      activeDot: defaultDot ? { r: isHovered ? activeDotRadius : 5 } : false,
      dot: defaultDot ? { r: isHovered ? dotRadius : 3 } : false,
      onMouseEnter: () => setHoveredGoalSeries(dataKey),
      onMouseLeave: () => setHoveredGoalSeries(null)
    };
  };

  if (isReadinessBoardPage) {
    return (
      <AppProvider>
        <Page fullWidth>
          <Layout>
            <Layout.Section variant="fullWidth">
              <div className="readiness-header">
                <BlockStack gap="050">
                  <Text variant="headingLg" as="h1">Readiness Board</Text>
                  <Text variant="bodyMd" tone="subdued" as="p">
                    Lifecycle, checklist, support controls, and evidence
                  </Text>
                </BlockStack>
                <InlineStack gap="200" blockAlign="center" wrap={true}>
                  <div className="readiness-project-select">
                    <label className="readiness-project-select__label" htmlFor="readiness-project-selector">
                      Project
                    </label>
                    <select
                      id="readiness-project-selector"
                      className="readiness-project-select__control"
                      value={readinessProjectCode}
                      onChange={(event) => setReadinessProjectCode(event.target.value)}
                    >
                      {readinessProjectGroups.map((group) => (
                        <optgroup label={group.label} key={group.label}>
                          {group.options.map((option) => (
                            <option value={option.value} key={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {readinessProjectCode === MALTA_NETSUITE_SESSION_PROJECT_CODE ? (
                      <div className="readiness-current-plan-descriptor">
                        {READINESS_CURRENT_PLAN_DESCRIPTOR}
                      </div>
                    ) : (
                      <div className="readiness-current-plan-descriptor readiness-current-plan-descriptor--muted">
                        Viewing comparison · {READINESS_PROJECT_LABELS[readinessProjectCode] || readinessProject?.project_name || readinessProjectCode}
                      </div>
                    )}
                  </div>
                  <Button onClick={() => fetchReadinessBoard()} icon={RefreshIcon} loading={readinessLoading}>
                    Refresh
                  </Button>
                  <Button onClick={copyReadinessBoardLink} icon={ClipboardIcon}>
                    Copy Shopify Link
                  </Button>
                  {readinessShareFeedback ? (
                    <span className="readiness-share-feedback">{readinessShareFeedback}</span>
                  ) : null}
                  <Button variant="primary" onClick={addReadinessItem} icon={PlusIcon} loading={readinessSaving}>
                    Add Item
                  </Button>
                  <Button onClick={() => { window.location.href = "/"; }}>
                    Dashboard
                  </Button>
                </InlineStack>
              </div>
            </Layout.Section>

            <Layout.Section variant="fullWidth">
              <div className="readiness-status-grid">
                {[
                  ["Current Phase", readinessProject?.current_phase || "-"],
                  ["Overall Status", readinessProject?.overall_status || "-"],
                  ["Next Milestone", readinessProject?.next_milestone || "-"],
                  ["Open Items", readinessOpenItems],
                  ["Done Items", readinessDoneItems]
                ].map(([label, value]) => (
                  <div className="readiness-status-card" key={label}>
                    <div className="readiness-status-card__label">{label}</div>
                    <div className="readiness-status-card__value">{value}</div>
                  </div>
                ))}
              </div>
            </Layout.Section>

            {isMaltaNetSuiteMigration && readinessPlanAnalysis ? (
              <Layout.Section variant="fullWidth">
                <Card sectioned>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center" wrap={true}>
                      <BlockStack gap="050">
                        <Text variant="headingLg" as="h2">Executive Feasibility Summary</Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Six-week migration schedule plus a separate two-week hypercare period
                        </Text>
                      </BlockStack>
                      <Banner status="critical">
                        Six-week feasibility: {readinessPlanAnalysis.feasibilityRating}
                      </Banner>
                    </InlineStack>

                    <div className="readiness-status-grid readiness-plan-metrics">
                      {[
                        ["Proposed start", readinessPlanAnalysis.proposedStartDate],
                        ["Proposed go-live", readinessPlanAnalysis.proposedGoLiveDate],
                        ["Hypercare complete", readinessPlanAnalysis.hypercareCompletionDate],
                        ["Total labor hours", readinessPlanAnalysis.totalEstimatedLaborHours],
                        ["Six-week hours", readinessPlanAnalysis.sixWeekMigrationHours],
                        ["Hypercare hours", readinessPlanAnalysis.hypercareHours],
                        ["Jim hours", readinessPlanAnalysis.jimHours],
                        ["Cody hours", readinessPlanAnalysis.codyHours],
                        ["Jim six-week hours", readinessPlanAnalysis.jimSixWeekHours],
                        ["Cody six-week hours", readinessPlanAnalysis.codySixWeekHours],
                        ["Supporting hours", readinessPlanAnalysis.supportingResourceHours],
                        ["Peak weekly staffing", `${readinessPlanAnalysis.peakWeeklyStaffingHours} hrs`],
                        ["Critical path", `${readinessPlanAnalysis.criticalPathBusinessDays} business days`],
                        ["AI/SDF hours saved", readinessPlanAnalysis.estimatedHoursSavedBySdfAndChatGPT],
                        ["Outside-vendor hours", readinessPlanAnalysis.outsideVendorHours],
                        ["Business-user hours", readinessPlanAnalysis.businessUserTestingHours],
                        ["Unassigned hours", readinessPlanAnalysis.unassignedHours]
                      ].map(([label, value]) => (
                        <div className="readiness-status-card" key={label}>
                          <div className="readiness-status-card__label">{label}</div>
                          <div className="readiness-status-card__value">{value ?? "-"}</div>
                        </div>
                      ))}
                    </div>

                    <div className="readiness-plan-callout">
                      <strong>Why the rating is {readinessPlanAnalysis.feasibilityRating}:</strong>{" "}
                      {readinessPlanAnalysis.feasibilityExplanation}
                    </div>
                    <div className="readiness-plan-schedule-grid">
                      <div><strong>Most likely schedule</strong><span>{readinessPlanAnalysis.mostLikelySchedule}</span></div>
                      <div><strong>Conservative schedule</strong><span>{readinessPlanAnalysis.conservativeSchedule}</span></div>
                    </div>

                    <div className="readiness-plan-grid">
                      <div className="readiness-plan-panel">
                        <h3>Weekly capacity</h3>
                        <div className="readiness-plan-table-wrap">
                          <table className="readiness-plan-table">
                            <thead><tr><th>Week</th><th>Total</th><th>Jim</th><th>Cody</th></tr></thead>
                            <tbody>
                              {(readinessPlanAnalysis.hoursByWeek || []).map((row) => (
                                <tr key={row.week}>
                                  <td>{row.week}{row.week > 6 ? " (Hypercare)" : ""}</td>
                                  <td>{row.hours}</td>
                                  <td>{readinessPlanAnalysis.weeklyHoursByResource?.Jim?.[row.week] || 0}</td>
                                  <td>{readinessPlanAnalysis.weeklyHoursByResource?.Cody?.[row.week] || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="readiness-plan-panel">
                        <h3>Resource totals and peaks</h3>
                        <div className="readiness-plan-table-wrap">
                          <table className="readiness-plan-table">
                            <thead><tr><th>Resource</th><th>Total</th><th>Peak week</th><th>Peak hours</th></tr></thead>
                            <tbody>
                              {(readinessPlanAnalysis.hoursByResource || []).map((row) => (
                                <tr key={row.resource}><td>{row.resource}</td><td>{row.hours}</td><td>{row.peak?.week}</td><td>{row.peak?.hours}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="readiness-plan-panel">
                      <h3>Hours by workstream</h3>
                      <div className="readiness-plan-table-wrap">
                        <table className="readiness-plan-table">
                          <thead><tr><th>Workstream</th><th>Hours</th></tr></thead>
                          <tbody>
                            {(readinessPlanAnalysis.hoursByWorkstream || []).map((row) => (
                              <tr key={row.workstream}><td>{row.workstream}</td><td>{row.hours}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="readiness-plan-grid">
                      <div className="readiness-plan-panel">
                        <h3>Overtime and resource conflicts</h3>
                        <ul>{(readinessPlanAnalysis.resourceConflicts || []).map((item) => <li key={item}>{item}</li>)}</ul>
                      </div>
                      <div className="readiness-plan-panel">
                        <h3>Schedule float</h3>
                        <ul>{Object.entries(readinessPlanAnalysis.scheduleFloat || {}).map(([label, value]) => <li key={label}><strong>{label}:</strong> {value}</li>)}</ul>
                      </div>
                    </div>

                    <div className="readiness-plan-panel">
                      <h3>Tasks requiring overtime</h3>
                      <div className="readiness-plan-table-wrap">
                        <table className="readiness-plan-table">
                          <thead><tr><th>Week</th><th>Resource</th><th>Task</th><th>Workstream</th><th>Resource hours</th></tr></thead>
                          <tbody>
                            {(readinessPlanAnalysis.tasksRequiringOvertime || []).map((row, index) => (
                              <tr key={`${row.week}-${row.resource}-${row.task}-${index}`}><td>{row.week}</td><td>{row.resource}</td><td>{row.task}</td><td>{row.workstream}</td><td>{row.taskResourceHours}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="readiness-plan-grid readiness-plan-grid--three">
                      <div className="readiness-plan-panel"><h3>Top ten risks</h3><ol>{(readinessPlanAnalysis.topRisks || []).map((item) => <li key={item}>{item}</li>)}</ol></div>
                      <div className="readiness-plan-panel"><h3>Minimum six-week conditions</h3><ol>{(readinessPlanAnalysis.minimumConditions || []).map((item) => <li key={item}>{item}</li>)}</ol></div>
                      <div className="readiness-plan-panel"><h3>Required decisions</h3><ol>{(readinessPlanAnalysis.requiredDecisions || []).map((item) => <li key={item.decision}><strong>{item.dueDate}:</strong> {item.decision}</li>)}</ol></div>
                      <div className="readiness-plan-panel"><h3>Major assumptions</h3><ul>{(readinessPlanAnalysis.majorAssumptions || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Major exclusions</h3><ul>{(readinessPlanAnalysis.majorExclusions || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Reusable migration-factory assets</h3><ul>{(readinessPlanAnalysis.reusableAssets || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Target-decision dependencies</h3><ul>{(readinessPlanAnalysis.targetDecisionDependencies || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Outside-vendor dependencies</h3><ul>{(readinessPlanAnalysis.outsideVendorDependencies || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Accounting dependencies</h3><ul>{(readinessPlanAnalysis.accountingDependencies || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Operations-testing dependencies</h3><ul>{(readinessPlanAnalysis.operationsTestingDependencies || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Most likely schedule extenders</h3><ol>{(readinessPlanAnalysis.tasksMostLikelyToExtendSchedule || []).map((item) => <li key={item}>{item}</li>)}</ol></div>
                    </div>
                  </BlockStack>
                </Card>
              </Layout.Section>
            ) : null}

            {isMaltaNetSuiteNormalized && readinessPlanAnalysis ? (
              <Layout.Section variant="fullWidth">
                <Card sectioned>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center" wrap={true}>
                      <BlockStack gap="050">
                        <Text variant="headingLg" as="h2">Normalized CLI/AI Execution Estimate</Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Second scenario only. The 308-task / 1,794-hour baseline remains unchanged and linked through the normalization audit.
                        </Text>
                      </BlockStack>
                      <InlineStack gap="200" blockAlign="center" wrap={true}>
                        <Button icon={ClipboardIcon} onClick={copyNormalizedPlanForAi}>Copy AI Plan</Button>
                        <Banner status="warning">Most likely: nine weeks plus two weeks of hypercare</Banner>
                      </InlineStack>
                    </InlineStack>
                    {readinessPlanCopyFeedback ? <Text variant="bodySm" tone="subdued">{readinessPlanCopyFeedback}</Text> : null}

                    <div className="readiness-status-grid readiness-plan-metrics">
                      {[
                        ["Original baseline", `${readinessPlanAnalysis.originalBaselineHours} hrs`],
                        ["Normalized human labor", `${readinessPlanAnalysis.normalizedTotalHours} hrs`],
                        ["Through go-live", `${readinessPlanAnalysis.normalizedGoLiveHours} hrs`],
                        ["Hypercare", `${readinessPlanAnalysis.normalizedHypercareHours} hrs`],
                        ["Automated elapsed", `${readinessPlanAnalysis.automatedElapsedHours} hrs`],
                        ["Human review + approval", `${readinessPlanAnalysis.humanReviewAndApprovalHours} hrs`],
                        ["SuiteCloud CLI/SDF saved", `${readinessPlanAnalysis.suiteCloudCliSdfHoursSaved} hrs`],
                        ["Jira automation saved", `${readinessPlanAnalysis.jiraAutomationHoursSaved} hrs`],
                        ["AI assistance saved", `${readinessPlanAnalysis.aiAssistanceHoursSaved} hrs`],
                        ["Malta execution", `${readinessPlanAnalysis.maltaExecutionHours} hrs`],
                        ["VSG factory development", `${readinessPlanAnalysis.migrationFactoryDevelopmentHours} hrs`],
                        ["Audit coverage", `${readinessPlanAnalysis.normalizationAuditRows} / ${readinessPlanAnalysis.originalBaselineTasks} tasks`]
                      ].map(([label, value]) => (
                        <div className="readiness-status-card" key={label}><div className="readiness-status-card__label">{label}</div><div className="readiness-status-card__value">{value}</div></div>
                      ))}
                    </div>

                    <div className="readiness-plan-table-wrap">
                      <table className="readiness-plan-table">
                        <thead><tr><th>Scenario</th><th>Migration</th><th>Hypercare</th><th>Rating</th><th>Confidence</th><th>Jim</th><th>Cody</th><th>Functional/data lead</th><th>Integration/test</th><th>Additional support</th><th>Total</th></tr></thead>
                        <tbody>
                          {(readinessPlanAnalysis.scenarios || []).map((row) => (
                            <tr key={row.scenario}>
                              <td><strong>{row.scenario}</strong></td><td>{row.migrationDates}</td><td>{row.hypercareDates}</td><td>{row.rating}</td><td>{row.confidence}</td>
                              <td>{row.jimHours}</td><td>{row.codyHours}</td><td>{row.functionalLeadHours}</td><td>{row.integrationAnalystHours}</td><td>{row.additionalSupportHours || 0}</td><td>{row.totalHours}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="readiness-plan-grid">
                      <div className="readiness-plan-panel"><h3>Estimation guardrails</h3><ul>{(readinessPlanAnalysis.estimationRules || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Revised resource hours</h3><ul>{Object.entries(readinessPlanAnalysis.resourceHours || {}).map(([resource, hours]) => <li key={resource}><strong>{resource}:</strong> {hours} hrs</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Confidence distribution</h3><ul>{Object.entries(readinessPlanAnalysis.confidenceDistribution || {}).map(([level, count]) => <li key={level}><strong>{level}:</strong> {count} baseline tasks</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Feasibility conclusion</h3><p>{readinessPlanAnalysis.finalAssessment?.sixWeekAnswer}</p><p><strong>Most defensible:</strong> {readinessPlanAnalysis.finalAssessment?.mostDefensibleSchedule}</p><p><strong>Conservative:</strong> {readinessPlanAnalysis.finalAssessment?.conservativeSchedule}</p></div>
                    </div>

                    <details className="readiness-plan-details">
                      <summary>Representative calibration sample ({readinessPlanAnalysis.representativeSampleSize} tasks)</summary>
                      <div className="readiness-plan-table-wrap">
                        <table className="readiness-plan-table">
                          <thead><tr><th>Category</th><th>Baseline task</th><th>Existing</th><th>Traditional assumption</th><th>CLI/AI method</th><th>Prep</th><th>Auto elapsed</th><th>Review</th><th>Correction</th><th>Approval</th><th>Normalized</th><th>Saved</th><th>Confidence</th><th>Remaining risk</th></tr></thead>
                          <tbody>
                            {(readinessPlanAnalysis.representativeSample || []).map((row, index) => (
                              <tr key={`${row.baselineTask}-${index}`}>
                                <td>{row.category}</td><td>{row.baselineTask}</td><td>{row.existingEstimateHours}</td><td>{row.traditionalExecutionAssumption}</td><td>{row.cliAiExecutionMethod}</td>
                                <td>{row.preparationHours}</td><td>{row.automatedElapsedHours}</td><td>{row.humanReviewHours}</td><td>{row.correctionTroubleshootingHours}</td><td>{row.humanApprovalHours}</td>
                                <td>{row.normalizedHumanHours}</td><td>{row.estimatedHoursSaved}</td><td>{row.confidence}</td><td>{row.remainingRisks}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>

                    <details className="readiness-plan-details">
                      <summary>Complete baseline-to-normalized audit ({readinessNormalizationAudit.length} mappings)</summary>
                      <div className="readiness-plan-table-wrap">
                        <table className="readiness-plan-table">
                          <thead><tr><th>Baseline task</th><th>Normalized work package</th><th>Before</th><th>After</th><th>Traditional</th><th>Normalized human</th><th>Automated elapsed</th><th>CLI/SDF saved</th><th>Jira saved</th><th>AI saved</th><th>Scope</th><th>Confidence</th><th>Basis</th></tr></thead>
                          <tbody>
                            {readinessNormalizationAudit.map((row) => (
                              <tr key={row.id}>
                                <td>{row.baseline_task_title}</td><td>{row.normalized_task_title}</td><td>{row.assignment_before}</td><td>{row.assignment_after}</td>
                                <td>{row.traditional_hours}</td><td>{row.normalized_hands_on_hours}</td><td>{row.automated_elapsed_hours}</td><td>{row.cli_sdf_hours_saved}</td>
                                <td>{row.jira_automation_hours_saved}</td><td>{row.ai_assistance_hours_saved}</td><td>{row.scope_classification}</td><td>{row.confidence_level}</td><td>{row.estimate_basis}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </BlockStack>
                </Card>
              </Layout.Section>
            ) : null}

            {isMaltaNetSuiteSession && readinessPlanAnalysis ? (
              <Layout.Section variant="fullWidth">
                <Card sectioned>
                  <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center" wrap={true}>
                      <BlockStack gap="050">
                        <Text variant="headingLg" as="h2">CLI-AI-v3 Execution-Session Model</Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Estimates 79 coordinated work packages directly. All 308 baseline rows remain linked as completeness checklists and generate no separate labor burden.
                        </Text>
                      </BlockStack>
                      <InlineStack gap="200" blockAlign="center" wrap={true}>
                        <Button icon={ClipboardIcon} onClick={copyNormalizedPlanForAi}>Copy AI Plan</Button>
                        <Banner status="warning">Current-team sustainable schedule: {readinessPlanAnalysis.finalAssessment?.shortestHonestSchedule}<br />{readinessPlanAnalysis.finalAssessment?.sevenWeekAcceleration}</Banner>
                      </InlineStack>
                    </InlineStack>
                    {readinessPlanCopyFeedback ? <Text variant="bodySm" tone="subdued">{readinessPlanCopyFeedback}</Text> : null}

                    <div className="readiness-plan-panel readiness-plan-panel--executive">
                      <h3>Executive conclusion</h3>
                      <p>
                        The original 1,794-hour plan is now normalized to a {readinessPlanAnalysis.v3MostLikelyLabor}-hour most-likely migration estimate after sequencing and known-exception QC. A six-week migration remains {readinessPlanAnalysis.sixWeekCapacity?.rating} because of peak resource requirements. {readinessPlanAnalysis.finalAssessment?.shortestHonestSchedule} {readinessPlanAnalysis.finalAssessment?.sevenWeekAcceleration} The conservative plan remains {(readinessPlanAnalysis.scenarios || []).find((row) => row.scenario?.startsWith("C -"))?.migrationWeeks || 12} migration weeks plus two hypercare weeks.
                      </p>
                      <p><strong>CRM estimate boundary:</strong> {readinessPlanAnalysis.crmScopeAudit?.estimateScopeStatement}</p>
                    </div>

                    {readinessPlanAnalysis.crmScopeAudit ? (
                      <details className="readiness-plan-details" open>
                        <summary>Native NetSuite CRM — approved increment with discovery validation</summary>
                        <div className="readiness-plan-grid readiness-plan-grid--three">
                          <div className="readiness-plan-panel">
                            <h3>Scope conclusion</h3>
                            <p>{readinessPlanAnalysis.crmScopeAudit.conclusion}</p>
                          </div>
                          <div className="readiness-plan-panel">
                            <h3>Record counts</h3>
                            <p><strong>{readinessPlanAnalysis.crmScopeAudit.counts?.availability}</strong></p>
                            <p>No unknown CRM count is treated as zero.</p>
                          </div>
                          <div className="readiness-plan-panel">
                            <h3>Estimate boundary</h3>
                            <p>{readinessPlanAnalysis.crmScopeAudit.implementationBoundary}</p>
                          </div>
                        </div>

                        <div className="readiness-plan-panel">
                          <h3>Formal CRM discovery and estimate gate</h3>
                          <p><strong>{readinessPlanAnalysis.crmScopeAudit.discoveryAndEstimateGate?.status}</strong></p>
                          <ul>{(readinessPlanAnalysis.crmScopeAudit.discoveryAndEstimateGate?.requirements || []).map((item) => <li key={item}>{item}</li>)}</ul>
                          <p>{readinessPlanAnalysis.crmScopeAudit.discoveryAndEstimateGate?.approvalRule}</p>
                        </div>

                        <div className="readiness-plan-table-wrap">
                          <table className="readiness-plan-table">
                            <thead><tr><th>Approved CRM estimate bridge</th><th>Current value</th></tr></thead>
                            <tbody>{[
                              ["Incremental base labor", "incrementalBaseLabor"],
                              ["Probability-weighted reserve", "probabilityWeightedReserve"],
                              ["Most-likely CRM labor", "mostLikelyLabor"],
                              ["Full risk exposure", "fullRiskExposure"],
                              ["Conservative CRM labor", "conservativeLabor"],
                              ["Jim hours", "jimHours"],
                              ["Functional/data lead hours", "functionalDataLeadHours"],
                              ["SME hours", "smeHours"],
                              ["Schedule impact", "scheduleImpact"],
                              ["Peak-capacity impact", "peakCapacityImpact"],
                              ["Affected work packages", "affectedWorkPackages"],
                              ["Linked baseline task IDs", "linkedBaselineTaskIds"]
                            ].map(([label, key]) => {
                              const value = readinessPlanAnalysis.crmScopeAudit.discoveryAndEstimateGate?.proposedEstimateBridge?.[key];
                              return <tr key={key}><td>{label}</td><td>{Array.isArray(value) ? (value.join(", ") || "None") : (value ?? "Not applicable")}</td></tr>;
                            })}</tbody>
                          </table>
                          <p>{readinessPlanAnalysis.crmScopeAudit.discoveryAndEstimateGate?.proposedEstimateBridge?.baselineIdRule}</p>
                        </div>

                        <div className="readiness-plan-table-wrap">
                          <table className="readiness-plan-table">
                            <thead><tr><th>Supplemental CRM task</th><th>Host package</th><th>Base</th><th>Reserve</th><th>Most likely</th><th>Full exposure</th><th>Jim</th></tr></thead>
                            <tbody>{(readinessPlanAnalysis.crmScopeAudit.discoveryAndEstimateGate?.supplementalEstimatedTasks || []).map((row) => (
                              <tr key={row.name}><td>{row.name}</td><td>{row.hostPackageNumber} — {row.hostWorkPackage}</td><td>{row.base}</td><td>{row.weightedReserve}</td><td>{row.mostLikely}</td><td>{row.fullRiskExposure}</td><td>{row.resources?.Jim || 0}</td></tr>
                            ))}</tbody>
                          </table>
                        </div>

                        <div className="readiness-plan-table-wrap">
                          <table className="readiness-plan-table">
                            <thead><tr><th>Conditional CRM boundary</th><th>Workstream</th><th>Host package</th><th>Baseline task IDs</th><th>Estimate</th><th>Activation gate</th></tr></thead>
                            <tbody>{(readinessPlanAnalysis.crmScopeAudit.conditionalWorkPackageBoundaries || []).map((row) => (
                              <tr key={row.name}>
                                <td><strong>{row.name}</strong>{row.scope ? <><br /><span>{row.scope}</span></> : null}</td>
                                <td>{row.workstream}</td>
                                <td>{row.hostPackageNumber}</td>
                                <td>{(row.baselineTaskNumbers || []).length ? row.baselineTaskNumbers.join(", ") : "None — baseline gap recorded"}</td>
                                <td>{row.estimatedHours == null ? "Unestimated" : `${row.estimatedHours} hrs`}</td>
                                <td>{row.activationGate}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>

                        <div className="readiness-plan-grid">
                          <div className="readiness-plan-panel">
                            <h3>Counts required by type, status, and age</h3>
                            <ul>{(readinessPlanAnalysis.crmScopeAudit.counts?.requiredDimensions || []).map((item) => <li key={item}>{item}</li>)}</ul>
                          </div>
                          <div className="readiness-plan-panel">
                            <h3>Preliminary relationship-safe load sequence</h3>
                            <ol>{(readinessPlanAnalysis.crmScopeAudit.preliminaryLoadSequence || []).map((item) => <li key={item}>{item}</li>)}</ol>
                          </div>
                        </div>

                        <details className="readiness-plan-details">
                          <summary>Existing packages extended ({(readinessPlanAnalysis.crmScopeAudit.existingPackageExtensions || []).length})</summary>
                          <div className="readiness-plan-table-wrap">
                            <table className="readiness-plan-table">
                              <thead><tr><th>Package</th><th>Work package</th><th>Linked baseline task IDs</th><th>Estimate treatment</th></tr></thead>
                              <tbody>{(readinessPlanAnalysis.crmScopeAudit.existingPackageExtensions || []).map((row) => (
                                <tr key={row.packageNumber}><td>{row.packageNumber}</td><td>{row.workPackage}</td><td>{(row.linkedBaselineTaskIds || row.baselineTaskNumbers || []).join(", ") || "None"}</td><td>{row.estimateTreatment}</td></tr>
                              ))}</tbody>
                            </table>
                          </div>
                        </details>

                        <p><strong>Codex boundary:</strong> Codex may draft code, mappings, queries, package definitions, validation logic, and documentation. Every privileged action, business decision, validation, reconciliation, and approval remains assigned to an accountable human.</p>
                      </details>
                    ) : null}

                    <div className="readiness-plan-grid readiness-plan-grid--three">
                      <div className="readiness-plan-panel"><h3>Most-likely reconciliation</h3><p>{readinessPlanAnalysis.mostLikelyReconciliation}</p></div>
                      <div className="readiness-plan-panel"><h3>Conservative reconciliation</h3><p>{readinessPlanAnalysis.conservativeReconciliation}</p></div>
                      <div className="readiness-plan-panel"><h3>Most-likely to conservative bridge</h3><p>{readinessPlanAnalysis.mostLikelyToConservativeBridge}</p><p><strong>Increment:</strong> {readinessPlanAnalysis.v3FullRiskExposure} full exposure minus {readinessPlanAnalysis.v3RiskReserve} weighted reserve = {readinessPlanAnalysis.v3IncrementalConservativeReserve} hrs.</p></div>
                    </div>

                    <div className="readiness-plan-grid">
                      <div className="readiness-plan-panel"><h3>Sequencing QC — exactly what changed</h3><ul>{(readinessPlanAnalysis.sequencingQcChanges || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
                      <div className="readiness-plan-panel"><h3>Critical-path dependencies</h3><ol>{(readinessPlanAnalysis.criticalPathDependencies || []).map((item) => <li key={item}>{item}</li>)}</ol></div>
                    </div>

                    <div className="readiness-plan-panel">
                      <h3>Weekly allocation reconciliation</h3>
                      <p><strong>{readinessPlanAnalysis.weeklyAllocationReconciliation}</strong></p>
                      <p>The prior table displayed 595.3 hours because rounded package/resource shares were independently repeated across scheduled weeks. Exact tenth-hour allocations now preserve each package total; after subsequent risk de-duplication, the weekly table reconciles to the current most-likely estimate shown above.</p>
                    </div>

                    {readinessPlanAnalysis.package473Scope ? (
                      <div className="readiness-plan-grid">
                        <div className="readiness-plan-panel">
                          <h3>Package 473 — exact execution method and scope</h3>
                          <p>{readinessPlanAnalysis.package473Scope.executionMethod}</p>
                          <p><strong>Deliverable:</strong> {readinessPlanAnalysis.package473Scope.deliverable}</p>
                          <p><strong>Representative proof:</strong> {readinessPlanAnalysis.package473Scope.representativeProof}</p>
                          <p><strong>Excluded:</strong> {readinessPlanAnalysis.package473Scope.excludedDownstreamWork}</p>
                        </div>
                        <div className="readiness-plan-panel">
                          <h3>Package 473 — risk de-duplication</h3>
                          <p><strong>Before:</strong> {readinessPlanAnalysis.package473RiskDeduplication?.before?.weightedReserve} weighted / {readinessPlanAnalysis.package473RiskDeduplication?.before?.fullRiskImpact} full-risk hours; {readinessPlanAnalysis.package473RiskDeduplication?.before?.mostLikelyPackageHours} most likely / {readinessPlanAnalysis.package473RiskDeduplication?.before?.conservativePackageHours} conservative.</p>
                          <p><strong>After:</strong> {readinessPlanAnalysis.package473RiskDeduplication?.after?.weightedReserve} weighted / {readinessPlanAnalysis.package473RiskDeduplication?.after?.fullRiskImpact} full-risk hours; {readinessPlanAnalysis.package473RiskDeduplication?.after?.mostLikelyPackageHours} most likely / {readinessPlanAnalysis.package473RiskDeduplication?.after?.conservativePackageHours} conservative.</p>
                          <p><strong>Removed as duplicate:</strong> {readinessPlanAnalysis.package473RiskDeduplication?.removedDuplicateWeightedReserve} weighted and {readinessPlanAnalysis.package473RiskDeduplication?.removedDuplicateFullExposure} full-exposure hours.</p>
                          <p><strong>Retained boundary:</strong> {readinessPlanAnalysis.package473RiskDeduplication?.retainedRiskBoundary}</p>
                          <p><strong>Release trigger:</strong> {readinessPlanAnalysis.package473RiskDeduplication?.retainedReleaseTrigger}</p>
                        </div>
                      </div>
                    ) : null}

                    {(readinessPlanAnalysis.package473RiskDeduplication?.downstreamRiskAllocation || []).length ? (
                      <details className="readiness-plan-details">
                        <summary>Package 473 downstream risk-boundary check</summary>
                        <p>Downstream reserves remain {readinessPlanAnalysis.package473RiskDeduplication.downstreamReservesUnchanged} weighted hours against {readinessPlanAnalysis.package473RiskDeduplication.downstreamFullExposureUnchanged} full-exposure hours.</p>
                        <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Downstream package</th><th>Risk boundary retained downstream</th><th>Probability</th><th>Full exposure</th><th>Weighted reserve</th></tr></thead><tbody>{readinessPlanAnalysis.package473RiskDeduplication.downstreamRiskAllocation.map((row) => (
                          <tr key={row.workPackage}><td>{row.workPackage}</td><td>{row.riskBoundary}</td><td>{Math.round(Number(row.probability || 0) * 100)}%</td><td>{row.fullRiskImpact}</td><td>{row.weightedReserve}</td></tr>
                        ))}</tbody></table></div>
                      </details>
                    ) : null}

                    {readinessPlanAnalysis.aiCliLaborAudit ? (
                      <>
                        <div className="readiness-plan-grid readiness-plan-grid--three">
                          <div className="readiness-plan-panel"><h3>AI-CLI labor reclassification</h3><p><strong>{readinessPlanAnalysis.aiCliLaborAudit.totalReconciliation}</strong></p><p>AI/CLI retains zero accountable labor hours and no weekly human-capacity limit.</p></div>
                          <div className="readiness-plan-panel"><h3>Human supervision retained</h3><p><strong>{readinessPlanAnalysis.aiCliLaborAudit.activeHumanOperationOrSupervision} hours</strong></p><p>Reassigned package by package to Jim, the functional/data lead, or the accountable business/operations/accounting owner.</p></div>
                          <div className="readiness-plan-panel"><h3>Non-human labor removed</h3><p><strong>{readinessPlanAnalysis.aiCliLaborAudit.automatedElapsedReclassified} automated + {readinessPlanAnalysis.aiCliLaborAudit.duplicatedHumanReviewRemoved} duplicate hours</strong></p><p>Automated time remains visible for elapsed scheduling but is excluded from labor and capacity.</p></div>
                        </div>
                        <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Estimate stage</th><th>Base</th><th>Weighted risk</th><th>Most likely</th><th>Full exposure</th><th>Conservative</th><th>Automated elapsed</th></tr></thead><tbody>{Object.entries(readinessPlanAnalysis.aiCliLaborAudit.estimateBridge || {}).map(([stage, row]) => (
                          <tr key={stage}><td>{stage.replace(/([A-Z])/g, " $1")}</td><td>{row.base}</td><td>{row.riskReserve}</td><td>{row.mostLikely}</td><td>{row.fullRiskExposure}</td><td>{row.conservative}</td><td>{row.automatedElapsed ?? "—"}</td></tr>
                        ))}</tbody></table></div>
                        <details className="readiness-plan-details" open>
                          <summary>All {readinessPlanAnalysis.aiCliLaborAudit.packagesAudited} packages formerly allocated to AI-CLI</summary>
                          <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Work package</th><th>Current AI-CLI</th><th>Human supervision</th><th>Automated elapsed</th><th>Duplicate removed</th><th>Accountable human</th><th>Classification basis</th></tr></thead><tbody>{(readinessPlanAnalysis.aiCliLaborAudit.rows || []).map((row) => (
                            <tr key={row.workPackage}><td>{row.workPackage}</td><td>{row.currentAiCliHours}</td><td>{row.humanSupervisionHours}</td><td>{row.automatedElapsedHours}</td><td>{row.duplicatedHoursRemoved}</td><td>{row.accountableHumanOwner}</td><td>{row.classificationBasis}</td></tr>
                          ))}</tbody></table></div>
                        </details>
                      </>
                    ) : null}

                    <div className="readiness-plan-grid">
                      {readinessPlanAnalysis.package448Calibration ? <details className="readiness-plan-details" open><summary>Package 448 communication-cadence calibration</summary><p>{readinessPlanAnalysis.package448Calibration.duplicateBoundary}</p><div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Component</th><th>Calculation</th><th>Jim hours</th></tr></thead><tbody>{(readinessPlanAnalysis.package448Calibration.components || []).map((row) => <tr key={row.component}><td>{row.component}</td><td>{row.calculation}</td><td>{row.jimHours}</td></tr>)}</tbody></table></div><div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>View</th><th>Base</th><th>Weighted reserve</th><th>Most likely</th><th>Full exposure</th><th>Conservative</th><th>Jim</th></tr></thead><tbody><tr><td>Before</td><td>{readinessPlanAnalysis.package448Calibration.before.base}</td><td>{readinessPlanAnalysis.package448Calibration.before.weightedReserve}</td><td>{readinessPlanAnalysis.package448Calibration.before.mostLikely}</td><td>{readinessPlanAnalysis.package448Calibration.before.fullRiskExposure}</td><td>{readinessPlanAnalysis.package448Calibration.before.conservative}</td><td>{readinessPlanAnalysis.package448Calibration.before.jimHours}</td></tr><tr><td>After</td><td>{readinessPlanAnalysis.package448Calibration.after.base}</td><td>{readinessPlanAnalysis.package448Calibration.after.weightedReserve}</td><td>{readinessPlanAnalysis.package448Calibration.after.mostLikely}</td><td>{readinessPlanAnalysis.package448Calibration.after.fullRiskExposure}</td><td>{readinessPlanAnalysis.package448Calibration.after.conservative}</td><td>{readinessPlanAnalysis.package448Calibration.after.jimHours}</td></tr></tbody></table></div><p><strong>Jim-hour reduction:</strong> {readinessPlanAnalysis.package448Calibration.jimHourReduction}. <strong>Automated elapsed:</strong> {readinessPlanAnalysis.package448Calibration.automatedElapsed?.hours} hours. <strong>Additional attendee person-hours:</strong> {Object.keys(readinessPlanAnalysis.package448Calibration.additionalAttendeePersonHours || {}).length ? Object.entries(readinessPlanAnalysis.package448Calibration.additionalAttendeePersonHours).map(([resource, hours]) => `${resource}: ${hours}`).join(", ") : "None"}.</p><p>{readinessPlanAnalysis.package448Calibration.additionalAttendeeBasis}</p></details> : null}
                      <details className="readiness-plan-details" open><summary>Package 478/507 financial risk de-duplication</summary><div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Package</th><th>Non-overlapping boundary</th><th>Before weighted/full</th><th>After weighted/full</th></tr></thead><tbody>{(readinessPlanAnalysis.financialRiskDeduplication || []).map((row) => <tr key={row.workPackage}><td>{row.workPackage}</td><td>{row.boundary}</td><td>{row.before.weightedReserve} / {row.before.fullRiskImpact}</td><td>{row.after.weightedReserve} / {row.after.fullRiskImpact}</td></tr>)}</tbody></table></div></details>
                      <details className="readiness-plan-details" open><summary>EDI hands-on calibrations applied</summary><div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Package</th><th>Non-overlapping boundary</th><th>Before base / weighted / most likely / conservative</th><th>After base / weighted / most likely / conservative</th><th>Jim hours</th></tr></thead><tbody>{(readinessPlanAnalysis.ediCalibrations || []).map((row) => <tr key={row.workPackage}><td>{row.workPackage}</td><td>{row.boundary}</td><td>{row.before.base} / {row.before.weightedReserve} / {row.before.mostLikely} / {row.before.conservative}</td><td>{row.after.base} / {row.after.weightedReserve} / {row.after.mostLikely} / {row.after.conservative}</td><td>{row.after.jimHours}</td></tr>)}</tbody></table></div></details>
                    </div>

                    <div className="readiness-plan-table-wrap">
                      <p><strong>Schedule estimate boundary:</strong> {readinessPlanAnalysis.scheduleExportScopeStatement}</p>
                      <table className="readiness-plan-table">
                        <thead><tr><th>Capacity scenario</th><th>Assessment</th><th>Jim peak</th><th>Functional/data peak</th><th>Integration peak</th><th>SME peak</th><th>Team peak</th><th>Named peak capacity required</th></tr></thead>
                        <tbody>{(readinessPlanAnalysis.scheduleScenarioTests || []).map((row) => (
                          <tr key={row.migrationWeeks}>
                            <td>{row.migrationWeeks} migration weeks</td><td>{row.assessment}</td>
                            <td>{row.peakWeekByResource?.Jim?.hours || 0}, week {row.peakWeekByResource?.Jim?.week || 0}</td>
                            <td>{row.peakWeekByResource?.["NetSuite functional/data-migration lead"]?.hours || 0}, week {row.peakWeekByResource?.["NetSuite functional/data-migration lead"]?.week || 0}</td>
                            <td>{row.integrationResource?.hours || 0}, week {row.integrationResource?.week || 0}</td>
                            <td>{row.smePeak?.hours || 0}, week {row.smePeak?.week || 0}</td>
                            <td>{row.totalTeamPeak?.hours || 0}, week {row.totalTeamPeak?.week || 0}</td>
                            <td>Technical/integration delegate: {row.requiredNamedAdditionalCapacity?.integrationTechnicalDelegatePeakHours || 0}; functional/data: {row.requiredNamedAdditionalCapacity?.functionalDataLeadPeakHours || 0}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>

                    {readinessPlanAnalysis.crmInclusiveScheduleAudit ? (
                      <details className="readiness-plan-details" open>
                        <summary>CRM-inclusive package-level schedule audit — {readinessPlanAnalysis.crmInclusiveScheduleAudit.shortestSustainableWithModeledTeam}</summary>
                        <div className="readiness-plan-grid readiness-plan-grid--three">
                          <div className="readiness-plan-panel"><h3>Defect found</h3><p>{readinessPlanAnalysis.crmInclusiveScheduleAudit.defectFound}</p></div>
                          <div className="readiness-plan-panel"><h3>Shortest technically achievable</h3><p>{readinessPlanAnalysis.crmInclusiveScheduleAudit.shortestTechnicallyAchievable}</p></div>
                          <div className="readiness-plan-panel"><h3>Recommended operating schedule</h3><p>{readinessPlanAnalysis.crmInclusiveScheduleAudit.recommendedOperatingSchedule}</p></div>
                        </div>
                        <p>{readinessPlanAnalysis.crmInclusiveScheduleAudit.correction}</p>
                        <p><strong>Eight-week condition:</strong> {readinessPlanAnalysis.crmInclusiveScheduleAudit.eightWeekCondition}</p>
                        <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>CRM supplemental task</th><th>Host package</th><th>Old host window</th><th>Revised window</th><th>Hours outside old window</th><th>Move/overlap basis</th></tr></thead><tbody>{(readinessPlanAnalysis.crmInclusiveScheduleAudit.crmMoveLedger || []).map((row) => (
                          <tr key={row.supplementalTask}><td>{row.supplementalTask}</td><td>{row.hostPackageNumber} — {row.hostWorkPackage}</td><td>{row.originalHostWindow}</td><td>{row.revisedWindow}</td><td>{row.hoursScheduledOutsideOriginalHostWindow}</td><td>{row.moveBasis}</td></tr>
                        ))}</tbody></table></div>
                      </details>
                    ) : null}

                    {readinessPlanAnalysis.sevenWeekResourceLeveling ? (
                      <details className="readiness-plan-details" open>
                        <summary>Constrained seven-week resource-leveling test — {readinessPlanAnalysis.sevenWeekResourceLeveling.result}</summary>
                        <div className="readiness-plan-grid readiness-plan-grid--three">
                          <div className="readiness-plan-panel"><h3>Labor and scope control</h3><p><strong>{readinessPlanAnalysis.sevenWeekResourceLeveling.totalLaborBefore} before / {readinessPlanAnalysis.sevenWeekResourceLeveling.totalLaborAfter} after</strong></p><p>Labor difference: {readinessPlanAnalysis.sevenWeekResourceLeveling.laborDifference}. Testing-scope change: {readinessPlanAnalysis.sevenWeekResourceLeveling.testingScopeChange}.</p></div>
                          <div className="readiness-plan-panel"><h3>Jim capacity</h3><p><strong>{readinessPlanAnalysis.sevenWeekResourceLeveling.capacityBefore?.peakByResource?.Jim} before / {readinessPlanAnalysis.sevenWeekResourceLeveling.capacityAfter?.peakByResource?.Jim} after</strong></p><p>Sustainable weekly limit: 50 hours.</p></div>
                          <div className="readiness-plan-panel"><h3>Functional/data capacity</h3><p><strong>{readinessPlanAnalysis.sevenWeekResourceLeveling.capacityBefore?.peakByResource?.["NetSuite functional/data-migration lead"]} before / {readinessPlanAnalysis.sevenWeekResourceLeveling.capacityAfter?.peakByResource?.["NetSuite functional/data-migration lead"]} after</strong></p><p>Sustainable weekly limit: 30 hours.</p></div>
                        </div>
                        <p>{readinessPlanAnalysis.sevenWeekResourceLeveling.gateProtection}</p>
                        <p><strong>Targeted specialist hours still required:</strong> integration {readinessPlanAnalysis.sevenWeekResourceLeveling.targetedIntegrationSpecialistHoursRequired}; functional/data {readinessPlanAnalysis.sevenWeekResourceLeveling.targetedFunctionalDataSpecialistHoursRequired}.</p>
                        <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Week</th><th>Jim before</th><th>Jim after</th><th>Functional/data before</th><th>Functional/data after</th></tr></thead><tbody>{(readinessPlanAnalysis.sevenWeekResourceLeveling.resourceLoadingByWeek || []).map((row) => (
                          <tr key={`seven-week-load-${row.week}`}><td>{row.week}</td><td>{row.jimBefore}</td><td>{row.jimAfter}</td><td>{row.functionalBefore}</td><td>{row.functionalAfter}</td></tr>
                        ))}</tbody></table></div>
                        <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Moved package</th><th>Accountable owner</th><th>Hours moved</th><th>Original week</th><th>Revised week</th><th>Predecessor evidence</th></tr></thead><tbody>{(readinessPlanAnalysis.sevenWeekResourceLeveling.moves || []).map((row, index) => (
                          <tr key={`${row.workPackage}-${row.accountableOwner}-${row.originalWeek}-${row.revisedWeek}-${index}`}><td>{row.workPackage}</td><td>{row.accountableOwner}</td><td>{row.hoursMoved}</td><td>{row.originalWeek}</td><td>{row.revisedWeek}</td><td>{row.predecessorEvidence}</td></tr>
                        ))}</tbody></table></div>
                      </details>
                    ) : null}

                    {(readinessPlanAnalysis.scheduleScenarioTests || []).filter((row) => !row.accepted).map((row) => (
                      <details className="readiness-plan-details" key={`capacity-${row.migrationWeeks}`} open={row.migrationWeeks === 7}>
                        <summary>{row.migrationWeeks}-week {row.assessment?.toLowerCase()} result — exact package, predecessor gate, and resource constraint</summary>
                        <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Resource constraint</th><th>Blocking package</th><th>Hours in constrained week</th><th>Predecessor gate</th></tr></thead><tbody>{(row.violations || []).flatMap((violation) => (violation.blockingPackages || []).map((item, index) => (
                          <tr key={`${row.migrationWeeks}-${violation.resource}-${violation.week}-${item.workPackage}-${index}`}><td><strong>{violation.resource}, week {violation.week}</strong><br />{violation.load} loaded / {violation.sustainableLimit} sustainable ({violation.overage} over)</td><td>{item.workPackage}</td><td>{item.hoursInWeek}</td><td>{item.predecessorGate}</td></tr>
                        )))}</tbody></table></div>
                      </details>
                    ))}

                    <details className="readiness-plan-details">
                      <summary>Known-exception reclassifications ({(readinessPlanAnalysis.knownExceptionReclassifications || []).length})</summary>
                      <div className="readiness-plan-table-wrap"><table className="readiness-plan-table"><thead><tr><th>Work package</th><th>Change</th><th>Risk probability</th><th>Full risk impact</th><th>Weighted reserve</th></tr></thead><tbody>{(readinessPlanAnalysis.knownExceptionReclassifications || []).map((row) => <tr key={row.workPackage}><td>{row.workPackage}</td><td>{row.change}</td><td>{Math.round(Number(row.riskProbability || 0) * 100)}%</td><td>{row.revisedFullRiskImpact}</td><td>{row.revisedRiskReserve}</td></tr>)}</tbody></table></div>
                    </details>

                    <div className="readiness-status-grid readiness-plan-metrics">
                      {[
                        ["Original baseline", `${readinessPlanAnalysis.originalBaselineHours} hrs`],
                        ["CLI-AI-v2", `${readinessPlanAnalysis.v2TotalHours} hrs`],
                        ["v3 base execution", `${readinessPlanAnalysis.v3BaseExecutionLabor} hrs`],
                        ["Known exceptions", `${readinessPlanAnalysis.v3KnownExceptionLabor} hrs`],
                        ["Risk reserve", `${readinessPlanAnalysis.v3RiskReserve} hrs`],
                        ["v3 most likely", `${readinessPlanAnalysis.v3MostLikelyLabor} hrs`],
                        ["Full risk exposure", `${readinessPlanAnalysis.v3FullRiskExposure} hrs`],
                        ["Additional conservative reserve", `${readinessPlanAnalysis.v3IncrementalConservativeReserve} hrs`],
                        ["v3 conservative", `${readinessPlanAnalysis.v3ConservativeLabor} hrs`],
                        ["Automated elapsed", `${readinessPlanAnalysis.automatedElapsedHours} hrs`],
                        ["Vendor/wait elapsed", `${readinessPlanAnalysis.vendorWaitingElapsedHours} hrs`],
                        ["Malta-critical factory", `${readinessPlanAnalysis.maltaCriticalFactoryHours} hrs`],
                        ["Deferred factory", `${readinessPlanAnalysis.deferredFactoryDevelopmentHours} hrs`],
                        ["Checklist links", `${readinessPlanAnalysis.normalizationAuditRows} / ${readinessPlanAnalysis.originalBaselineTasks}`]
                      ].map(([label, value]) => (
                        <div className="readiness-status-card" key={label}><div className="readiness-status-card__label">{label}</div><div className="readiness-status-card__value">{value}</div></div>
                      ))}
                    </div>

                    <div className="readiness-plan-grid">
                      <div className="readiness-plan-panel"><h3>Review and approval</h3><p><strong>v2:</strong> {readinessPlanAnalysis.v2ReviewApprovalHours} hrs</p><p><strong>v3 retained:</strong> {readinessPlanAnalysis.v3ReviewApprovalHours} hrs</p><p><strong>Consolidated/removed:</strong> {readinessPlanAnalysis.reviewApprovalHoursConsolidated} hrs</p></div>
                      <div className="readiness-plan-panel"><h3>Troubleshooting and reserve</h3><p><strong>v2 guaranteed:</strong> {readinessPlanAnalysis.v2TroubleshootingHours} hrs</p><p><strong>Known exceptions:</strong> {readinessPlanAnalysis.knownTroubleshootingHours} hrs</p><p><strong>Probability-weighted reserve:</strong> {readinessPlanAnalysis.probabilityWeightedTroubleshootingReserve} hrs</p></div>
                      <div className="readiness-plan-panel"><h3>Project control</h3><p><strong>v2:</strong> {readinessPlanAnalysis.v2ProjectControlHours} hrs</p><p><strong>v3 weekly/gate control:</strong> {readinessPlanAnalysis.v3ProjectControlHours} hrs</p><p><strong>Reduced:</strong> {readinessPlanAnalysis.projectControlHoursReduced} hrs</p></div>
                      <div className="readiness-plan-panel"><h3>Core resource totals</h3><ul>{Object.entries(readinessPlanAnalysis.resourceHours || {}).map(([role, hours]) => <li key={role}><strong>{role}:</strong> {hours} hrs</li>)}</ul></div>
                    </div>

                    <div className="readiness-plan-table-wrap">
                      <table className="readiness-plan-table">
                        <thead><tr><th>Scenario</th><th>Migration</th><th>Hypercare</th><th>Rating</th><th>Confidence</th><th>Labor</th><th>Resources / condition</th><th>Remaining bottlenecks</th></tr></thead>
                        <tbody>{(readinessPlanAnalysis.scenarios || []).map((row) => (
                          <tr key={row.scenario}><td><strong>{row.scenario}</strong></td><td>{row.migrationDates}</td><td>{row.hypercareDates}</td><td>{row.rating}</td><td>{row.confidence}</td><td>{row.laborHours}</td><td>{row.resources}<br />{row.condition}<br /><strong>CRM boundary:</strong> {row.estimateScopeStatement}</td><td>{(row.bottlenecks || []).join("; ") || "No core-capacity breach in modeled weeks"}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>

                    <div className="readiness-plan-table-wrap">
                      <table className="readiness-plan-table">
                        <thead><tr><th>Week</th><th>Jim</th><th>Functional/data lead</th><th>Total team</th></tr></thead>
                        <tbody>{(readinessPlanAnalysis.teamHoursByWeek || []).map((row) => (
                          <tr key={row.week}><td>{row.week}</td><td>{readinessPlanAnalysis.weeklyHoursByResource?.Jim?.[row.week] || 0}</td><td>{readinessPlanAnalysis.weeklyHoursByResource?.["NetSuite functional/data-migration lead"]?.[row.week] || 0}</td><td><strong>{row.hours}</strong></td></tr>
                        ))}</tbody>
                      </table>
                    </div>

                    <details className="readiness-plan-details" open>
                      <summary>Execution-session calibration sample ({readinessPlanAnalysis.calibrationSampleSize} work packages)</summary>
                      <div className="readiness-plan-table-wrap">
                        <table className="readiness-plan-table">
                          <thead><tr><th>Work package</th><th>Baseline IDs</th><th>v2</th><th>v2 prep</th><th>v2 review</th><th>v2 troubleshooting</th><th>v2 approval</th><th>Session design</th><th>Sessions</th><th>Shared setup</th><th>Shared gate</th><th>Base</th><th>Known</th><th>Risk reserve</th><th>Full risk</th><th>Auto</th><th>Wait</th><th>v3</th><th>Difference</th><th>Confidence</th></tr></thead>
                          <tbody>{(readinessPlanAnalysis.calibrationSample || []).map((row) => (
                            <tr key={row.workPackage}><td>{row.workPackage}</td><td>{(row.linkedBaselineTaskIds || []).join(", ")}</td><td>{row.v2EstimateHours}</td><td>{row.v2PreparationHours}</td><td>{row.v2ReviewHours}</td><td>{row.v2TroubleshootingHours}</td><td>{row.v2ApprovalHours}</td><td>{row.executionSessionDesign}</td><td>{row.distinctWorkSessions}</td><td>{row.sharedSetupActivities}</td><td>{row.sharedReviewApprovalGate}</td><td>{row.baseExecutionHours}</td><td>{row.knownExceptionHours}</td><td>{row.probabilityWeightedRiskReserve}</td><td>{row.fullRiskExposureHours}</td><td>{row.automatedElapsedHours}</td><td>{row.waitingVendorElapsedHours}</td><td>{row.v3HumanHours}</td><td>{row.differenceFromV2}</td><td>{row.confidence}</td></tr>
                          ))}</tbody>
                        </table>
                      </div>
                    </details>

                    <details className="readiness-plan-details">
                      <summary>Estimate review triggers and explicit justifications ({(readinessPlanAnalysis.estimateReviewTriggers || []).length})</summary>
                      <div className="readiness-plan-table-wrap">
                        <table className="readiness-plan-table">
                          <thead><tr><th>Work package</th><th>Review trigger</th><th>Explicit justification</th></tr></thead>
                          <tbody>{(readinessPlanAnalysis.estimateReviewTriggers || []).map((row, index) => <tr key={`${row.workPackage}-${row.trigger}-${index}`}><td>{row.workPackage}</td><td>{row.trigger}</td><td>{row.justification}</td></tr>)}</tbody>
                        </table>
                      </div>
                    </details>

                    <div className="readiness-plan-grid">
                      {[
                        ["1. Did v2 overestimate by estimating baseline tasks first?", "v2OverestimatedByBaselineFirst"],
                        ["2. How much review and approval was duplicated?", "duplicatedReviewApproval"],
                        ["3. How much troubleshooting was guaranteed?", "guaranteedTroubleshooting"],
                        ["4. How much project-control labor was unnecessary?", "unnecessaryProjectControl"],
                        ["5. How much factory work moves after go-live?", "factoryAfterGoLive"],
                        ["6. What is the true base labor?", "trueBaseLabor"],
                        ["7. What contingency stays separate?", "separateContingency"],
                        ["8. Can the named core team finish in six weeks?", "sixWeekAnswer"],
                        ["9. What exact bottleneck prevents six weeks?", "exactSixWeekBottleneck"],
                        ["10. What is the shortest sustainable schedule with the currently named team and sequencing?", "shortestHonestSchedule"],
                        ["11. What would make seven migration weeks feasible?", "sevenWeekAcceleration"],
                        ["12. What CRM execution labor is included in the current estimate?", "crmEstimateBoundary"]
                      ].map(([question, key]) => <div className="readiness-plan-panel" key={key}><h3>{question}</h3><p>{readinessPlanAnalysis.finalAssessment?.[key]}</p></div>)}
                    </div>
                  </BlockStack>
                </Card>
              </Layout.Section>
            ) : null}

            <Layout.Section variant="fullWidth">
              <Card sectioned>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" wrap={true}>
                    <Text variant="headingMd" as="h2">Lifecycle</Text>
                    {readinessSaving ? (
                      <Text variant="bodySm" tone="subdued">Saving...</Text>
                    ) : null}
                  </InlineStack>
                  <div className="readiness-lifecycle-strip">
                    {readinessLifecycle.map((stage) => (
                      <div className="readiness-lifecycle-card" key={stage.id}>
                        <div className="readiness-lifecycle-card__top">
                          <div className="readiness-lifecycle-stage">{stage.stage_name}</div>
                          <span className={buildBadgeClass("readiness-lifecycle-badge", stage.status)}>
                            {stage.status}
                          </span>
                        </div>
                        <Select
                          label="Status"
                          labelHidden
                          options={READINESS_LIFECYCLE_STATUS_OPTIONS.map((value) => ({ label: value, value }))}
                          value={stage.status || "Not Started"}
                          onChange={(value) => saveReadinessLifecycleRow(stage, { status: value })}
                        />
                        <textarea
                          className="readiness-lifecycle-summary"
                          value={stage.summary || ""}
                          title={stage.summary || ""}
                          rows={3}
                          onChange={(event) => {
                            const nextSummary = event.target.value;
                            setReadinessLifecycle((current) => current.map((item) => (
                              item.id === stage.id ? { ...item, summary: nextSummary } : item
                            )));
                          }}
                          onBlur={(event) => saveReadinessLifecycleRow(stage, { summary: event.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section variant="fullWidth">
              <Card sectioned>
                <BlockStack gap="250">
                  <Text variant="headingMd" as="h2">Evidence Summary</Text>
                  <div className="readiness-evidence-summary">
                    {readinessEvidenceSummary.map(([label, value]) => (
                      <div className="readiness-evidence-summary__item" key={label} title={String(value || "")}>
                        <div className="readiness-evidence-summary__label">{label}</div>
                        <div className="readiness-evidence-summary__value">
                          {label === "Status" ? (
                            <span className={buildBadgeClass("readiness-badge", value)}>{value}</span>
                          ) : value}
                        </div>
                      </div>
                    ))}
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section variant="fullWidth">
              <Card sectioned>
                <BlockStack gap="250">
                  <InlineStack align="space-between" blockAlign="center" wrap={true}>
                    <BlockStack gap="050">
                      <Text variant="headingMd" as="h2">AI Readiness Context</Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        Structured context contract for readiness analysis.
                      </Text>
                    </BlockStack>
                    <InlineStack gap="200" wrap={true}>
                      <Button
                        onClick={() => {
                          const nextOpen = !readinessAiContextOpen;
                          setReadinessAiContextOpen(nextOpen);
                          if (nextOpen && readinessProject?.id) fetchReadinessAiContext(readinessProject.id);
                        }}
                      >
                        {readinessAiContextOpen ? "Hide" : "Preview"}
                      </Button>
                      <Button
                        onClick={copyReadinessAiContextJson}
                        icon={ClipboardIcon}
                        disabled={!readinessAiContext}
                        loading={readinessAiContextLoading}
                      >
                        Copy AI Context
                      </Button>
                      <Button
                        onClick={copyReadinessAiBrief}
                        icon={ClipboardIcon}
                        disabled={!readinessAiContext}
                        loading={readinessAiContextLoading}
                      >
                        Copy AI Brief
                      </Button>
                    </InlineStack>
                  </InlineStack>

                  {readinessAiContextOpen ? (
                    <div className="readiness-ai-context-panel">
                      <div className="readiness-ai-health-strip">
                        <div className={`readiness-ai-health-pill readiness-ai-health-pill--${readinessAiHealthLabel}`}>
                          <span>AI Context Health</span>
                          <strong>{readinessAiHealthLabel}</strong>
                        </div>
                        <div>
                          <span>Version</span>
                          <strong>{readinessAiContext?.aiContextVersion || "-"}</strong>
                        </div>
                        <div>
                          <span>Generated</span>
                          <strong>{readinessAiContext?.generatedAt ? new Date(readinessAiContext.generatedAt).toLocaleString() : "-"}</strong>
                        </div>
                        <div>
                          <span>Contract</span>
                          <strong>{readinessAiContractHealth.message || "Loading..."}</strong>
                        </div>
                      </div>

                      <div className="readiness-ai-context-meta">
                        <div>
                          <span>Project</span>
                          <strong>{readinessAiContext?.project?.projectCode || readinessProject?.project_code || "-"} / {readinessAiContext?.project?.projectName || readinessProject?.project_name || "-"}</strong>
                        </div>
                        <div>
                          <span>Status</span>
                          <strong>{readinessAiContext?.project?.overallStatus || readinessProject?.overall_status || "-"}</strong>
                        </div>
                        <div>
                          <span>Phase</span>
                          <strong>{readinessAiContext?.project?.currentPhase || readinessProject?.current_phase || "-"}</strong>
                        </div>
                        <div>
                          <span>Next Milestone</span>
                          <strong>{readinessAiContext?.project?.nextMilestone || readinessProject?.next_milestone || "-"}</strong>
                        </div>
                      </div>

                      <div className="readiness-ai-brief">
                        <div className="readiness-ai-context-block readiness-ai-context-block--wide">
                          <h3>Current Readiness State</h3>
                          <p>{readinessAiBrief.currentReadinessState || readinessAiContext?.readinessAssessment?.summary || "Context loading..."}</p>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Top Blockers</h3>
                          <ul>{(readinessAiBrief.topBlockers || []).slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Leadership Questions</h3>
                          <ul>{(readinessAiBrief.questionsLeadershipShouldAsk || []).slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                      </div>

                      <div className="readiness-sprint-focus">
                        <div className="readiness-ai-context-block readiness-ai-context-block--wide">
                          <h3>MD-EDI Sprint Focus</h3>
                          <ol>
                            {readinessSprintPriorities.slice(0, 9).map((item) => (
                              <li key={item.rank}>
                                <strong>{item.title}</strong>
                                <span className={buildBadgeClass("readiness-badge", item.status)}>{item.status}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        <div className="readiness-ai-context-block readiness-ai-context-block--wide">
                          <h3>Definition of Done</h3>
                          <ul>{(readinessSprintFocus.definitionOfDone || []).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                      </div>

                      <div className="readiness-ai-context-grid">
                        <div className="readiness-ai-context-block">
                          <h3>Lifecycle Summary</h3>
                          <p>
                            Complete {readinessAiLifecycleSummary.complete ?? 0} / In Progress {readinessAiLifecycleSummary.inProgress ?? 0} / Blocked {readinessAiLifecycleSummary.blocked ?? 0} / Not Started {readinessAiLifecycleSummary.notStarted ?? 0}
                          </p>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Contract Health</h3>
                          <p>
                            {readinessAiContractHealth.ok ? "OK" : "Pending"} / {readinessAiContractHealth.generatedInMs ?? 0}ms / {readinessAiContractHealth.responseBytes ?? 0} bytes
                          </p>
                        </div>
                        <div className="readiness-ai-context-block readiness-ai-context-block--wide">
                          <h3>AI Summary</h3>
                          <p>{readinessAiContext?.readinessAssessment?.summary || "Context loading..."}</p>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Blocked Items</h3>
                          <ul>{(readinessAiBlockedItems.length ? readinessAiBlockedItems : [{ title: "No blocked items marked." }]).slice(0, 5).map((item) => <li key={item.id || item.title}>{item.title}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Critical Open</h3>
                          <ul>{(readinessAiCriticalOpenItems.length ? readinessAiCriticalOpenItems : [{ title: "No critical open items." }]).slice(0, 5).map((item) => <li key={item.id || item.title}>{item.title}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>High Open</h3>
                          <ul>{(readinessAiHighOpenItems.length ? readinessAiHighOpenItems : [{ title: "No high-priority open items." }]).slice(0, 5).map((item) => <li key={item.id || item.title}>{item.title}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Top Risks</h3>
                          <ul>{readinessAiTopRisks.slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Next Actions</h3>
                          <ul>{readinessAiNextActions.slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Known Exclusions</h3>
                          <ul>{readinessAiKnownExclusions.slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Assumptions</h3>
                          <ul>{readinessAiAssumptions.slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                        <div className="readiness-ai-context-block">
                          <h3>Recommended Questions</h3>
                          <ul>{readinessAiRecommendedQuestions.slice(0, 5).map((item) => <li key={item}>{item}</li>)}</ul>
                        </div>
                      </div>
                      {readinessAiContextFeedback ? (
                        <div className="readiness-ai-context-feedback">{readinessAiContextFeedback}</div>
                      ) : null}
                    </div>
                  ) : null}
                </BlockStack>
              </Card>
            </Layout.Section>

            {readinessProjectCode === PROP65_PROJECT_CODE ? (
              <Layout.Section variant="fullWidth">
                <Card sectioned>
                  <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" wrap={true}>
                    <BlockStack gap="050">
                      <Text variant="headingMd" as="h2">Prop 65 Mini-Audit</Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        Prop 65 warning coverage review, evidence gaps, and legal review status.
                      </Text>
                    </BlockStack>
                    <InlineStack gap="200" wrap={true}>
                      <Button onClick={addReadinessComplianceAudit} icon={PlusIcon} loading={readinessComplianceSaving}>
                        Add Finding
                      </Button>
                      <Button onClick={() => fetchReadinessBoard()} icon={RefreshIcon} loading={readinessLoading}>
                        Refresh Board
                      </Button>
                    </InlineStack>
                  </InlineStack>

                  <div className="readiness-status-grid">
                    {[
                      ["Findings", readinessComplianceAudits.length],
                      ["High Risk", readinessComplianceHighRiskCount],
                      ["Needs Legal Review", readinessComplianceLegalReviewCount],
                      ["Supplier Info Needed", readinessComplianceAudits.filter((audit) => String(audit.status || "").toLowerCase().includes("supplier info needed")).length],
                      ["Product Page Update Needed", readinessComplianceAudits.filter((audit) => String(audit.status || "").toLowerCase().includes("product page update needed")).length]
                    ].map(([label, value]) => (
                      <div className="readiness-status-card" key={label}>
                        <div className="readiness-status-card__label">{label}</div>
                        <div className="readiness-status-card__value">{value}</div>
                      </div>
                    ))}
                  </div>

                  <div className="readiness-controls">
                    <Select
                      label="Compliance Category"
                      options={buildReadinessOptions(readinessComplianceCategories, "All categories")}
                      value={readinessComplianceFilters.category}
                      onChange={(value) => setReadinessComplianceFilters((current) => ({ ...current, category: value }))}
                    />
                    <Checkbox
                      label="Prop 65 only"
                      checked={Boolean(readinessComplianceFilters.prop65Only)}
                      onChange={(checked) => setReadinessComplianceFilters((current) => ({ ...current, prop65Only: checked }))}
                    />
                    <Checkbox
                      label="Needs Legal Review"
                      checked={Boolean(readinessComplianceFilters.needsLegalReview)}
                      onChange={(checked) => setReadinessComplianceFilters((current) => ({ ...current, needsLegalReview: checked }))}
                    />
                    <Checkbox
                      label="Supplier Info Needed"
                      checked={Boolean(readinessComplianceFilters.supplierInfoNeeded)}
                      onChange={(checked) => setReadinessComplianceFilters((current) => ({ ...current, supplierInfoNeeded: checked }))}
                    />
                    <Checkbox
                      label="Product Page Update Needed"
                      checked={Boolean(readinessComplianceFilters.productPageUpdateNeeded)}
                      onChange={(checked) => setReadinessComplianceFilters((current) => ({ ...current, productPageUpdateNeeded: checked }))}
                    />
                    <Checkbox
                      label="High Risk"
                      checked={Boolean(readinessComplianceFilters.highRisk)}
                      onChange={(checked) => setReadinessComplianceFilters((current) => ({ ...current, highRisk: checked }))}
                    />
                  </div>

                  {readinessComplianceError ? (
                    <Banner status="critical">{readinessComplianceError}</Banner>
                  ) : null}

                  <div className="readiness-table-wrap" aria-busy={readinessComplianceSaving}>
                    {readinessComplianceSaving ? (
                      <Text variant="bodySm" tone="subdued">Saving Prop 65 finding...</Text>
                    ) : null}
                    <div ref={readinessComplianceAuditsTableRef} className="readiness-tabulator readiness-tabulator--compliance" />
                  </div>
                  </BlockStack>
                </Card>
              </Layout.Section>
            ) : null}

            <Layout.Section variant="fullWidth">
              <Card sectioned>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" wrap={true}>
                    <BlockStack gap="050">
                      <Text variant="headingMd" as="h2">Checklist</Text>
                    </BlockStack>
                    {readinessSaving ? (
                      <Text variant="bodySm" tone="subdued">Saving...</Text>
                    ) : null}
                  </InlineStack>

                  <div className="readiness-controls">
                    <Select
                      label="Lane"
                      options={buildReadinessOptions(READINESS_LANE_OPTIONS, "All lanes")}
                      value={readinessFilters.lane}
                      onChange={(value) => setReadinessFilters((current) => ({ ...current, lane: value }))}
                    />
                    <Select
                      label="Status"
                      options={buildReadinessOptions(READINESS_STATUS_OPTIONS, "All statuses")}
                      value={readinessFilters.status}
                      onChange={(value) => setReadinessFilters((current) => ({ ...current, status: value }))}
                    />
                    <Select
                      label="Owner"
                      options={buildReadinessOptions(readinessOwners, "All owners")}
                      value={readinessFilters.owner}
                      onChange={(value) => setReadinessFilters((current) => ({ ...current, owner: value }))}
                    />
                    <Select
                      label="Category"
                      options={buildReadinessOptions(readinessCategories, "All categories")}
                      value={readinessFilters.category}
                      onChange={(value) => setReadinessFilters((current) => ({ ...current, category: value }))}
                    />
                  </div>

                  {readinessError ? (
                    <Banner status="critical">{readinessError}</Banner>
                  ) : null}

                  <div className="readiness-table-wrap" aria-busy={readinessLoading}>
                    {readinessLoading ? (
                      <Text variant="bodySm" tone="subdued">Loading Readiness Board...</Text>
                    ) : null}
                    <div
                      ref={readinessItemsTableRef}
                      className={`readiness-tabulator readiness-tabulator--items${isAnyMaltaNetSuiteProject ? " readiness-tabulator--migration" : ""}`}
                    />
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section variant="fullWidth">
              <Card sectioned>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" wrap={true}>
                    <BlockStack gap="050">
                      <Text variant="headingMd" as="h2">Evidence / Artifacts</Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        Captured source documents, references, and certification artifacts.
                      </Text>
                    </BlockStack>
                  </InlineStack>
                  <div className="readiness-table-wrap">
                    <div ref={readinessArtifactsTableRef} className="readiness-tabulator readiness-tabulator--artifacts" />
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </AppProvider>
    );
  }

  if (isSystemsInventoryPage) {
    return (
      <AppProvider>
        <Page fullWidth>
          <Layout>
            <Layout.Section variant="fullWidth">
              <InlineStack align="space-between" blockAlign="center" wrap={true}>
                <BlockStack gap="050">
                  <Text variant="headingLg" as="h1">Systems Inventory</Text>
                  <Text variant="bodyMd" tone="subdued" as="p">
                    Malta custom scripts, integrations, EDI logic, Shopify components, and migration notes.
                  </Text>
                </BlockStack>
                <Button onClick={() => { window.location.href = "/"; }}>
                  Back to Dashboard
                </Button>
              </InlineStack>
            </Layout.Section>

            <Layout.Section variant="fullWidth">
              <Card sectioned>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center" wrap={true}>
                    <div className="systems-inventory-controls systems-inventory-controls--page">
                      <input
                        value={systemsInventorySearch}
                        onChange={(event) => setSystemsInventorySearch(event.target.value)}
                        placeholder="Search systems inventory..."
                        className="systems-inventory-search"
                      />
                      <Select
                        label="Category"
                        options={systemsInventorySelectOptions(systemsInventoryFilterOptions.categories, "All categories")}
                        value={systemsInventoryFilters.category || ""}
                        onChange={(value) => updateSystemsInventoryFilter("category", value)}
                      />
                      <Select
                        label="Platform"
                        options={systemsInventorySelectOptions(systemsInventoryFilterOptions.platforms, "All platforms")}
                        value={systemsInventoryFilters.platform || ""}
                        onChange={(value) => updateSystemsInventoryFilter("platform", value)}
                      />
                      <Select
                        label="Status"
                        options={systemsInventorySelectOptions(systemsInventoryFilterOptions.statuses, "All statuses")}
                        value={systemsInventoryFilters.status || ""}
                        onChange={(value) => updateSystemsInventoryFilter("status", value)}
                      />
                      <Select
                        label="Risk"
                        options={systemsInventorySelectOptions(systemsInventoryFilterOptions.riskLevels, "All risk levels")}
                        value={systemsInventoryFilters.risk_level || ""}
                        onChange={(value) => updateSystemsInventoryFilter("risk_level", value)}
                      />
                      <Select
                        label="Vendor Bundle"
                        options={[
                          { label: "All", value: "" },
                          { label: "Custom", value: "0" },
                          { label: "Vendor Bundle", value: "1" }
                        ]}
                        value={systemsInventoryFilters.vendor_bundle ?? ""}
                        onChange={(value) => updateSystemsInventoryFilter("vendor_bundle", value)}
                      />
                    </div>
                    <InlineStack gap="200" wrap={true}>
                      <Button onClick={downloadVisibleSystemsInventoryCsv}>
                        Download CSV
                      </Button>
                      <Button variant="primary" onClick={openSystemsInventoryAddModal}>
                        Add New Inventory Item
                      </Button>
                    </InlineStack>
                  </InlineStack>

                  {systemsInventoryError ? (
                    <Banner status="critical">{systemsInventoryError}</Banner>
                  ) : null}

                  <div className="systems-inventory-table-wrap systems-inventory-table-wrap--page" aria-busy={systemsInventoryLoading}>
                    {systemsInventoryLoading ? (
                      <Text variant="bodySm" tone="subdued">Loading Systems Inventory...</Text>
                    ) : null}
                    <div ref={systemsInventoryTableRef} className="systems-inventory-tabulator" />
                  </div>
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>

          <SystemsInventoryDetailsModal
            item={systemsInventoryDetailsItem}
            onClose={() => setSystemsInventoryDetailsItem(null)}
          />
          <SystemsInventoryFormModal
            open={systemsInventoryFormOpen}
            mode={systemsInventoryFormMode}
            draft={systemsInventoryFormDraft}
            saving={systemsInventorySaving}
            onClose={() => setSystemsInventoryFormOpen(false)}
            onSave={saveSystemsInventoryItem}
            onChange={updateSystemsInventoryDraft}
          />
        </Page>
      </AppProvider>
    );
  }

  return (
    <AppProvider>
      <Page
        fullWidth
      >



        {!storeConnected && (
          <Banner status="critical" title="Store not connected">
            <p>
              Connect your Shopify store to see sales data.{" "}
              <a href={`/auth?shop=${process.env.REACT_APP_SHOPIFY_STORE}`} style={{ color: "#008060", textDecoration: "underline" }}>
                Connect Store
              </a>
            </p>
          </Banner>
        )}
        <Layout>
          <Layout.Section variant="fullWidth">
            <div className="tops-row">
              <div className="tops-row-left">
                <span className="tops-sales-title">
                  {selectedMonthLabel} Sales Goal {formatCurrency(monthlyGoal)}
                </span>
                {environment !== "production" ? (
                  <span className="tops-dev-badge">DEV</span>
                ) : null}
              </div>
              <div className="tops-row-center">
                <div className="tops-title-stack">
                  <span className="tops-main-title">TECH OPS</span>
                  <span className="tops-subtitle">
                    Lightweight operational governance framework, verification tracking, status aging, and exception visibility across critical business systems.
                  </span>
                </div>
              </div>
              <div className="tops-row-right">
                <div className="tops-actions-stack">
                  <span className="tops-user-greeting">{dashboardUserGreeting}</span>
                  <div className="tops-actions-row">
                    <Button size="slim" onClick={handleRefresh} loading={isRefreshing}>
                      {refreshLabel}
                    </Button>
                    <Button size="slim" onClick={() => setForecastMethodologyModalOpen(true)}>
                      Forecasting Methodology
                    </Button>
                    <Button size="slim" onClick={() => { window.location.href = "/readiness-board"; }}>
                      Readiness Board
                    </Button>
                    <Button
                      size="slim"
                      icon={ClipboardIcon}
                      onClick={handleCopyAiContext}
                      loading={isCopyingAiContext}
                      accessibilityLabel="Copy AI representation JSON to clipboard"
                    />
                  </div>
                  {aiCopyFeedback ? (
                    <span className={`tops-copy-feedback tops-copy-feedback--${aiCopyFeedback.tone}`}>
                      {aiCopyFeedback.message}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <Text variant="bodySm" tone="subdued" as="p" style={{ marginTop: 4 }}>
              The Road to a Million Dollars
            </Text>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <div className="sales-view-controls">
              <div className="dashboard-month-switcher">
                <Select
                  label="Dashboard month"
                  options={DASHBOARD_MONTH_OPTIONS}
                  value={selectedMonth}
                  onChange={handleSelectedMonthChange}
                />
              </div>
              <fieldset className="sales-store-scope" aria-label="Sales store">
                <legend>Sales store</legend>
                <div className="sales-store-scope__options">
                  {SALES_STORE_SCOPE_OPTIONS.map((option) => (
                    <label key={option.value} className="sales-store-scope__option">
                      <input
                        type="radio"
                        name="sales-store-scope"
                        value={option.value}
                        checked={salesStoreScope === option.value}
                        onChange={() => handleSalesStoreScopeChange(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              {scopedSalesLoading ? (
                <span className="sales-store-scope__status">Updating sales…</span>
              ) : null}
            </div>
            {scopedSalesError ? (
              <div className="sales-store-scope__error">
                <Banner status="warning" title={scopedSalesError.message}>
                  {scopedSalesError.connectUrl ? (
                    <p>
                      <a href={scopedSalesError.connectUrl}>Connect the Hunting store</a> to load its sales totals.
                    </p>
                  ) : (
                    <p>Refresh the dashboard or select MD only.</p>
                  )}
                </Banner>
              </div>
            ) : null}
          </Layout.Section>

          {/* 4 Metrics Section */}
          <Layout.Section variant="fullWidth">
            <div className="metrics-four-up">
              <Card sectioned>
                <MetricTile
                  label="Yesterday's Net Sales"
                  value={formatCurrency(displayedSalesYesterday)}
                  subValue={`Goal: ${formatCurrency(goalYesterday)}`}

                  sideLabel="Placeholder"
                  sideValue={`${goalYesterday > 0 ? ((displayedSalesYesterday / goalYesterday) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(displayedSalesMTD)}
                  sideTone={
                    goalYesterday > 0 && displayedSalesYesterday / goalYesterday >= 1
                      ? "success"
                      : goalYesterday > 0 && displayedSalesYesterday / goalYesterday >= 0.8
                        ? "caution"
                        : "critical"
                  }
                />
              </Card>
              <Card sectioned>
                <MetricTile
                  label="Sales Month To Date"
                  value={formatCurrency(displayedSalesMTD)}
                  subValue={`MTD Target. Should have been: ${formatCurrency(planMTD)} by yesterday.`}

                  sideLabel="MTD Trending"
                  sideValue={`${displayedMtdPct.toFixed(1)}%`}
                  sideSubValue={formatCurrency(planMTD > 0 ? monthlyGoal * (displayedSalesMTD / planMTD) : 0)}
                  sideTone={displayedMtdTone}
                />
              </Card>

              <Card sectioned>
                <MetricTile
                  label={selectedDaySalesLabel}
                  value={formatCurrency(displayedTodaySales)}
                  subValue={`${selectedDayGoalLabel}: ${formatCurrency(todaysGoal)}`}
                  sideLabel={selectedDayPaceLabel}
                  sideValue={`${displayedIntradayPacePct.toFixed(1)}%`}
                  sideSubValue={intradayPacing ? `Expected: ${formatCurrency(intradayExpectedSales)} (${intradayExpectedPct.toFixed(1)}%) by now.` : formatCurrency(displayedSalesMTD)}
                  sideTone={intradayTone}
                />
              </Card>

              <Card sectioned>
                <MetricTile
                  label="Sales Year To Date"
                  value={`${formatCurrency(displayedYearToDate)}`}
                  subValue={`YTD Target: ${formatCurrency(yearlyStats?.ytdPlan)}`}
                  sideLabel="Pace"
                  sideValue={`${(yearlyStats?.ytdPlan ?? 0) > 0 ? ((displayedYearToDate / yearlyStats.ytdPlan) * 100).toFixed(1) : 0}%`}
                  sideSubValue={`${formatCurrency(displayedProjectedYearEnd)} (${formatCurrency(yearlyStats?.goal)})`}
                  sideTone={(yearlyStats?.ytdPlan ?? 0) > 0 && displayedYearToDate >= yearlyStats.ytdPlan ? "success" : "critical"}
                />
              </Card>
            </div>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <div className="webops-strip" aria-label="WebOps: Mission-critical website operations">
              <PolarisTooltip content="WebOps operational controls are monitored at predetermined best-practice intervals with measurable verification cadence, status aging, and exception visibility.">
                <Text variant="bodySm" as="span" className="webops-strip-label">
                  <span className="webops-strip-label-text">
                    <GlossaryText text="WEB OPS" terms={glossaryTermsMap} />
                  </span>
                </Text>
              </PolarisTooltip>
              <div className="webops-strip-row">
                {(webopsItems.length ? webopsItems : WEBOPS_ITEMS).map((item) => {
                  const timing = getWebopsTimingState(item);
                  const status = timing.effectiveStatus;
                  const lampOverrideStatus = webopsLampOverrides[item.key];
                  const renderedStatus = webopsLampBlinkOff ? "off" : (lampOverrideStatus || status);
                  const ringClass = (webopsLampBlinkOff || lampOverrideStatus)
                    ? "webops-status-dot--lamp-test"
                    : `webops-status-dot--${timing.ringState}`;
                  const statusLabel = WEBOPS_STATUS_LABELS[status] || status;
                  const cadenceLabel = formatWebopsCadence(item?.checkFrequencyHours);
                  const lastCheckedLabel = formatWebopsDate(item?.lastChecked);
                  const dueLabel = timing.dueAtIso ? formatWebopsDate(timing.dueAtIso) : "Not scheduled";
                  const glossaryKey = String(item?.name || item?.title || "").toLowerCase();
                  const glossaryDefinition = glossaryTermsMap?.[glossaryKey];
                  const definitionContent = glossaryDefinition
                    ? renderGlossaryTooltipContent(glossaryDefinition)
                    : (item.description || item.definition || "");
                  const detailsText = item?.details ? `\n\nLatest check: ${item.details}` : "";
                  const metaLines = [
                    `Cadence: ${cadenceLabel}`,
                    `Last Checked: ${lastCheckedLabel}`,
                    `Next Due: ${dueLabel}`,
                    `Check Type: ${item.checkType || "manual"}`,
                    `Status: ${statusLabel}`,
                    item?.details ? `Latest Check: ${item.details}` : "",
                    "Tip: Double-click the status dot to mark checked."
                  ].filter(Boolean).join("\n");
                  const tooltipContent = typeof definitionContent === "string"
                    ? (
                      <div>
                        <div style={{ whiteSpace: "pre-line" }}>{`${definitionContent}${detailsText}`}</div>
                        <div style={{ marginTop: 8, fontSize: 12, whiteSpace: "pre-line" }}>{metaLines}</div>
                        {item.key === "contentMerchandising" ? (
                          <div style={{ marginTop: 8, fontSize: 12 }}>Tip: Click the label to open checklist.</div>
                        ) : null}
                      </div>
                    )
                    : (
                      <div>
                        <div>{definitionContent}</div>
                        <div style={{ marginTop: 8, fontSize: 12, whiteSpace: "pre-line" }}>{metaLines}</div>
                        {item.key === "contentMerchandising" ? (
                          <div style={{ marginTop: 8, fontSize: 12 }}>Tip: Click the label to open checklist.</div>
                        ) : null}
                      </div>
                    );

                  return (
                    <div key={item.key} className="webops-item">
                      <button
                        type="button"
                        className={`webops-status-dot webops-status-dot--${renderedStatus} ${ringClass}`}
                        aria-label={`${item.name || item.title}: ${statusLabel}`}
                        title={`${statusLabel} (click to change)`}
                        onClick={() => handleWebopsStatusClick(item)}
                        onDoubleClick={() => handleWebopsStatusDoubleClick(item)}
                      />
                      <PolarisTooltip content={tooltipContent}>
                        <span
                          className="webops-item-title"
                          style={{
                            ...glossaryTermTextStyle,
                            cursor: item.key === "contentMerchandising" ? "pointer" : glossaryTermTextStyle.cursor
                          }}
                          onClick={item.key === "contentMerchandising" ? openContentMerchChecklistModal : undefined}
                        >
                          {item.name || item.title}
                        </span>
                      </PolarisTooltip>
                    </div>
                  );
                })}
              </div>
            </div>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <InlineStack align="space-between" blockAlign="center" wrap={true}>
                <BlockStack gap="050">
                  <Text variant="headingMd" as="h2">Systems Inventory</Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    NetSuite scripts, integrations, EDI logic, Shopify components, and migration notes.
                  </Text>
                </BlockStack>
                <InlineStack gap="200" wrap={true}>
                  <Button onClick={() => { window.location.href = "/systems-inventory"; }}>
                    Open Systems Inventory
                  </Button>
                  <Button onClick={() => { window.location.href = "/readiness-board"; }}>
                    Open Readiness Board
                  </Button>
                </InlineStack>
              </InlineStack>
            </Card>
          </Layout.Section>

          {false && (
          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" wrap={true}>
                  <BlockStack gap="050">
                    <Text variant="headingMd" as="h2">Systems Inventory</Text>
                    <Text variant="bodySm" tone="subdued" as="p">
                      Malta Systems Inventory for NetSuite scripts, automation, Celigo dependencies, EDI logic, Shopify components, and VSG migration support.
                    </Text>
                  </BlockStack>
                  <InlineStack gap="200" wrap={true}>
                    <Button onClick={downloadVisibleSystemsInventoryCsv}>
                      Download CSV
                    </Button>
                    <Button variant="primary" onClick={openSystemsInventoryAddModal}>
                      Add New Inventory Item
                    </Button>
                  </InlineStack>
                </InlineStack>

                {systemsInventoryError ? (
                  <Banner status="critical">{systemsInventoryError}</Banner>
                ) : null}

                <div className="systems-inventory-controls">
                  <input
                    value={systemsInventorySearch}
                    onChange={(event) => setSystemsInventorySearch(event.target.value)}
                    placeholder="Search systems inventory..."
                    className="systems-inventory-search"
                  />
                  <Select
                    label="Category"
                    options={systemsInventorySelectOptions(systemsInventoryFilterOptions.categories, "All categories")}
                    value={systemsInventoryFilters.category || ""}
                    onChange={(value) => updateSystemsInventoryFilter("category", value)}
                  />
                  <Select
                    label="Platform"
                    options={systemsInventorySelectOptions(systemsInventoryFilterOptions.platforms, "All platforms")}
                    value={systemsInventoryFilters.platform || ""}
                    onChange={(value) => updateSystemsInventoryFilter("platform", value)}
                  />
                  <Select
                    label="Script Type"
                    options={systemsInventorySelectOptions(systemsInventoryFilterOptions.scriptTypes, "All script types")}
                    value={systemsInventoryFilters.script_type || ""}
                    onChange={(value) => updateSystemsInventoryFilter("script_type", value)}
                  />
                  <Select
                    label="Status"
                    options={systemsInventorySelectOptions(systemsInventoryFilterOptions.statuses, "All statuses")}
                    value={systemsInventoryFilters.status || ""}
                    onChange={(value) => updateSystemsInventoryFilter("status", value)}
                  />
                  <Select
                    label="Risk"
                    options={systemsInventorySelectOptions(systemsInventoryFilterOptions.riskLevels, "All risk levels")}
                    value={systemsInventoryFilters.risk_level || ""}
                    onChange={(value) => updateSystemsInventoryFilter("risk_level", value)}
                  />
                  <Select
                    label="Migration"
                    options={systemsInventorySelectOptions(systemsInventoryFilterOptions.migrationActions, "All actions")}
                    value={systemsInventoryFilters.migration_action || ""}
                    onChange={(value) => updateSystemsInventoryFilter("migration_action", value)}
                  />
                  <Select
                    label="Vendor Bundle"
                    options={[
                      { label: "All", value: "" },
                      { label: "Custom", value: "0" },
                      { label: "Vendor Bundle", value: "1" }
                    ]}
                    value={systemsInventoryFilters.vendor_bundle ?? ""}
                    onChange={(value) => updateSystemsInventoryFilter("vendor_bundle", value)}
                  />
                </div>

                <div className="systems-inventory-table-wrap" aria-busy={systemsInventoryLoading}>
                  {systemsInventoryLoading ? (
                    <Text variant="bodySm" tone="subdued">Loading Systems Inventory...</Text>
                  ) : null}
                  <div ref={systemsInventoryTableRef} className="systems-inventory-tabulator" />
                </div>
              </BlockStack>
            </Card>
          </Layout.Section>
          )}

          <Layout.Section variant="fullWidth">
            <div className="dashboard-grid">

              {/* Today's Scheduled Tech Projects */}
              <div className="dashboard-item span-two-thirds">
                <Card sectioned>
                  <BlockStack gap="100">
                    <Text variant="headingMd" as="h2">Today's Scheduled Tech Projects</Text>
                    <Checkbox
                      label="Show Completed History"
                      checked={showCompleted}
                      onChange={(val) => setShowCompleted(val)}
                    />
                    <DragDropContext
                      onDragEnd={async (result) => {
                        if (!result.destination) return;

                        // Reorder majorProjects directly
                        const updatedProjects = Array.from(majorProjects);
                        const [moved] = updatedProjects.splice(result.source.index, 1);
                        updatedProjects.splice(result.destination.index, 0, moved);

                        // Update sort_order
                        const updatedWithOrder = updatedProjects.map((p, idx) => ({
                          ...p,
                          sort_order: idx
                        }));

                        setMajorProjects(updatedWithOrder);
                        markProjectsDirty();
                      }}
                    >
                      <Droppable droppableId="projects">
                        {(provided) => (
                          <div {...provided.droppableProps} ref={provided.innerRef}>
                            {majorProjects.map((proj, idx) => {
                              const visible = isVisibleProject(proj, showCompleted);
                              return (
                                <Draggable
                                  key={getKey(proj)}
                                  draggableId={`proj-${getKey(proj)}`}
                                  index={idx}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        display: visible ? "flex" : "none",
                                        marginBottom: 4,
                                        position: "relative",
                                        padding: "6px 8px",
                                        borderRadius: 6,
                                        background: snapshot.isDragging ? "#f4f6f8" : "#fff",
                                        border: "none",
                                        background: "rgb(244 244 244)",
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      {/* Drag Handle */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: "40px",
                                          top: "13px",
                                          cursor: "grab",
                                        }}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                          <circle cx="5" cy="5" r="2" fill="#666" />
                                          <circle cx="5" cy="12" r="2" fill="#666" />
                                          <circle cx="5" cy="19" r="2" fill="#666" />
                                          <circle cx="12" cy="5" r="2" fill="#666" />
                                          <circle cx="12" cy="12" r="2" fill="#666" />
                                          <circle cx="12" cy="19" r="2" fill="#666" />
                                        </svg>
                                      </div>

                                      {/* Completed Checkbox */}
                                      <Checkbox
                                        checked={!!proj.completed_date}
                                        onChange={(checked) => {
                                          const now = checked ? localToMySQL(new Date()) : null;
                                          setMajorProjects(prev =>
                                            prev.map(p =>
                                              (p.id ?? p.tempId) === (proj.id ?? proj.tempId)
                                                ? { ...p, completed_date: now }
                                                : p
                                            )
                                          );
                                          markProjectsDirty();
                                        }}
                                      />



                                      {/* Project Name */}
                                      {proj.completed_date && (
                                        <Tooltip
                                          content={`Completed at: ${new Date(proj.completed_date).toLocaleString("en-US", {
                                            timeZone: "America/Chicago",
                                            month: "numeric",
                                            day: "numeric",
                                            year: "2-digit",
                                            hour: "numeric",
                                            minute: "2-digit",
                                            hour12: true,
                                          })}`}
                                        >
                                          <span style={{ cursor: 'pointer', align: "center", paddingtop: "2px" }}>
                                            {/* Inline calendar SVG */}
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                              <rect x="3" y="4" width="18" height="18" stroke="#666" strokeWidth="2" />
                                              <line x1="3" y1="10" x2="21" y2="10" stroke="#666" strokeWidth="2" />
                                              <line x1="8" y1="4" x2="8" y2="0" stroke="#666" strokeWidth="2" />
                                              <line x1="16" y1="4" x2="16" y2="0" stroke="#666" strokeWidth="2" />
                                            </svg>
                                          </span>
                                        </Tooltip>
                                      )}
                                      <input
                                        value={proj.project_name || ""}
                                        onChange={(e) => {
                                          setMajorProjects(prev =>
                                            prev.map(p =>
                                              (p.id ?? p.tempId) === (proj.id ?? proj.tempId)
                                                ? { ...p, project_name: e.target.value }
                                                : p
                                            )
                                          );
                                          markProjectsDirty();
                                        }}
                                        style={{
                                          flex: 1,
                                          padding: "6px 8px 6px 30px",
                                          border: "unset",
                                          textDecoration: isToday(proj.completed_date) ? "line-through" : "none",
                                          opacity: proj.completed_date ? 0.6 : 1
                                        }}
                                      />

                                      {/* Completed Date */}
                                      <ProjectDateTooltip completedDate={proj.completed_date} />

                                      {/* Delete Button */}
                                      <button
                                        onClick={() => {
                                          if (proj.id) {
                                            handleDeleteProject(proj.id);
                                          } else {
                                            setMajorProjects(prev => prev.filter(p => p.tempId !== proj.tempId));
                                            markProjectsDirty();
                                          }
                                        }}
                                        style={{
                                          /* position: "absolute",
                                           zIndex: 10,*/
                                          border: "none",
                                          background: "none",
                                          color: "#666",
                                          right: "3px",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <Icon source={DeleteIcon} />
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>



                    {/* Add Project Button */}
                    <button
                      onClick={() => {
                        setMajorProjects(prev => [
                          ...prev,
                          {
                            tempId: uuidv4(),
                            project_name: "",
                            completed_date: null
                          }
                        ]);
                        markProjectsDirty();
                      }}
                      style={{
                        marginTop: "0px",
                        padding: "6px 8px",
                        background: "none",
                        border: "1px solid #777",
                        cursor: "pointer"
                      }}
                    >
                      + Add Project
                    </button>





                    <Button
                      variant="primary"
                      disabled={saveStatus === "saving" || !projectsDirty}
                      onClick={saveProjects}
                    >
                      {saveStatus === "idle" && (projectsDirty ? "Save Projects" : "Saved")}
                      {saveStatus === "saving" && `Saving${'.'.repeat(dotCount)}`}
                      {saveStatus === "saved" && "Saved"}

                    </Button>

                    <Button
                      hidden
                      variant="primary"
                      disabled
                      onClick={() => { }}
                    >
                      Legacy Save
                    </Button>
                    {false && (
                      <Button
                        hidden
                        variant="primary"
                        disabled={saveStatus === "saving"}
                        onClick={async () => {
                          setSaveStatus("saving");
                          setSaveColor(undefined);

                          try {
                            // 1️⃣ Prepare payload: only send fields backend expects
                            const payload = majorProjects.map((p, idx) => ({
                              id: p.id || null,
                              tempId: p.tempId || null,   // 👈 ADD THIS
                              project_name: p.project_name || "",
                              completed_date: p.completed_date || null,
                              sort_order: p.sort_order ?? idx
                            }));

                            console.log("Sending projects to backend:", payload);

                            // 2️⃣ Post to backend
                            const saveRes = await api.post("/api/projects", { projects: payload });

                            // 3️⃣ Backend should return full list with real MySQL IDs
                            const refreshed = await api.get("/api/projects");

                            // 4️⃣ Map backend IDs back into frontend state
                            setMajorProjects(
                              refreshed.data.projects
                                .sort((a, b) => a.sort_order - b.sort_order) // 👈 safety net
                                .map(p => ({
                                  id: p.id,
                                  tempId: p.temp_id || uuidv4(), // 👈 keep identity alive
                                  project_name: p.project_name,
                                  completed_date: p.completed_date,
                                  sort_order: p.sort_order
                                }))
                            );

                            setSaveStatus("saved");
                            setSaveColor("#28a745");

                            setTimeout(() => {
                              setSaveStatus("idle");
                              setSaveColor(undefined);
                            }, 2000);

                          } catch (err) {
                            console.error("Failed saving projects:", err);
                            setSaveStatus("idle");
                            console.log("Sending projects to backend:", payload);
                          }
                        }}
                      >
                        {saveStatus === "idle" && "Save Projects"}
                        {saveStatus === "saving" && `Saving${'.'.repeat(dotCount)}`}
                        {saveStatus === "saved" && "Saved"}

                      </Button>
                    )}

                  </BlockStack>
                </Card>
              </div>
              <div className="dashboard-item span-third">
                <BlockStack gap="400">
                  {/* Legacy Sales Yesterday Section */}
                  <div>
                    <Card sectioned>
                      <button
                        onClick={() => setSalesCollapsed(!salesCollapsed)}
                        style={{
                          all: 'unset',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          display: 'block',
                          marginBottom: 4,

                        }}
                      >
                        Sales Yesterday (Net) — {formatCurrency(salesYesterday)}
                      </button>
                      {salesCollapsed && (
                        <BlockStack gap="100" style={{ paddingLeft: 16, flexDirection: 'column' }}>
                          <Metric label="Online Store Yesterday" value={formatCurrency(onlineStoreYesterday)} />
                          <Metric label="POS Yesterday" value={formatCurrency(posYesterday)} />
                          <Metric label="Refunds Yesterday" value={formatCurrency(refundsYesterday)} />
                          <Metric label="POS Month to Date" value={formatCurrency(salesMTDWithPOS - salesMTD)} />
                          <Metric label="Plan to Date" value={formatCurrency(planMTD)} />
                        </BlockStack>
                      )}
                      <Metric label="Monthly Goal" value={formatCurrency(monthlyGoal)} />
                      <Metric
                        label="% to Plan (Web Only)"
                        value={`${percentToPlan}% (${formatCurrency(salesMTD)})`}
                        tone={percentToPlan > 99 ? "success" : percentToPlan < 99 ? "critical" : tone}
                      />
                      <Metric
                        label="% to Plan (Including POS)"
                        value={`${percentToPlanWithPOS}% (${formatCurrency(salesMTDWithPOS)})`}
                        tone={percentToPlan > 99 ? "success" : percentToPlan < 99 ? "critical" : tone}
                      />

                      {yearlyStats && (
                        <>
                          <Metric label="YTD POS" value={formatCurrency(yearlyStats.posYearToDate)} />
                          <Metric label="YTD Progress" value={`${yearlyStats.progressPct}% (${formatCurrency(yearlyStats.yearToDate)})`} />
                          <Metric label="Projected Year End" value={formatCurrency(yearlyStats.projectedYearEnd)} tone={yearlyStats.projectedYearEnd >= yearlyStats.goal ? "success" : "critical"} />
                        </>
                      )}

                    </Card>
                  </div>
                  <div>
                    <Card sectioned>
                      <BlockStack gap="200">
                        <Text variant="headingMd" as="h2">Order Mix Intelligence</Text>
                        <Metric label="Large-Order Threshold (YTD-derived)" value={formatCurrency(Number(orderMixThresholding.largeOrderThreshold || 0))} />
                        <Metric label="Raw AOV (MTD)" value={formatCurrency(Number(orderMixAov.rawAov || 0))} />
                        <Metric label="Adjusted AOV (MTD)" value={formatCurrency(Number(orderMixAov.adjustedAov || 0))} />
                        <Metric label="AOV Distortion" value={`${formatCurrency(Number(orderMixAov.distortion || 0))} (${Number(orderMixAov.distortionPct || 0).toFixed(1)}%)`} />
                        <Metric label="Top 5 Revenue Concentration" value={`${Number(orderMixConcentration.top5Pct || 0).toFixed(1)}%`} />
                        <Metric label="Core Forecast" value={formatCurrency(Number(orderMixDecomposition.coreForecast || 0))} />
                        <Metric label="Expected Large-Order Contribution" value={formatCurrency(Number(orderMixDecomposition.expectedLargeOrderContribution || 0))} />
                        <Metric label="Decomposed Forecast" value={formatCurrency(Number(orderMixDecomposition.decomposedForecast || 0))} />
                        <Metric
                          label="Forecastability"
                          value={`${orderMixConfidence.label || "Low"} (${Number(orderMixConfidence.score || 0)})`}
                          tone={Number(orderMixConfidence.score || 0) >= 75 ? "success" : Number(orderMixConfidence.score || 0) >= 55 ? "caution" : "critical"}
                        />
                      </BlockStack>
                    </Card>
                  </div>

                  {/* Orders Yesterday Section */}
                  <div>
                    <Card sectioned>
                      <BlockStack gap="6"> {/* reasonable gap */}
                        {/* Header with button */}
                        <InlineStack align="space-between">
                          <Text variant="headingMd" as="h2">
                            Orders Yesterday
                          </Text>
                          {orders.length > 3 && (
                            <Button plain className="toggle-orders-btn" onClick={() => setOrdersCollapsed(!ordersCollapsed)}>
                              {ordersCollapsed ? `+${orders.length - 3} more` : '-Collapse'}
                            </Button>
                          )}
                        </InlineStack>

                        {/* Orders list */}
                        {loading ? (
                          <Text>Loading…</Text>
                        ) : orders.length ? (
                          <div className="orders-container">
                            {orders.map((order, idx) => {
                              const isExtra = idx >= 3;
                              return (
                                <div
                                  key={order.id}
                                  className={`order-item ${isExtra ? 'extra' : ''} ${!ordersCollapsed && isExtra ? 'show' : ''
                                    }`}
                                >
                                  <InlineStack align="space-between">
                                    <a
                                      href={`https://admin.shopify.com/store/${process.env.REACT_APP_SHOPIFY_STORE}/orders/${order.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Text tone="brand">{order.name}</Text>
                                    </a>
                                    <Text>{formatCurrency(order.financials.netYesterday)}</Text>
                                  </InlineStack>

                                  {order.financials.refundsYesterday > 0 && (
                                    <InlineStack align="space-between">
                                      <Text tone="critical">Refunds</Text>
                                      <Text tone="critical">
                                        -{formatCurrency(order.financials.refundsYesterday)}
                                      </Text>
                                    </InlineStack>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <BlockStack gap="2">
                            <Text>No orders available</Text>
                            <a
                              href={`/auth?shop=${process.env.REACT_APP_SHOPIFY_STORE}`}
                              style={{ color: "#008060", textDecoration: "underline" }}
                            >
                              Connect Store
                            </a>
                          </BlockStack>
                        )}
                      </BlockStack>
                    </Card>
                  </div>



                  {/* Top Products MTD Section */}
                  <div>
                    <Card sectioned>
                      <BlockStack gap="200">
                        <Text variant="headingMd" as="h2">
                          Top Products Month-to-Date
                        </Text>

                        {topProductsMTD.length ? (
                          topProductsMTD.map((p, idx) => (
                            <BlockStack key={idx} gap="50">
                              <InlineStack align="center" style={{ width: "100%" }}>
                                {p.productId ? (
                                  <a
                                    href={`https://admin.shopify.com/store/${process.env.REACT_APP_SHOPIFY_STORE}/products/${p.productId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none" }}
                                  >
                                    <Text tone="brand">{p.title}</Text>
                                  </a>
                                ) : (
                                  <Text tone="subdued">{p.title}</Text>
                                )}

                                {p.handle ? (
                                  <a
                                    href={`https://${process.env.REACT_APP_SHOPIFY_STORE}.myshopify.com/products/${p.handle}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: "flex", alignItems: "center", textDecoration: "none", marginLeft: 12, marginRight: "auto" }}
                                  >
                                    <Text tone="brand">Store</Text>
                                    <Icon source={ExternalIcon} color="base" style={{ marginLeft: 4 }} />
                                  </a>
                                ) : (
                                  <div style={{ marginLeft: 12, marginRight: "auto" }} />
                                )}

                                <Text variant="headingSm">
                                  {formatCurrency(p.revenue)} (qty: {p.quantity})
                                </Text>
                              </InlineStack>
                            </BlockStack>
                          ))
                        ) : (
                          <Text tone="subdued">No product sales this month</Text>
                        )}
                      </BlockStack>
                    </Card>
                  </div>
                </BlockStack>
              </div>
            </div>
          </Layout.Section>

          {/* Percent-of-Goal Chart by Channel */}
          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="050" style={{ marginBottom: 16 }}>
                <Text variant="headingMd">{monthName} Details</Text>
                <Text variant="bodySm" tone="subdued">source: {monthDetailsRenderSource}</Text>
              </BlockStack>
              <div className="month-details-kpi-strip">
                {renderedMonthDetailsGauges.map((kpi) => (
                  <CompactKpiGauge
                    key={kpi.key}
                    label={kpi.label}
                    value={
                      Object.prototype.hasOwnProperty.call(monthDetailsGaugeDisplayValues, kpi.key)
                        ? monthDetailsGaugeDisplayValues[kpi.key]
                        : kpi.value
                    }
                    min={kpi.min}
                    max={kpi.max}
                    bands={kpi.bands}
                    formattedValue={kpi.formattedValue}
                    tooltipContent={kpi.tooltipContent}
                  />
                ))}
                {hasOrderMixGaugeSource && (
                  <div className="month-details-ai-summary month-details-ai-summary--in-strip">
                    {monthDetailsRenderSource !== "ai_contract" && (
                      <div className="order-mix-summary-mode-tabs">
                        {ORDER_MIX_SUMMARY_MODES.map((modeOption) => (
                          <label
                            key={modeOption.value}
                            className={`order-mix-summary-mode-option ${orderMixSummaryMode === modeOption.value ? "order-mix-summary-mode-option--selected" : ""}`}
                            htmlFor={`order-mix-summary-mode-${modeOption.value}`}
                          >
                            <span className="order-mix-summary-mode-option-text">{modeOption.label}</span>
                            <input
                              id={`order-mix-summary-mode-${modeOption.value}`}
                              className="order-mix-summary-mode-option-radio"
                              type="radio"
                              name="order-mix-summary-mode"
                              checked={orderMixSummaryMode === modeOption.value}
                              onChange={() => setOrderMixSummaryMode(modeOption.value)}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                    {monthDetailsRenderSource === "ai_contract" ? (
                      <BlockStack gap="100">
                        <Text as="p">
                          {String(juneDetailsIntelligence?.narrative?.sentence || "Summary unavailable right now.")}
                        </Text>
                        {Array.isArray(juneDetailsIntelligence?.narrative?.bullets) && juneDetailsIntelligence.narrative.bullets.length > 0 && (
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {juneDetailsIntelligence.narrative.bullets.slice(0, 3).map((bullet, idx) => (
                              <li key={`${idx}-${bullet}`}>
                                <Text tone="subdued" as="span">{String(bullet)}</Text>
                              </li>
                            ))}
                          </ul>
                        )}
                      </BlockStack>
                    ) : (
                      <Text as="p">
                        {orderMixExecutiveSummaryLoading
                          ? "Building summary..."
                          : (orderMixExecutiveSummary || "Summary unavailable right now.")}
                      </Text>
                    )}
                  </div>
                )}
              </div>
              {salesSummaryBullets.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {salesSummaryBullets.map((bullet, index) => (
                      <li key={`${index}-${bullet}`} style={{ marginBottom: 6 }}>
                        <Text tone="subdued" as="span">
                          <GlossaryText text={bullet} terms={glossaryTermsMap} />
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Web Sales Trend Sentence */}
              <Text variant="heading" style={{ marginBottom: 16 }}>
                <GlossaryText text={salesSummary || webSalesTrendSentence(chartData)} terms={glossaryTermsMap} />
              </Text>
              <InlineStack gap="300" blockAlign="center" style={{ marginBottom: 16 }}>
                <Button onClick={handleReevaluate} loading={isReevaluating}>
                  Re-evaluate
                </Button>
                <Checkbox
                  label="Show Projected Finish Pace"
                  checked={showWebTrendline}
                  onChange={(newValue) => setShowWebTrendline(newValue)}
                />
                <Checkbox
                  label={(
                    <span style={{ color: showRealitySeasoning ? "#116dff" : undefined, fontWeight: showRealitySeasoning ? 600 : undefined }}>
                      <GlossaryText text="Intelligent Forecast" terms={glossaryTermsMap} />
                    </span>
                  )}
                  labelClassName="intelligent-forecast-checkbox"
                  checked={showRealitySeasoning}
                  onChange={(newValue) => setShowRealitySeasoning(newValue)}
                />
                <Checkbox
                  label={<GlossaryText text="Simulation" terms={glossaryTermsMap} />}
                  checked={showSimulation}
                  onChange={handleSimulationToggle}
                />
                {environment !== "production" && (
                  <Checkbox
                    label="Compare Goal Model"
                    checked={compareGoalModel}
                    onChange={(newValue) => setCompareGoalModel(newValue)}
                  />
                )}
              </InlineStack>

              {chartData.length > 0 ? (
                <div className="goal-chart-shell" style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 12,
                      zIndex: 2,
                      background: "rgba(255, 255, 255, 0.94)",
                      border: "1px solid #d9d9d9",
                      borderRadius: 10,
                      padding: "10px 12px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      pointerEvents: "none"
                    }}
                  >
                    <BlockStack gap="050">
                      <Text variant="bodySm" tone="subdued" as="p">
                        {monthName} Sales Projected
                      </Text>
                      <Text variant="headingMd" as="p">
                        <span>
                          {formatCurrency(monthSalesProjectedValue)}
                        </span>
                      </Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        <span title="MTD + recent completed-day pace + expected outlier upside">
                          Intelligent: <span style={{ color: projectedPanelTone.value, fontWeight: 600 }}>{formatCurrency(intelligentForecastValue)}</span>
                        </span>
                      </Text>
                      {showRealitySeasoning && (
                        <Text variant="bodyMd" as="p">
                          <span style={{ color: "#116dff", fontWeight: 600 }}>
                            Trend line only: {chartIntelligentForecastPct.toFixed(1)}%
                          </span>
                        </Text>
                      )}
                    </BlockStack>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={goalModelDiagnostics.rows}
                      margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                      onMouseLeave={() => setHoveredGoalSeries(null)}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <ReferenceArea
                        y1={0}
                        y2={100}
                        fill="rgba(200, 0, 0, 0.08)"
                        ifOverflow="extendDomain"
                      />
                      <XAxis dataKey="formattedDate" />
                      <YAxis unit="%" domain={[0, 'auto']} />
                      <Tooltip content={<GoalChartTooltip hoveredSeries={hoveredGoalSeries} compareGoalModel={compareGoalModel} />} offset={44} />
                      <Legend formatter={(value) => value && value.startsWith("Scenario ") ? "" : value} />
                      <Line
                        type="monotone"
                        dataKey="dailyPercentOfGoal"
                        name="% of Goal Web and POS"
                        {...getGoalChartLineProps("dailyPercentOfGoal", "#82ca9d")}
                      />
                      <Line
                        type="monotone"
                        dataKey="dailyWebPercentOfGoal"
                        name="% of Goal Web Only"
                        {...getGoalChartLineProps("dailyWebPercentOfGoal", "#ff5100")}
                      />
                      {showWebTrendline && (
                        <>
                          {webScenarioKeys.map((scenarioKey) => (
                            <Line
                              key={scenarioKey}
                              type="monotone"
                              dataKey={scenarioKey}
                              name={`Trajectory as of ${monthNameAbbr} ${Number(String(scenarioKey).replace("webScenario", ""))}`}
                              legendType="none"
                              stroke="#000000"
                              {...getGoalChartLineProps(
                                scenarioKey,
                                "#000000",
                                {
                                  strokeWidth: 1,
                                  dot: false
                                }
                              )}
                              strokeOpacity={
                                hoveredGoalSeries
                                  ? (hoveredGoalSeries === scenarioKey ? 1 : 0.12)
                                  : webScenarioKeys.length > 1
                                    ? 0.08 + ((webScenarioKeys.indexOf(scenarioKey)) / (webScenarioKeys.length - 1)) * 0.45
                                    : 0.45
                              }
                              isAnimationActive={false}
                              connectNulls={false}
                            />
                          ))}
                          <Line
                            type="linear"
                            dataKey="webTrend"
                            name="Projected Finish Pace"
                            strokeDasharray="6 6"
                            isAnimationActive={true}
                            {...getGoalChartLineProps("webTrend", "#ff5900", { dot: false })}
                          />
                        </>
                      )}
                      {showRealitySeasoning && (
                        <Line
                          type="linear"
                          dataKey="realitySeasoning"
                          name="Intelligent Forecast"
                          stroke="#116dff"
                          strokeDasharray="10 6"
                          isAnimationActive={true}
                          connectNulls={false}
                          {...getGoalChartLineProps("realitySeasoning", "#116dff", { dot: false })}
                        />
                      )}
                      {compareGoalModel && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="recalibratedDailyGoalPct"
                            name="% of Goal Recalibrated"
                            stroke="#2f6fed"
                            strokeDasharray="6 4"
                            {...getGoalChartLineProps("recalibratedDailyGoalPct", "#2f6fed", { dot: false })}
                          />
                          <Line
                            type="monotone"
                            dataKey="varianceVsCurrentGoalPct"
                            name="Variance vs Current Goal %"
                            stroke="#6f42c1"
                            strokeDasharray="2 6"
                            {...getGoalChartLineProps("varianceVsCurrentGoalPct", "#6f42c1", { dot: false })}
                          />
                          <Line
                            type="monotone"
                            dataKey="varianceVsRecalibratedGoalPct"
                            name="Variance vs Recalibrated Goal %"
                            stroke="#009688"
                            strokeDasharray="2 6"
                            {...getGoalChartLineProps("varianceVsRecalibratedGoalPct", "#009688", { dot: false })}
                          />
                        </>
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                  {compareGoalModel && goalModelDiagnostics.stats && (
                    <div style={{ marginTop: 12 }}>
                      <InlineStack gap="400" wrap={true}>
                        <Text variant="bodySm" tone="subdued">Avg abs variance vs old: <strong>{goalModelDiagnostics.stats.avgAbsVarianceOld.toFixed(1)}%</strong></Text>
                        <Text variant="bodySm" tone="subdued">Avg abs variance vs recalibrated: <strong>{goalModelDiagnostics.stats.avgAbsVarianceRecal.toFixed(1)}%</strong></Text>
                        <Text variant="bodySm" tone="subdued">Days over goal (old): <strong>{goalModelDiagnostics.stats.daysOverOld}</strong></Text>
                        <Text variant="bodySm" tone="subdued">Days over goal (recalibrated): <strong>{goalModelDiagnostics.stats.daysOverRecal}</strong></Text>
                        <Text variant="bodySm" tone="subdued">
                          Biggest over day: <strong>{goalModelDiagnostics.stats.biggestOverCurrent ? `${goalModelDiagnostics.stats.biggestOverCurrent.formattedDate} (${Number(goalModelDiagnostics.stats.biggestOverCurrent.varianceVsCurrentGoalPct || 0).toFixed(1)}%)` : "n/a"}</strong>
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                          Biggest under day: <strong>{goalModelDiagnostics.stats.biggestUnderCurrent ? `${goalModelDiagnostics.stats.biggestUnderCurrent.formattedDate} (${Number(goalModelDiagnostics.stats.biggestUnderCurrent.varianceVsCurrentGoalPct || 0).toFixed(1)}%)` : "n/a"}</strong>
                        </Text>
                      </InlineStack>
                    </div>
                  )}
                  {pacingDiagnosticsV2 && (
                    <div
                      style={{
                        marginTop: 12,
                        border: "1px solid #d9d9d9",
                        borderRadius: 10,
                        padding: "10px 12px",
                        background: "#f7faf7"
                      }}
                    >
                      <InlineStack gap="400" wrap={true}>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Core Pace: <strong>{Number(pacingDiagnosticsV2.corePacePct || 0).toFixed(1)}%</strong>
                        </Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Blended Pace: <strong>{Number(pacingDiagnosticsV2.blendedPacePct || 0).toFixed(1)}%</strong>
                        </Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Outlier Contribution: <strong>{formatCurrency(Number(pacingDiagnosticsV2.outlierContribution || 0))}</strong>
                        </Text>
                      </InlineStack>
                    </div>
                  )}
                </div>
              ) : (
                <Text>No data for chart</Text>
              )}
              <InlineStack blockAlign="center" style={{ width: "100%", marginTop: 12 }}>
                <div style={{ flex: 1 }}>
                  <Text tone="subdued" variant="bodySm" as="p">
                    Each gray line is a trajectory adjustment based on the its MTD average. These scenario lines show each day's projected month-end pace; the Malta-Orange dotted line is the current projected finish pace.
                  </Text>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <Button size="slim" onClick={openGlossaryModal}>
                    Glossary
                  </Button>
                </div>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Daily Sales vs Goal Chart */}
          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="050">
                <Text variant="headingMd" as="h2">Daily Sales vs Goal</Text>
                <Text variant="bodySm" tone="subdued" as="p">Weighted for day-of-week</Text>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text variant="bodySm" tone="subdued" as="span">Moving Avg:</Text>
                  <select
                    value={movingAveragePreset}
                    onChange={(event) => setMovingAveragePreset(event.target.value)}
                    style={{ padding: "4px 6px" }}
                    aria-label="Moving average preset"
                  >
                    <option value="3">Fast (3d)</option>
                    <option value="5">Balanced (5d)</option>
                    <option value="7">Weekly (7d)</option>
                    <option value="14">Smooth (14d)</option>
                    <option value="custom">Custom</option>
                  </select>
                  {movingAveragePreset === "custom" && (
                    <input
                      type="number"
                      min={1}
                      max={30}
                      step={1}
                      value={movingAverageCustomDaysInput}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next === "") {
                          setMovingAverageCustomDaysInput("");
                          return;
                        }
                        const parsed = Math.floor(Number(next));
                        if (!Number.isFinite(parsed)) return;
                        const clamped = Math.max(1, Math.min(30, parsed));
                        setMovingAverageCustomDaysInput(String(clamped));
                      }}
                      onBlur={() => {
                        const parsed = Math.floor(Number(movingAverageCustomDaysInput));
                        if (!Number.isFinite(parsed)) {
                          setMovingAverageCustomDaysInput("5");
                          return;
                        }
                        setMovingAverageCustomDaysInput(String(Math.max(1, Math.min(30, parsed))));
                      }}
                      style={{ width: 64, padding: "4px 6px" }}
                      aria-label="Custom moving average days"
                    />
                  )}
                </div>
                <Text variant="bodySm" tone="subdued" as="p">
                  Fast reacts quickly, Balanced is default, Weekly smooths one week, Smooth highlights broader trend.
                </Text>
                {movingAveragePreset === "custom" && (
                  <Text variant="bodySm" tone="subdued" as="p">Custom range: 1-30 days.</Text>
                )}
                <Text variant="bodySm" tone="subdued" as="p">
                  Gap Trend ({movingAverageDays}d): {Number.isFinite(dailySalesGapTrend.latestGapPctMA) ? `${(dailySalesGapTrend.latestGapPctMA * 100).toFixed(1)}%` : "n/a"} • {dailySalesGapTrend.direction}
                </Text>
              </BlockStack>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailySalesGapTrend.rows} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="formattedDate" />
                    <YAxis
                      tickFormatter={(value) =>
                        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value)
                      }
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value),
                        name === 'webOnly' ? 'Web Only' : name
                      ]}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="goal" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="actual" stroke="#82ca9d" strokeWidth={2} />
                    <Line type="monotone" dataKey="webOnly" stroke="#FFA500" strokeWidth={2} />
                    <Line
                      type="monotone"
                      dataKey="webOnlyMA"
                      name={`Web Only MA (${movingAverageDays}d)`}
                      stroke="#5f6f8f"
                      strokeOpacity={0.6}
                      strokeWidth={1.25}
                      strokeDasharray="6 6"
                      dot={false}
                      connectNulls={true}
                    />
                    {compareGoalModel && (
                      <>
                        <Line type="monotone" dataKey="currentExpectedMTD" name="Expected MTD $ (Current)" stroke="#455a64" strokeDasharray="6 6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="recalibratedExpectedMTD" name="Recalibrated Expected MTD $" stroke="#1565c0" strokeDasharray="8 5" strokeWidth={2} dot={false} />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text>No data for chart</Text>
              )}
              {compareGoalModel && revenueGoalDiagnostics.summary && (
                <div style={{ marginTop: 12 }}>
                  <InlineStack gap="400" wrap={true}>
                    <Text variant="bodySm" tone="subdued">MTD actual: <strong>{formatCurrency(revenueGoalDiagnostics.summary.mtdActual)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">MTD expected (current): <strong>{formatCurrency(revenueGoalDiagnostics.summary.mtdExpectedCurrent)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">MTD expected (recalibrated): <strong>{formatCurrency(revenueGoalDiagnostics.summary.mtdExpectedRecalibrated)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">Variance vs current: <strong>{formatCurrency(revenueGoalDiagnostics.summary.varianceVsCurrent)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">Variance vs recalibrated: <strong>{formatCurrency(revenueGoalDiagnostics.summary.varianceVsRecalibrated)}</strong></Text>
                  </InlineStack>
                </div>
              )}
            </Card>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">
                    <GlossaryText text="Traffic Signals (Non-bot traffic)" terms={glossaryTermsMap} />
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    {trafficSummaryText}
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    <GlossaryText
                      text="Active-month comparisons use completed days only, so signals reflect prior-month completed-day MTD rather than a full prior month."
                      terms={glossaryTermsMap}
                    />
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    Solid lines show historical actuals. Dotted lines show forecast direction. The vertical April marker compares current actuals with the same period last month.
                  </Text>
                </BlockStack>

                {trafficError ? (
                  <Banner status="critical">{trafficError}</Banner>
                ) : (!hasTrafficData && trafficLoading) ? (
                  <Text>Loading traffic analytics...</Text>
                ) : (
                  <div className="traffic-panel-shell">
                    {trafficLoading ? (
                      <div className="traffic-refresh-badge">Refreshing traffic analytics...</div>
                    ) : null}
                    <div className="traffic-summary-grid">
                      {trafficSignalPanels.map((panel) => (
                        <TrafficSignalCard
                          key={`${panel.panelKey}-${panel.key}`}
                          panelKey={panel.panelKey}
                          label={panel.label}
                          subtext={panel.subtext || ""}
                          summary={trafficSummary?.[panel.key]}
                          signal={trafficSignals?.[panel.key]}
                          comparisonWindow={trafficComparisonWindow}
                          terms={glossaryTermsMap}
                          valueFormatter={(panel.kind === "conversion" || panel.kind === "clickthrough")
                            ? formatTrafficRate
                            : (panel.kind === "abandonment" ? formatTrafficCount : formatTrafficPercent)}
                          valueMode={(panel.kind === "abandonment" || panel.kind === "clickthrough") ? "current" : "pctChange"}
                          compareAsRate={panel.kind === "conversion" || panel.kind === "abandonment" || panel.kind === "clickthrough"}
                          invertSignalPolarity={Boolean(panel.invertSignalPolarity)}
                          cartAbandonmentMode={cartAbandonmentMode}
                          setCartAbandonmentMode={setCartAbandonmentMode}
                          activeTrafficTab={activeTrafficTab}
                          setActiveTrafficTab={setActiveTrafficTab}
                          clickThroughMode={clickThroughMode}
                          setClickThroughMode={setClickThroughMode}
                        />
                      ))}
                    </div>

                    <div className="traffic-chart-grid">
                      {trafficChartPanels.map((panel) => (
                        <TrafficTrendCard
                          key={panel.key}
                          label={panel.label}
                          color={panel.color}
                          data={trafficCharts?.[panel.key] || []}
                          summary={trafficSummary?.[panel.key]}
                          metricKind={panel.kind}
                          terms={glossaryTermsMap}
                        />
                      ))}
                    </div>

                    <div className="traffic-top-grid">
                      {trafficChartPanels.map((panel) => (
                        <TrafficTopChannelsCard
                          key={panel.key}
                          label={panel.label.replace(" Sessions", "")}
                          channels={topReferringChannels?.[panel.key] || []}
                          metricKind={panel.kind}
                          terms={glossaryTermsMap}
                        />
                      ))}
                    </div>

                    <InlineStack blockAlign="center" style={{ width: "100%", marginTop: 12 }}>
                      <div style={{ marginLeft: "auto" }}>
                        <Button size="slim" onClick={openGlossaryModal}>
                          Glossary
                        </Button>
                      </div>
                    </InlineStack>
                  </div>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>





          <Layout.Section variant="fullWidth">
            <Text variant="bodySm" tone="subdued">Environment: {environment.toUpperCase()}</Text>
          </Layout.Section>
        </Layout>
      </Page>
      <Modal
        open={Boolean(systemsInventoryDetailsItem)}
        onClose={() => setSystemsInventoryDetailsItem(null)}
        title={systemsInventoryDetailsItem?.component_name || "Systems Inventory Details"}
        size="large"
        primaryAction={{
          content: "Close",
          onAction: () => setSystemsInventoryDetailsItem(null)
        }}
      >
        <Modal.Section>
          {systemsInventoryDetailsItem ? (
            <BlockStack gap="300">
              <div className="systems-inventory-detail-grid">
                {[
                  "category",
                  "component_type",
                  "platform",
                  "netsuite_internal_id",
                  "netsuite_script_id",
                  "netsuite_script_type",
                  "netsuite_owner",
                  "netsuite_inactive",
                  "related_system",
                  "related_partner",
                  "related_record_type",
                  "status",
                  "risk_level",
                  "migration_action",
                  "vendor_bundle",
                  "source",
                  "last_reviewed",
                  "reviewed_by"
                ].map((field) => (
                  <div key={field} className="systems-inventory-detail-field">
                    <Text variant="bodySm" tone="subdued">{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</Text>
                    <Text as="p">
                      {field === "vendor_bundle"
                        ? (systemsInventoryDetailsItem[field] ? "Vendor Bundle" : "Custom")
                        : (systemsInventoryDetailsItem[field] || "—")}
                    </Text>
                  </div>
                ))}
              </div>
              {["business_purpose", "technical_notes", "dependencies", "known_risks"].map((field) => (
                <div key={field} className="systems-inventory-detail-block">
                  <Text variant="headingSm" as="h3">{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</Text>
                  <Text as="p">{systemsInventoryDetailsItem[field] || "—"}</Text>
                </div>
              ))}
              <InlineStack gap="200" wrap={true}>
                {(systemsInventoryDetailsItem.view_url || buildSystemsInventoryNetSuiteUrl(systemsInventoryDetailsItem.netsuite_internal_id, false)) ? (
                  <a href={systemsInventoryDetailsItem.view_url || buildSystemsInventoryNetSuiteUrl(systemsInventoryDetailsItem.netsuite_internal_id, false)} target="_blank" rel="noreferrer">
                    View NetSuite Script
                  </a>
                ) : null}
                {(systemsInventoryDetailsItem.edit_url || buildSystemsInventoryNetSuiteUrl(systemsInventoryDetailsItem.netsuite_internal_id, true)) ? (
                  <a href={systemsInventoryDetailsItem.edit_url || buildSystemsInventoryNetSuiteUrl(systemsInventoryDetailsItem.netsuite_internal_id, true)} target="_blank" rel="noreferrer">
                    Edit NetSuite Script
                  </a>
                ) : null}
              </InlineStack>
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>
      <Modal
        open={systemsInventoryFormOpen}
        onClose={() => setSystemsInventoryFormOpen(false)}
        title={systemsInventoryFormMode === "edit" ? "Edit Inventory Item" : "Add New Inventory Item"}
        size="large"
        primaryAction={{
          content: systemsInventorySaving ? "Saving..." : "Save",
          onAction: saveSystemsInventoryItem,
          loading: systemsInventorySaving
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setSystemsInventoryFormOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <div className="systems-inventory-form-grid">
            {SYSTEMS_INVENTORY_FORM_FIELDS.map((field) => {
              if (field === "status") {
                return (
                  <Select
                    key={field}
                    label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                    options={SYSTEMS_INVENTORY_STATUS_OPTIONS.map((value) => ({ label: value, value }))}
                    value={systemsInventoryFormDraft[field] || "Needs Validation"}
                    onChange={(value) => updateSystemsInventoryDraft(field, value)}
                  />
                );
              }
              if (field === "risk_level") {
                return (
                  <Select
                    key={field}
                    label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                    options={SYSTEMS_INVENTORY_RISK_OPTIONS.map((value) => ({ label: value, value }))}
                    value={systemsInventoryFormDraft[field] || "Unknown"}
                    onChange={(value) => updateSystemsInventoryDraft(field, value)}
                  />
                );
              }
              if (field === "migration_action") {
                return (
                  <Select
                    key={field}
                    label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                    options={SYSTEMS_INVENTORY_MIGRATION_OPTIONS.map((value) => ({ label: value, value }))}
                    value={systemsInventoryFormDraft[field] || "Validate"}
                    onChange={(value) => updateSystemsInventoryDraft(field, value)}
                  />
                );
              }
              if (field === "vendor_bundle") {
                return (
                  <Select
                    key={field}
                    label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                    options={[
                      { label: "Custom", value: "0" },
                      { label: "Vendor Bundle", value: "1" }
                    ]}
                    value={String(systemsInventoryFormDraft[field] || 0)}
                    onChange={(value) => updateSystemsInventoryDraft(field, value)}
                  />
                );
              }
              if (["business_purpose", "technical_notes", "dependencies", "known_risks"].includes(field)) {
                return (
                  <label key={field} className="systems-inventory-form-field systems-inventory-form-field--wide">
                    <span>{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</span>
                    <textarea
                      value={systemsInventoryFormDraft[field] || ""}
                      onChange={(event) => updateSystemsInventoryDraft(field, event.target.value)}
                      rows={4}
                    />
                  </label>
                );
              }
              return (
                <label key={field} className="systems-inventory-form-field">
                  <span>{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</span>
                  <input
                    type={field === "last_reviewed" ? "date" : "text"}
                    value={systemsInventoryFormDraft[field] || ""}
                    onChange={(event) => updateSystemsInventoryDraft(field, event.target.value)}
                  />
                </label>
              );
            })}
          </div>
        </Modal.Section>
      </Modal>
      <Modal
        open={forecastMethodologyModalOpen}
        onClose={() => setForecastMethodologyModalOpen(false)}
        title="Forecasting Methodology"
        size="large"
        primaryAction={{
          content: "Close",
          onAction: () => setForecastMethodologyModalOpen(false)
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              This forecast is designed as an operational pacing model, not a formal financial forecast.
            </Text>
            <Text as="p">
              The model estimates expected month-end sales by combining current month-to-date performance, completed-day sales velocity, day-of-week weighting, historical pacing behavior, and bounded upside assumptions.
            </Text>
            <Text as="p">
              <strong>Key Methodology</strong>
            </Text>
            <Text as="p"><strong>1. Completed-Day Normalization</strong></Text>
            <Text as="p">
              Only completed sales days are used in run-rate calculations. This avoids distortion from partial-day sales activity.
            </Text>
            <Text as="p"><strong>2. Recent Sales Velocity</strong></Text>
            <Text as="p">
              The model uses recent completed-day performance to reflect current demand conditions rather than relying only on historical averages.
            </Text>
            <Text as="p"><strong>3. Day-of-Week Weighting</strong></Text>
            <Text as="p">
              Sales are not assumed to occur evenly across the month. The model applies normalized weekday weighting so stronger and weaker sales days are reflected more realistically.
            </Text>
            <Text as="p"><strong>4. Controlled Outlier Treatment</strong></Text>
            <Text as="p">
              The model allows for the possibility of unusually strong selling days, but caps the upside to avoid unrealistic overprojection.
            </Text>
            <Text as="p"><strong>5. Conservative Forecast Guardrails</strong></Text>
            <Text as="p">
              The forecast is bounded and always remains grounded in actual month-to-date sales. It is intentionally designed to avoid runaway optimism.
            </Text>
            <Text as="p"><strong>6. Operational Context</strong></Text>
            <Text as="p">
              Forecast interpretation is supported by traffic trends, sales pacing, and WebOps health indicators to help distinguish demand softness from platform or operational issues.
            </Text>
            <Text as="p">
              <strong>Intended Use</strong>
            </Text>
            <Text as="p">
              This forecast is intended for directional decision support, early pacing visibility, and operational planning. Accuracy generally improves as the month progresses and more completed sales days are available.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={contentMerchChecklistModalOpen}
        onClose={() => setContentMerchChecklistModalOpen(false)}
        title="Storefront Merchandising & Product Experience"
        size="large"
        primaryAction={{
          content: contentMerchChecklistCompleting ? "Marking..." : "Mark Complete",
          onAction: markContentMerchChecklistComplete,
          loading: contentMerchChecklistCompleting
        }}
        secondaryActions={[
          {
            content: contentMerchChecklistSaving ? "Saving..." : "Save Progress",
            onAction: saveContentMerchChecklistProgress,
            loading: contentMerchChecklistSaving
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text variant="bodySm" tone="subdued" as="p">
              Status: {(contentMerchChecklistState.effectiveStatus || "red").toUpperCase()} | Last Completed: {contentMerchChecklistState.lastCompletedAt ? formatWebopsDate(contentMerchChecklistState.lastCompletedAt) : "Never"} | Last Saved: {contentMerchChecklistState.lastSavedAt ? formatWebopsDate(contentMerchChecklistState.lastSavedAt) : "Never"}
            </Text>
            <InlineStack gap="200" wrap={true}>
              {CONTENT_MERCH_STOREFRONT_LINKS.map((link) => (
                <a key={link.label} className="webops-storefront-link" href={link.href} target="_blank" rel="noreferrer">
                  {link.label}
                </a>
              ))}
            </InlineStack>
            {contentMerchChecklistState.sections.map((section) => (
              <div key={section.key} style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3">{section.title}</Text>
                  {section.items.map((item) => (
                    <Checkbox
                      key={item.key}
                      label={item.label}
                      checked={Boolean(item.checked)}
                      onChange={(checked) => toggleChecklistItem(section.key, item.key, checked)}
                    />
                  ))}
                </BlockStack>
              </div>
            ))}
            <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Notes</Text>
                <textarea
                  value={contentMerchChecklistState.notes}
                  onChange={(event) => setContentMerchChecklistState((current) => ({ ...current, notes: event.target.value }))}
                  rows={4}
                  placeholder="Weekly merchandising review notes..."
                  style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6, resize: "vertical" }}
                />
              </BlockStack>
            </div>
            <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Issues Found</Text>
                <Select
                  label="Severity"
                  labelHidden
                  options={CONTENT_MERCH_ISSUE_SEVERITY_OPTIONS}
                  value={contentMerchChecklistState.issueSeverity || "none"}
                  onChange={(value) => setContentMerchChecklistState((current) => ({ ...current, issueSeverity: value }))}
                />
              </BlockStack>
            </div>
            {contentMerchChecklistState.lastCompletionSnapshot ? (
              <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
                <BlockStack gap="100">
                  <Text variant="headingSm" as="h3">Completion Snapshot</Text>
                  <Text as="p">
                    Completed by {contentMerchChecklistState.lastCompletionSnapshot.completedBy || contentMerchChecklistState.lastCompletedBy || "unknown"} on{" "}
                    {contentMerchChecklistState.lastCompletionSnapshot.completedAt
                      ? new Date(contentMerchChecklistState.lastCompletionSnapshot.completedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                      : "unknown date"}
                  </Text>
                  <Text as="p">
                    {Number(contentMerchChecklistState.lastCompletionSnapshot.issueCount || 0)} issue{Number(contentMerchChecklistState.lastCompletionSnapshot.issueCount || 0) === 1 ? "" : "s"} noted
                    {contentMerchChecklistState.lastCompletionSnapshot.issueSeverity
                      ? ` (${String(contentMerchChecklistState.lastCompletionSnapshot.issueSeverity).toUpperCase()})`
                      : ""}.
                  </Text>
                  {Array.isArray(contentMerchChecklistState.lastCompletionSnapshot.issues) &&
                    contentMerchChecklistState.lastCompletionSnapshot.issues.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {contentMerchChecklistState.lastCompletionSnapshot.issues.map((issue, index) => (
                        <li key={`${issue}-${index}`}>
                          <Text as="span">{issue}</Text>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </BlockStack>
              </div>
            ) : null}
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={simulationModalOpen}
        onClose={closeSimulationModal}
        title="Simulation"
        size="large"
        primaryAction={{
          content: "Done",
          onAction: closeSimulationModal
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {simulationDraft.map((entry, index) => (
              <div key={entry.id} style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
                <InlineStack align="space-between" blockAlign="center" style={{ width: "100%" }}>
                  <div style={{ flex: 1, marginLeft: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <Text variant="bodySm" tone="subdued" as="span">Add a Day's Sales:</Text>
                      <Text variant="headingMd" as="span">$</Text>
                      <input
                        value={formatSimulationAmount(entry.amount)}
                        onChange={(event) => handleSimulationDraftChange(index, event.target.value)}
                        placeholder="0"
                        inputMode="decimal"
                        style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6 }}
                      />
                    </div>
                  </div>
                  <Button destructive onClick={() => removeSimulationDraftRow(index)}>
                    Delete
                  </Button>
                </InlineStack>
              </div>
            ))}
            <div>
              <Button onClick={addSimulationDraftRow}>Add Another</Button>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={glossaryModalOpen}
        onClose={() => setGlossaryModalOpen(false)}
        title="Glossary"
        size="large"
        primaryAction={{
          content: glossarySaving ? "Saving..." : "Save",
          onAction: saveGlossary,
          loading: glossarySaving
        }}
        secondaryActions={[
          {
            content: "Add Term",
            onAction: addGlossaryDraftRow
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {glossaryDraft.map((entry, index) => (
              <div key={entry.id} style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
                <BlockStack gap="200">
                  <input
                    value={entry.term}
                    onChange={(event) => handleGlossaryDraftChange(index, "term", event.target.value)}
                    placeholder="Term"
                    style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6 }}
                  />
                  <textarea
                    value={entry.definition}
                    onChange={(event) => handleGlossaryDraftChange(index, "definition", event.target.value)}
                    placeholder="Definition"
                    rows={4}
                    style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6, resize: "vertical" }}
                  />
                  <InlineStack align="end">
                    <Button destructive onClick={() => removeGlossaryDraftRow(index)}>
                      Delete
                    </Button>
                  </InlineStack>
                </BlockStack>
              </div>
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
}

const isToday = (dateStr) => {
  if (!dateStr) return false;

  const today = new Date();
  const d = new Date(dateStr);

  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
};

const isVisibleProject = (p, showCompleted) => {
  return !p.completed_date || isToday(p.completed_date) || showCompleted;
};

function SystemsInventoryDetailsModal({ item, onClose }) {
  return (
    <Modal
      open={Boolean(item)}
      onClose={onClose}
      title={item?.component_name || "Systems Inventory Details"}
      size="large"
      primaryAction={{
        content: "Close",
        onAction: onClose
      }}
    >
      <Modal.Section>
        {item ? (
          <BlockStack gap="300">
            <div className="systems-inventory-detail-grid">
              {[
                "category",
                "component_type",
                "platform",
                "netsuite_internal_id",
                "netsuite_script_id",
                "netsuite_script_type",
                "netsuite_owner",
                "netsuite_inactive",
                "related_system",
                "related_partner",
                "related_record_type",
                "status",
                "risk_level",
                "migration_action",
                "vendor_bundle",
                "source",
                "last_reviewed",
                "reviewed_by"
              ].map((field) => (
                <div key={field} className="systems-inventory-detail-field">
                  <Text variant="bodySm" tone="subdued">{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</Text>
                  <Text as="p">
                    {field === "vendor_bundle"
                      ? (item[field] ? "Vendor Bundle" : "Custom")
                      : (item[field] || "-")}
                  </Text>
                </div>
              ))}
            </div>
            {["business_purpose", "technical_notes", "dependencies", "known_risks"].map((field) => (
              <div key={field} className="systems-inventory-detail-block">
                <Text variant="headingSm" as="h3">{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</Text>
                <Text as="p">{item[field] || "-"}</Text>
              </div>
            ))}
            <InlineStack gap="200" wrap={true}>
              {(item.view_url || buildSystemsInventoryNetSuiteUrl(item.netsuite_internal_id, false)) ? (
                <a href={item.view_url || buildSystemsInventoryNetSuiteUrl(item.netsuite_internal_id, false)} target="_blank" rel="noreferrer">
                  View NetSuite Script
                </a>
              ) : null}
              {(item.edit_url || buildSystemsInventoryNetSuiteUrl(item.netsuite_internal_id, true)) ? (
                <a href={item.edit_url || buildSystemsInventoryNetSuiteUrl(item.netsuite_internal_id, true)} target="_blank" rel="noreferrer">
                  Edit NetSuite Script
                </a>
              ) : null}
            </InlineStack>
          </BlockStack>
        ) : null}
      </Modal.Section>
    </Modal>
  );
}

function SystemsInventoryFormModal({ open, mode, draft, saving, onClose, onSave, onChange }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit Inventory Item" : "Add New Inventory Item"}
      size="large"
      primaryAction={{
        content: saving ? "Saving..." : "Save",
        onAction: onSave,
        loading: saving
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: onClose
        }
      ]}
    >
      <Modal.Section>
        <div className="systems-inventory-form-grid">
          {SYSTEMS_INVENTORY_FORM_FIELDS.map((field) => {
            if (field === "status") {
              return (
                <Select
                  key={field}
                  label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                  options={SYSTEMS_INVENTORY_STATUS_OPTIONS.map((value) => ({ label: value, value }))}
                  value={draft[field] || "Needs Validation"}
                  onChange={(value) => onChange(field, value)}
                />
              );
            }
            if (field === "risk_level") {
              return (
                <Select
                  key={field}
                  label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                  options={SYSTEMS_INVENTORY_RISK_OPTIONS.map((value) => ({ label: value, value }))}
                  value={draft[field] || "Unknown"}
                  onChange={(value) => onChange(field, value)}
                />
              );
            }
            if (field === "migration_action") {
              return (
                <Select
                  key={field}
                  label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                  options={SYSTEMS_INVENTORY_MIGRATION_OPTIONS.map((value) => ({ label: value, value }))}
                  value={draft[field] || "Validate"}
                  onChange={(value) => onChange(field, value)}
                />
              );
            }
            if (field === "vendor_bundle") {
              return (
                <Select
                  key={field}
                  label={SYSTEMS_INVENTORY_FIELD_LABELS[field]}
                  options={[
                    { label: "Custom", value: "0" },
                    { label: "Vendor Bundle", value: "1" }
                  ]}
                  value={String(draft[field] || 0)}
                  onChange={(value) => onChange(field, value)}
                />
              );
            }
            if (["business_purpose", "technical_notes", "dependencies", "known_risks"].includes(field)) {
              return (
                <label key={field} className="systems-inventory-form-field systems-inventory-form-field--wide">
                  <span>{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</span>
                  <textarea
                    value={draft[field] || ""}
                    onChange={(event) => onChange(field, event.target.value)}
                    rows={4}
                  />
                </label>
              );
            }
            return (
              <label key={field} className="systems-inventory-form-field">
                <span>{SYSTEMS_INVENTORY_FIELD_LABELS[field]}</span>
                <input
                  type={field === "last_reviewed" ? "date" : "text"}
                  value={draft[field] || ""}
                  onChange={(event) => onChange(field, event.target.value)}
                />
              </label>
            );
          })}
        </div>
      </Modal.Section>
    </Modal>
  );
}

function Metric({ label, value, tone }) {
  return (
    <InlineStack align="space-between">
      <Text>{label}</Text>
      <Text variant="headingLg" tone={tone}>{value}</Text>
    </InlineStack>
  );
}

function MetricTile({
  label,
  value,
  subValue,
  tone,
  sideLabel,
  sideValue,
  sideSubValue,
  sideTone
}) {
  const hasSide = Boolean(sideLabel || sideValue || sideSubValue);
  return (
    <div className={`metric-tile ${hasSide ? "metric-tile--paired" : "metric-tile--single"}`}>
      <div className="metric-tile-row">
        <Text variant="bodySm" tone="subdued">{label}</Text>
        {hasSide ? (
          <div className="metric-tile-side">
            <Text variant="bodySm" tone="subdued">{sideLabel}</Text>
          </div>
        ) : null}
      </div>
      <div className="metric-tile-row">
        <Text variant="headingLg" tone={tone}>{value}</Text>
        {hasSide ? (
          <div className="metric-tile-side">
            <Text variant="headingLg" tone={sideTone}>{sideValue}</Text>
          </div>
        ) : null}
      </div>
      <div className="metric-tile-row">
        <Text variant="bodySm" tone="subdued">{subValue}</Text>
        {hasSide ? (
          <div className="metric-tile-side">
            <Text variant="bodySm" tone="subdued">{sideSubValue}</Text>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function localToMySQL(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function webSalesTrendSentence(chartData) {
  if (!chartData || chartData.length === 0) return "No web sales data available.";

  const validData = chartData.filter(d => d.mtdWebPercentOfGoal !== undefined);
  if (validData.length < 2) return "Not enough data to calculate trend.";

  // Calculate "below/above goal" percentages
  const startPctBelow = 100 - validData[0].mtdWebPercentOfGoal;
  const endPctBelow = 100 - validData[validData.length - 1].mtdWebPercentOfGoal;

  const startWord = startPctBelow >= 0 ? "below" : "above";
  const endWord = endPctBelow >= 0 ? "below" : "above";

  // Rate of change
  const changePct = endPctBelow - startPctBelow;
  const daysElapsed = validData.length - 1;
  const dailyRate = changePct / daysElapsed;
  const monthlyRate = dailyRate * 30;

  const droppingIncreasing = dailyRate >= 0 ? "dropping" : "increasing";

  return `Web sales started the month at ${Math.abs(startPctBelow).toFixed(0)}% ${startWord} goal and are now ${Math.abs(endPctBelow).toFixed(0)}% ${endWord} goal, ${droppingIncreasing} at a rate of ${Math.abs(dailyRate).toFixed(2)}% per day (${Math.abs(monthlyRate).toFixed(1)}%/month).`;
}

function ProjectDateTooltip({ completedDate }) {
  if (!completedDate) return null;

  const displayText = `Completed at: ${new Date(completedDate).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;

  return (
    <PolarisTooltip content={displayText}>
      <span style={{ cursor: "pointer", position: "relative", top: "4px", left: "4px" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" stroke="#666" strokeWidth="2" />
          <line x1="3" y1="10" x2="21" y2="10" stroke="#666" strokeWidth="2" />
          <line x1="8" y1="4" x2="8" y2="0" stroke="#666" strokeWidth="2" />
          <line x1="16" y1="4" x2="16" y2="0" stroke="#666" strokeWidth="2" />
        </svg>
      </span>
    </PolarisTooltip>
  );
}

function buildGlossarySplitRegex(terms) {
  const glossaryKeys = Object.keys(terms || {});
  if (!glossaryKeys.length) {
    return null;
  }

  return new RegExp(
    `(${glossaryKeys
      .sort((a, b) => b.length - a.length)
      .map((term) => `\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`)
      .join("|")})`,
    "gi"
  );
}

function renderGlossaryTooltipContent(text) {
  const rawText = String(text || "").trim();
  if (!rawText) {
    return "";
  }

  const renderInlineGlossaryFormatting = (value) => {
    const renderWithBreaks = (input, keyPrefix) => {
      return String(input || "")
        .split(/(<br\s*\/?>)/gi)
        .filter((part) => part !== "")
        .map((part, partIndex) => {
          if (/^<br\s*\/?>$/i.test(part.trim())) {
            return <br key={`${keyPrefix}-br-${partIndex}`} />;
          }

          return <React.Fragment key={`${keyPrefix}-txt-${partIndex}`}>{part}</React.Fragment>;
        });
    };

    const segments = String(value || "")
      .split(/(<(?:b|strong)>[\s\S]*?<\/(?:b|strong)>)/gi)
      .filter(Boolean);

    return segments.map((segment, index) => {
      const boldMatch = segment.match(/^<(?:b|strong)>([\s\S]*?)<\/(?:b|strong)>$/i);
      if (boldMatch) {
        return (
          <strong key={`${index}-${boldMatch[1].slice(0, 12)}`}>
            {renderWithBreaks(boldMatch[1], `bold-${index}`)}
          </strong>
        );
      }

      return (
        <React.Fragment key={`${index}-${segment.slice(0, 12)}`}>
          {renderWithBreaks(segment, `seg-${index}`)}
        </React.Fragment>
      );
    });
  };

  const paragraphMatches = [...rawText.matchAll(/<p>([\s\S]*?)<\/p>/gi)];
  const paragraphs = paragraphMatches.length
    ? paragraphMatches.map((match) => String(match[1] || "").trim()).filter(Boolean)
    : rawText
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);

  if (!paragraphs.length) {
    return rawText.replace(/<\/?p>/gi, "").trim();
  }

  return (
    <div style={{ maxWidth: "35rem", lineHeight: 1.45 }}>
      {paragraphs.map((paragraph, index) => (
        <p key={`${index}-${paragraph.slice(0, 12)}`} style={{ margin: index === paragraphs.length - 1 ? 0 : "0 0 0.55rem 0" }}>
          {renderInlineGlossaryFormatting(paragraph.replace(/<\/?p>/gi, ""))}
        </p>
      ))}
    </div>
  );
}

const glossaryTermTextStyle = {
  textDecoration: "underline dotted",
  textUnderlineOffset: "2px",
  cursor: "help"
};

function renderPlainTextWithGlossary(text, terms = {}, keyPrefix = "plain") {
  const splitRegex = buildGlossarySplitRegex(terms);
  if (!splitRegex) {
    return String(text || "");
  }

  const parts = String(text || "").split(splitRegex);

  return parts.map((part, index) => {
    const glossaryText = terms[part.toLowerCase()];

    if (!glossaryText) {
      return <React.Fragment key={`${keyPrefix}-${part}-${index}`}>{part}</React.Fragment>;
    }

    return (
      <PolarisTooltip key={`${keyPrefix}-${part}-${index}`} content={renderGlossaryTooltipContent(glossaryText)}>
        <span style={glossaryTermTextStyle}>
          {part}
        </span>
      </PolarisTooltip>
    );
  });
}

function renderSafeHtmlWithGlossary(text, terms = {}, keyPrefix = "html") {
  const rawText = String(text || "");
  if (!rawText.trim()) {
    return "";
  }

  if (!/[<>]/.test(rawText) || typeof DOMParser === "undefined") {
    return renderPlainTextWithGlossary(rawText, terms, keyPrefix);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${rawText}</div>`, "text/html");
  const root = doc.body.firstChild;

  const renderNode = (node, path) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return renderPlainTextWithGlossary(node.textContent || "", terms, `${keyPrefix}-${path}`);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const tagName = node.tagName.toLowerCase();
    const childNodes = Array.from(node.childNodes)
      .map((child, index) => renderNode(child, `${path}-${index}`))
      .flat()
      .filter((child) => child !== null && child !== undefined);

    if (tagName === "b" || tagName === "strong") {
      return <strong key={`${keyPrefix}-${path}`}>{childNodes}</strong>;
    }

    if (tagName === "i" || tagName === "em") {
      return <em key={`${keyPrefix}-${path}`}>{childNodes}</em>;
    }

    if (tagName === "u") {
      return <u key={`${keyPrefix}-${path}`}>{childNodes}</u>;
    }

    if (tagName === "br") {
      return <br key={`${keyPrefix}-${path}`} />;
    }

    if (tagName === "p") {
      return (
        <React.Fragment key={`${keyPrefix}-${path}`}>
          {childNodes}
          <br />
          <br />
        </React.Fragment>
      );
    }

    if (tagName === "span" || tagName === "div") {
      return <React.Fragment key={`${keyPrefix}-${path}`}>{childNodes}</React.Fragment>;
    }

    return <React.Fragment key={`${keyPrefix}-${path}`}>{childNodes}</React.Fragment>;
  };

  const renderedNodes = Array.from(root?.childNodes || [])
    .map((node, index) => renderNode(node, `${index}`))
    .flat()
    .filter((node) => node !== null && node !== undefined);

  if (!renderedNodes.length) {
    return renderPlainTextWithGlossary(rawText, terms, keyPrefix);
  }

  if (renderedNodes.length >= 2) {
    const trailingBreaks = renderedNodes.slice(-2);
    if (
      React.isValidElement(trailingBreaks[0]) && trailingBreaks[0].type === "br" &&
      React.isValidElement(trailingBreaks[1]) && trailingBreaks[1].type === "br"
    ) {
      renderedNodes.splice(-2, 2);
    }
  }

  return renderedNodes;
}

function GlossaryText({ text, terms = {} }) {
  return renderSafeHtmlWithGlossary(text, terms);
}

function TooltipGlossary({ content, children }) {
  return (
    <PolarisTooltip content={content}>
      {children}
    </PolarisTooltip>
  );
}

function GoalChartTooltip({ active, label, payload, hoveredSeries = null, compareGoalModel = false }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = payload[0]?.payload || {};
  const formatCurrency = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

  const formatSeriesName = (entry) => {
    const rawName = entry?.name;
    const rawKey = entry?.dataKey;

    if (typeof rawName === "string" && rawName.trim()) {
      return rawName;
    }

    const scenarioMatch = String(rawKey || "").match(/^webScenario(\d+)$/);
    if (scenarioMatch) {
      return `Scenario Day ${scenarioMatch[1]}`;
    }

    return rawKey;
  };

  const sortedPayload = [...payload]
    .filter((entry) => entry?.value !== null && entry?.value !== undefined)
    .sort((a, b) => {
      if (b.value !== a.value) {
        return b.value - a.value;
      }

      if (a.dataKey === "webTrend") return -1;
      if (b.dataKey === "webTrend") return 1;

      return 0;
    });

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        padding: "14px 16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
      }}
    >
      <div style={{ marginBottom: 6 }}>{label}</div>
      {sortedPayload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            color: hoveredSeries === entry.dataKey ? "#000000" : entry.color,
            marginBottom: 2,
            fontWeight: hoveredSeries === entry.dataKey ? 700 : 400,
            background: hoveredSeries === entry.dataKey ? "rgba(0, 0, 0, 0.06)" : "transparent",
            borderRadius: 6,
            padding: hoveredSeries === entry.dataKey ? "4px 6px" : "0"
          }}
        >
          {formatSeriesName(entry)}: {Number(entry.value).toFixed(1)}%
        </div>
      ))}
      {compareGoalModel && (
        <div style={{ marginTop: 8, borderTop: "1px solid #ececec", paddingTop: 8 }}>
          <div>Actual Sales: {formatCurrency(point.webOnly)}</div>
          <div>Old Goal: {formatCurrency(point.oldDailyGoal)}</div>
          <div>Old Variance: {Number(point.varianceVsCurrentGoalPct || 0).toFixed(1)}%</div>
          <div>Recal Goal: {formatCurrency(point.recalibratedDailyGoal)}</div>
          <div>Recal Variance: {Number(point.varianceVsRecalibratedGoalPct || 0).toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
}

function RevenueGoalTooltip({ active, label, payload, compareGoalModel = false }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const point = payload[0]?.payload || {};
  const formatCurrency = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        padding: "12px 14px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
      }}
    >
      <div style={{ marginBottom: 6, fontWeight: 600 }}>{label}</div>
      <div>Actual Revenue: {formatCurrency(point.actualRevenue)}</div>
      <div>Current Expected $: {formatCurrency(point.currentExpectedRevenue)}</div>
      {compareGoalModel && (
        <>
          <div>Recalibrated Expected $: {formatCurrency(point.recalibratedExpectedRevenue)}</div>
          <div>Variance vs Current (MTD): {formatCurrency(point.varianceVsCurrentDollar)}</div>
          <div>Variance vs Recalibrated (MTD): {formatCurrency(point.varianceVsRecalibratedDollar)}</div>
        </>
      )}
    </div>
  );
}

function formatTrafficMonthLabel(monthLabel) {
  if (!monthLabel) {
    return "";
  }

  const parsed = new Date(`${monthLabel}-01T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return monthLabel;
  }

  return parsed.toLocaleString("en-US", { month: "short" });
}

function formatTrafficCompletedRangeLabel(monthLabel, completedDay) {
  const monthText = formatTrafficMonthLabel(monthLabel);
  if (!monthText || !completedDay) {
    return monthText;
  }

  return `${monthText} 1-${completedDay}`;
}

function parseMonthLabel(monthLabel) {
  if (!monthLabel) {
    return null;
  }

  const parsed = new Date(`${monthLabel}-01T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isActiveTrafficMonth(monthLabel) {
  if (!monthLabel) {
    return false;
  }

  const parsed = new Date(`${monthLabel}-01T00:00:00`);
  const now = new Date();

  return !Number.isNaN(parsed.getTime())
    && parsed.getFullYear() === now.getFullYear()
    && parsed.getMonth() === now.getMonth();
}

function buildTrafficChartSeries(data = [], metricKind = "sessions") {
  if (!Array.isArray(data) || !data.length) {
    return [];
  }

  const valueFromPoint = (point) => {
    if (metricKind === "conversion" || metricKind === "clickthrough") {
      return Number(point.conversion_rate || 0) * 100;
    }
    return Number(point.sessions || 0);
  };

  const rows = data.map((point) => ({
    ...point,
    axisLabel: formatTrafficMonthLabel(point.month),
    historicalActual: valueFromPoint(point),
    forecastTrend: null,
    forecastPoint: null,
    currentMonthActual: null,
    projectedMonthEnd: null,
    isActiveMonth: false
  }));

  const lastRow = rows[rows.length - 1];
  if (!lastRow || !isActiveTrafficMonth(lastRow.month)) {
    return rows;
  }

  const now = new Date();
  const observedDays = Math.max(Math.min(now.getDate(), new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()), 1);
  const completedDays = Math.max(now.getDate() - 1, 0);
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const isRateMetric = metricKind === "conversion" || metricKind === "clickthrough";

  if (completedDays >= daysInMonth || rows.length < 2) {
    return rows;
  }

  const liveObservedActual = Number(lastRow.historicalActual || 0);
  const rawCompletedDayActual = isRateMetric
    ? (completedDays > 0 ? liveObservedActual : null)
    : (
      completedDays > 0
        ? (liveObservedActual * (completedDays / observedDays))
        : null
    );
  const rawProjectedMonthEnd = isRateMetric
    ? (completedDays > 0 ? Number(rawCompletedDayActual || 0) : liveObservedActual)
    : (
      completedDays > 0
        ? ((Number(rawCompletedDayActual || 0) / completedDays) * daysInMonth)
        : ((liveObservedActual / observedDays) * daysInMonth)
    );
  const completedDayActual = completedDays > 0
    ? (
      isRateMetric
        ? Number(rawCompletedDayActual.toFixed(2))
        : Math.round(rawCompletedDayActual)
    )
    : null;
  const projectedMonthEnd = isRateMetric
    ? Number(rawProjectedMonthEnd.toFixed(2))
    : Math.round(rawProjectedMonthEnd);
  const previousRow = rows[rows.length - 2];

  previousRow.forecastTrend = Number(previousRow.historicalActual || 0);
  lastRow.historicalActual = null;
  lastRow.axisLabel = completedDays > 0
    ? formatTrafficCompletedRangeLabel(lastRow.month, completedDays)
    : `${formatTrafficMonthLabel(lastRow.month)} (N/A)`;
  lastRow.forecastTrend = null;
  lastRow.forecastPoint = null;
  lastRow.currentMonthActual = completedDayActual;
  lastRow.projectedMonthEnd = projectedMonthEnd;
  lastRow.isActiveMonth = true;
  lastRow.completedDaysUsed = completedDays;
  lastRow.noCompletedDayData = completedDays === 0;

  rows.push({
    month: `${lastRow.month}-eom`,
    axisLabel: `${formatTrafficMonthLabel(lastRow.month)} EOM`,
    historicalActual: null,
    forecastTrend: projectedMonthEnd,
    forecastPoint: projectedMonthEnd,
    currentMonthActual: null,
    projectedMonthEnd,
    isActiveMonth: false,
    isProjectedEom: true
  });

  return rows;
}

function formatTrafficPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "n/a";
  }

  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(1)}%`;
}

function formatTrafficRate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "n/a";
  }
  return `${numeric.toFixed(2)}%`;
}

function formatTrafficCount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "n/a";
  }
  return `${Math.round(numeric).toLocaleString("en-US")}`;
}

function getTrafficSignalTone(status) {
  if (status === "up") return "success";
  if (status === "watch") return "caution";
  if (status === "alert" || status === "down") return "critical";
  return "subdued";
}

function getTrafficSignalStatusFromPct(pctChange) {
  const numeric = Number(pctChange);

  if (!Number.isFinite(numeric)) return "flat";
  if (numeric < -25) return "alert";
  if (numeric < -10) return "down";
  if (numeric > 10) return "up";
  return "flat";
}

function buildTrafficComparisonRangeLabel(comparisonWindow = null) {
  const currentMonth = parseMonthLabel(comparisonWindow?.currentMonth);
  const priorMonth = parseMonthLabel(comparisonWindow?.priorMonth);
  const currentComparableDays = Number(comparisonWindow?.currentComparableDays || 0);
  const priorComparableDays = Number(comparisonWindow?.priorComparableDays || 0);

  if (currentMonth && priorMonth && currentComparableDays > 0 && priorComparableDays > 0) {
    const currentMonthLabel = currentMonth.toLocaleString("en-US", { month: "short" });
    const priorMonthLabel = priorMonth.toLocaleString("en-US", { month: "short" });
    return `${currentMonthLabel} 1-${currentComparableDays} vs ${priorMonthLabel} 1-${priorComparableDays}`;
  }

  const now = new Date();
  const fallbackCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const fallbackPriorMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const completedDay = Math.max(now.getDate() - 1, 1);
  const fallbackCurrentEndDay = Math.min(completedDay, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate());
  const fallbackPriorEndDay = Math.min(completedDay, new Date(now.getFullYear(), now.getMonth(), 0).getDate());
  const fallbackCurrentMonthLabel = fallbackCurrentMonth.toLocaleString("en-US", { month: "short" });
  const fallbackPriorMonthLabel = fallbackPriorMonth.toLocaleString("en-US", { month: "short" });
  const currentMonthLabel = fallbackCurrentMonthLabel;
  const priorMonthLabel = fallbackPriorMonthLabel;
  const currentMonthEndDay = fallbackCurrentEndDay;
  const priorMonthEndDay = fallbackPriorEndDay;

  return `${currentMonthLabel} 1–${currentMonthEndDay} vs ${priorMonthLabel} 1–${priorMonthEndDay}`;
}

function getTrafficSignalDisplayLabel(status) {
  if (status === "insufficient_data") return "too early";
  if (status === "watch") return "! Needs Attention";
  if (status === "steady" || status === "flat") return "steady";
  return status || "flat";
}

function buildTrafficSummarySentence(signals, summary = {}) {
  const entries = Object.entries(TRAFFIC_SERIES_CONFIG)
    .filter(([key]) => key !== "allTraffic")
    .map(([key, config]) => ({
      key,
      label: config.label.replace(" Sessions", ""),
      status: signals?.[key]?.status || "flat",
      pctChange: Number(signals?.[key]?.pctChange),
      currentMTD: Number(summary?.[key]?.currentMTD || 0),
      priorMTD: Number(summary?.[key]?.priorMTD || 0)
    }))
    .filter((entry) => Number.isFinite(entry.pctChange));

  if (!entries.length) {
    return "Traffic signals will appear here once the monthly traffic feed is available.";
  }

  const insufficientCount = entries.filter((entry) => entry.status === "insufficient_data").length;
  if (insufficientCount === entries.length) {
    return "Traffic comparisons are still too early to judge reliably this month.";
  }

  const strongest = [...entries].sort((a, b) => b.pctChange - a.pctChange)[0];
  const weakest = [...entries].sort((a, b) => a.pctChange - b.pctChange)[0];
  const strongestLabel = getTrafficSignalDisplayLabel(strongest.status);
  const weakestLabel = getTrafficSignalDisplayLabel(weakest.status);

  if (strongest.key === weakest.key) {
    return `${strongest.label} is ${strongestLabel} at ${formatTrafficPercent(strongest.pctChange)} versus prior-month completed-day MTD traffic.`;
  }

  return `${strongest.label} is ${strongestLabel} at ${formatTrafficPercent(strongest.pctChange)}, while ${weakest.label} is ${weakestLabel} at ${formatTrafficPercent(weakest.pctChange)} versus prior-month completed-day MTD traffic.`;
}

function TrafficSignalCard({
  panelKey = "",
  label,
  summary,
  signal,
  comparisonWindow = null,
  terms = {},
  subtext = "",
  valueFormatter = formatTrafficPercent,
  valueMode = "pctChange",
  compareAsRate = false,
  invertSignalPolarity = false,
  cartAbandonmentMode = "operational",
  setCartAbandonmentMode = () => { },
  activeTrafficTab = "allTraffic",
  setActiveTrafficTab = () => { },
  clickThroughMode = "organic",
  setClickThroughMode = () => { }
}) {
  const status = signal?.status || "flat";
  const effectiveStatus = invertSignalPolarity
    ? (status === "down" ? "up" : (status === "up" ? "down" : status))
    : status;
  const statusLabel = getTrafficSignalDisplayLabel(status);
  const rangeLabel = buildTrafficComparisonRangeLabel(comparisonWindow);
  const isCurrentMode = valueMode === "current";
  const comparisonLabel = status === "insufficient_data"
    ? "Too early for a reliable completed-day comparison"
    : `${(isCurrentMode || compareAsRate) ? valueFormatter(summary?.currentMTD) : Number(summary?.currentMTD || 0).toLocaleString("en-US")} vs ${(isCurrentMode || compareAsRate) ? valueFormatter(summary?.priorMTD) : Number(summary?.priorMTD || 0).toLocaleString("en-US")} (${rangeLabel})`;
  const headlineValue = isCurrentMode
    ? valueFormatter(summary?.currentMTD)
    : formatTrafficPercent(signal?.pctChange);

  return (
    <div className="traffic-summary-card">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack blockAlign="center" gap="100">
          <Text variant="headingSm" as="h3">
            <GlossaryText text={label} terms={terms} />
          </Text>
          {panelKey === "trafficSessions" ? (
            <div className="traffic-tab-group-inline" role="radiogroup" aria-label="Traffic channel view">
              {TRAFFIC_TAB_OPTIONS.map((option) => (
                <label key={option.key} className="traffic-tab-radio">
                  <input
                    type="radio"
                    name="traffic-channel-tab"
                    value={option.key}
                    checked={activeTrafficTab === option.key}
                    onChange={(event) => {
                      if (event.target.checked) setActiveTrafficTab(option.key);
                    }}
                  />
                  <span>{option.shortLabel}</span>
                </label>
              ))}
            </div>
          ) : null}
          {panelKey === "cartAbandonment" ? (
            <div className="traffic-tab-group-inline" role="radiogroup" aria-label="Cart abandonment mode">
              <label className="traffic-tab-radio">
                <input
                  type="radio"
                  name="cart-abandonment-mode"
                  value="operational"
                  checked={cartAbandonmentMode === "operational"}
                  onChange={(event) => {
                    if (event.target.checked) setCartAbandonmentMode("operational");
                  }}
                />
                <span>Ops</span>
              </label>
              <label className="traffic-tab-radio">
                <input
                  type="radio"
                  name="cart-abandonment-mode"
                  value="funnel"
                  checked={cartAbandonmentMode === "funnel"}
                  onChange={(event) => {
                    if (event.target.checked) setCartAbandonmentMode("funnel");
                  }}
                />
                <span>Funnel</span>
              </label>
            </div>
          ) : null}
          {panelKey === "clickThrough" ? (
            <div className="traffic-tab-group-inline" role="radiogroup" aria-label="Click-through mode">
              {CLICK_THROUGH_MODE_OPTIONS.map((option) => (
                <label key={option.key} className="traffic-tab-radio">
                  <input
                    type="radio"
                    name="click-through-mode"
                    value={option.key}
                    checked={clickThroughMode === option.key}
                    onChange={(event) => {
                      if (event.target.checked) setClickThroughMode(option.key);
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          ) : null}
        </InlineStack>
        <span className={`traffic-status-pill traffic-status-pill--${effectiveStatus}`}>
          {statusLabel}
        </span>
      </InlineStack>
      <BlockStack gap="050">
        {subtext ? (
          <Text variant="bodySm" tone="subdued">{subtext}</Text>
        ) : null}
        <Text variant="headingLg" tone={getTrafficSignalTone(effectiveStatus)}>
          {headlineValue}
        </Text>
        <Text variant="bodySm" tone="subdued">
          {status === "insufficient_data"
            ? comparisonLabel
            : `${comparisonLabel} | Change: ${formatTrafficPercent(signal?.pctChange)}`}
        </Text>
      </BlockStack>
    </div>
  );
}

function TrafficTrendCard({ label, color, data, summary, terms = {}, metricKind = "sessions" }) {
  const TargetCrosshair = ({ cx, cy, stroke }) => {
    const size = 10; // length of each arm (px)

    return (
      <g>
        {/* vertical */}
        <line
          x1={cx}
          y1={cy - size}
          x2={cx}
          y2={cy + size}
          stroke={stroke}
          strokeWidth={1}
          strokeOpacity={0.8}
        />
        {/* horizontal */}
        <line
          x1={cx - size}
          y1={cy}
          x2={cx + size}
          y2={cy}
          stroke={stroke}
          strokeWidth={1}
          strokeOpacity={0.8}
        />
      </g>
    );
  };
  const baseRows = buildTrafficChartSeries(data, metricKind);
  const activeMonthRow = baseRows.find((row) => row.isActiveMonth);
  const projectedEomRow = baseRows.find((row) => row.isProjectedEom);
  const priorSamePeriod = Number(summary?.priorMTD || 0);
  const comparisonGapColor = activeMonthRow
    ? (Number(activeMonthRow.currentMonthActual || 0) >= priorSamePeriod ? "#1b8a3a" : "#b71c1c")
    : color;

  if (activeMonthRow) {
    activeMonthRow.priorSamePeriod = priorSamePeriod;
  }

  const chartRows = baseRows.map((row) => {
    if (!row.isActiveMonth) {
      return {
        ...row,
        actualPillar: null,
        gapBandBase: null,
        gapBandValue: null
      };
    }

    const hasCompletedActual = row.currentMonthActual !== null && row.currentMonthActual !== undefined;
    const actual = hasCompletedActual ? Number(row.currentMonthActual) : null;
    const expected = Number(priorSamePeriod || 0);

    return {
      ...row,
      actualPillar: actual,
      gapBandBase: hasCompletedActual ? Math.min(actual, expected) : null,
      gapBandValue: hasCompletedActual ? Math.abs(expected - actual) : null
    };
  });
  const formatTrendValue = (value) => (
    (metricKind === "conversion" || metricKind === "clickthrough")
      ? formatTrafficRate(value)
      : Number(value || 0).toLocaleString("en-US")
  );



  return (
    <div className="traffic-chart-card">
      <BlockStack gap="200">
        <Text variant="headingSm" as="h3">
          <GlossaryText text={label} terms={terms} />
        </Text>
        {chartRows.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={chartRows}
              margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
              barCategoryGap="0%"
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="axisLabel" />
              <YAxis tickFormatter={(value) => Number(value || 0).toLocaleString("en-US")} />
              <Tooltip content={<TrafficChartTooltip label={label} metricKind={metricKind} />} />

              <Bar
                dataKey="actualPillar"
                fill={color}
                fillOpacity={0.9}
                isAnimationActive={false}
                legendType="none"
                barSize={12}
                radius={[8, 8, 0, 0]}
              />

              <Line
                type="monotone"
                dataKey="historicalActual"
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="forecastTrend"
                stroke={color}
                strokeWidth={2}
                strokeDasharray="3 5"
                dot={false}
                activeDot={false}
                connectNulls={true}
              />

              {activeMonthRow ? (
                <>
                  <ReferenceDot
                    x={activeMonthRow.axisLabel}
                    y={priorSamePeriod}
                    r={0} // hide default dot
                    shape={(props) => (
                      <TargetCrosshair
                        cx={props.cx}
                        cy={props.cy}
                        stroke={comparisonGapColor}
                      />
                    )}
                  />

                  <ReferenceDot
                    x={activeMonthRow.axisLabel}
                    y={priorSamePeriod}
                    r={6}
                    fill="#ffffff3a"
                    stroke={activeMonthRow.noCompletedDayData ? "#9aa0a6" : comparisonGapColor}
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  />
                  <ReferenceDot
                    x={activeMonthRow.axisLabel}
                    y={priorSamePeriod}
                    r={2.5}
                    fill={activeMonthRow.noCompletedDayData ? "#9aa0a6" : comparisonGapColor}
                    stroke="none"
                    ifOverflow="extendDomain"
                  />
                  <ReferenceDot
                    x={(projectedEomRow || activeMonthRow).axisLabel}
                    y={activeMonthRow.projectedMonthEnd}
                    r={3}
                    fill="#ffffff3a"
                    stroke={color}
                    strokeWidth={2}
                  />

                </>
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Text tone="subdued">No traffic data available.</Text>
        )}
      </BlockStack>
    </div>
  );
}

function TrafficChartTooltip({ active, payload, label, metricKind = "sessions" }) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload || {};
  const historicalActualEntry = payload.find((entry) => entry.dataKey === "historicalActual");
  const forecastEntry = payload.find((entry) => entry.dataKey === "forecastTrend");
  const isActiveMonth = Boolean(point?.isActiveMonth);
  const isProjectedEom = Boolean(point?.isProjectedEom);
  const formatTrendValue = (value) => (
    (metricKind === "conversion" || metricKind === "clickthrough")
      ? formatTrafficRate(value)
      : Number(value || 0).toLocaleString("en-US")
  );

  return (
    <div className="traffic-tooltip">
      <BlockStack gap="050">
        <Text variant="bodySm" tone="subdued">{label}</Text>
        <Text variant="headingSm">{point.axisLabel || formatTrafficMonthLabel(point.month)}</Text>
        {isProjectedEom ? (
          <>
            <Text variant="bodySm">Projected EOM: {formatTrendValue(point.projectedMonthEnd || forecastEntry?.value || 0)}</Text>
            <Text variant="bodySm" tone="subdued">Dotted line shows forecast direction into month-end.</Text>
          </>
        ) : isActiveMonth ? (
          <>
            {point?.noCompletedDayData ? (
              <Text variant="bodySm">No completed {formatTrafficMonthLabel(point.month)} selling days yet.</Text>
            ) : (
              <Text variant="bodySm">{point.axisLabel}: {formatTrendValue(point.currentMonthActual || 0)}</Text>
            )}
            <Text variant="bodySm">Same period last month: {formatTrendValue(point.priorSamePeriod || 0)}</Text>
            <Text variant="bodySm">{formatTrafficMonthLabel(point.month)} Projected EOM: {formatTrendValue(point.projectedMonthEnd || 0)}</Text>
            <Text variant="bodySm" tone="subdued">
              {point?.noCompletedDayData
                ? "No completed-month actual point is plotted yet; forecast line remains available."
                : "Vertical marker = current actual vs same period last month."
              }
            </Text>
          </>
        ) : (
          <>
            <Text variant="bodySm">
              {metricKind === "conversion"
                ? "Conversion Rate"
                : (metricKind === "count" ? "Cart-to-checkout dropoff users" : (metricKind === "clickthrough" ? "Click-through rate" : "Sessions"))
              }: {formatTrendValue(historicalActualEntry?.value || point.historicalActual || 0)}
            </Text>
            {metricKind === "count" ? (
              <>
                <Text variant="bodySm">Begin checkout users: {Number(point.orders || 0).toLocaleString("en-US")}</Text>
                <Text variant="bodySm">Dropoff rate: {formatTrafficPercent(Number(point.conversion_rate || 0) * 100)}</Text>
                <Text variant="bodySm" tone="subdued">Definition: add_to_cart users minus begin_checkout users.</Text>
              </>
            ) : metricKind === "clickthrough" ? (
              <>
                <Text variant="bodySm">Click events: {Number(point.orders || 0).toLocaleString("en-US")}</Text>
                <Text variant="bodySm">Sessions: {Number(point.sessions || 0).toLocaleString("en-US")}</Text>
                <Text variant="bodySm" tone="subdued">Definition: click events divided by sessions.</Text>
              </>
            ) : (
              <>
                <Text variant="bodySm">Orders: {Number(point.orders || 0).toLocaleString("en-US")}</Text>
                <Text variant="bodySm">
                  Sales: {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(Number(point.total_sales || 0))}
                </Text>
                <Text variant="bodySm">Conversion: {formatTrafficPercent(Number(point.conversion_rate || 0) * 100)}</Text>
              </>
            )}
            {forecastEntry?.value !== null && forecastEntry?.value !== undefined ? (
              <Text variant="bodySm" tone="subdued">Dotted line shows forecast direction into month-end.</Text>
            ) : null}
          </>
        )}
      </BlockStack>
    </div>
  );
}

function TrafficTopChannelsCard({ label, channels, terms = {}, metricKind = "sessions" }) {
  return (
    <div className="traffic-top-card">
      <BlockStack gap="200">
        <Text variant="headingSm" as="h3">
          <GlossaryText text={`${label} Top Channels`} terms={terms} />
        </Text>
        {Array.isArray(channels) && channels.length > 0 ? (
          <div className="traffic-channel-list">
            {channels.map((channel) => (
              <div key={channel.referring_channel} className="traffic-channel-row">
                <div className="traffic-channel-main">
                  <Text variant="bodySm">{channel.referring_channel}</Text>
                  {metricKind === "conversion" ? (
                    <Text variant="bodySm" tone="subdued">
                      {Number(channel.sessions || 0).toLocaleString("en-US")} sessions | {Number(channel.sessionsThatCompletedCheckout || 0).toLocaleString("en-US")} checkout sessions | {formatTrafficRate(channel.currentRatePct)}
                    </Text>
                  ) : metricKind === "clickthrough" ? (
                    <Text variant="bodySm" tone="subdued">
                      {Number(channel.sessions || 0).toLocaleString("en-US")} sessions | {Number(channel.clicks || 0).toLocaleString("en-US")} click events | {formatTrafficRate(channel.currentRatePct)}
                    </Text>
                  ) : metricKind === "count" ? (
                    <Text variant="bodySm" tone="subdued">
                      {Number(channel.sessions || 0).toLocaleString("en-US")} est. cart-to-checkout dropoff users
                    </Text>
                  ) : (
                    <Text variant="bodySm" tone="subdued">
                      {Number(channel.sessions || 0).toLocaleString("en-US")} sessions
                    </Text>
                  )}
                </div>
                <Text
                  variant="bodySm"
                  tone={getTrafficSignalTone(getTrafficSignalStatusFromPct(channel.pctChange))}
                >
                  {formatTrafficPercent(channel.pctChange)}
                </Text>
              </div>
            ))}
          </div>
        ) : (
          <Text tone="subdued">No channel data available.</Text>
        )}
      </BlockStack>
    </div>
  );
}
